import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Sidebar } from 'primereact/sidebar';
import { Checkbox } from 'primereact/checkbox';
import { Slider } from 'primereact/slider';
import EmiRecalculateDialog from './EmiRecalculateDialog';

/**
 * Offers Comparison Component
 *
 * Displays loan offers in a comparison table format
 * with offers displayed side-by-side in columns.
 * Features: Lock/pin offers for comparison, pagination with max 3 visible offers
 * Mobile: Shows only 1 offer at a time with navigation
 *
 * Uses PrimeFlex utility classes only - no custom SCSS
 */
const OffersComparison = ({ offers, selectedOffer, onSelectOffer, readOnly = false, hideEligibility = false, eligibilityParams = null, renderOfferHeader = null, renderOfferFooter = null, onEditOffer = null, onShareViaWhatsApp = null,
    // Number of payslips the borrower uploaded for this lead. When exactly 1
    // is present, the filter sidebar shows an "assumed same for last 3 months"
    // notice — because the eligibility/EMI math runs on that single payslip's
    // net salary as if it were the average. Null/undefined → no notice shown.
    payslipCount = null,
    // When true: hide the desktop filter sidebar, Share via WhatsApp button,
    // Cards/Table view toggle, lock-instructions strip. Used by the
    // regenerate-preview modal in OffersStep where the user just needs to
    // SEE the new offers in their native card format before clicking Replace
    // All — filtering / sorting / sharing don't apply there.
    hideFilters = false,
    // When true: disable position:sticky on the card header AND the filter
    // sidebar. Both default to sticky positioning that pins to the viewport
    // (top:8rem / 8.5rem) — designed for the main offers page where this
    // component sits under a sticky tabs row. Inside a Dialog/modal, sticky
    // anchors to the viewport instead of the modal body, so headers float
    // mid-card while content scrolls past. Setting inModal=true forces them
    // to render in normal flow (top of card / top of sidebar).
    inModal = false,
    // When true: bound the offers (cards) area to a fixed-height box that scrolls
    // INTERNALLY (its own scrollbar) instead of relying on page scroll. The
    // bank-name card header pins to the TOP OF THE BOX (sticky top:0) so the
    // lender stays identified while a tall card scrolls inside; the paging
    // arrows center on the box; the filter sidebar drops its viewport-sticky
    // and sits beside the box. Once the box hits its scroll end, normal page
    // scroll resumes. Used by the marketplace listing-detail offers panel so
    // the offers don't spill down the whole page. scrollHeight sets the box
    // height (default 70vh). The lead wizard / View Leads leave this off and
    // keep the page-level sticky behavior unchanged.
    scrollContained = false,
    scrollHeight = '70vh',
    // Sticky offset for the bank-lane header (Cards view) on the page. Defaults
    // to 8.5rem to clear the wizard's sticky offer-tabs strip (.oe-tabs-sticky:
    // top 4rem + ~3.5rem tabs + 1rem seal). Pages WITHOUT that strip (Available
    // Offers, Received Claims) pass a smaller value (e.g. '4rem' to clear only
    // the topbar). Ignored when inModal/scrollContained (header pins to box top).
    stickyHeaderTop = '8.5rem',
}) => {
    const [detailsDialogVisible, setDetailsDialogVisible] = useState(false);
    const [selectedOfferDetails, setSelectedOfferDetails] = useState(null);

    // Offer filter state (All / Old / New)
    const [offerFilter, setOfferFilter] = useState('all');
    const hasNewOffers = offers.some(o => o.is_new === true);
    const hasOldOffers = offers.some(o => !o.is_new); // undefined or false = old
    const showFilterButtons = hasNewOffers && hasOldOffers;

    // Edit offer state
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [editingOfferIndex, setEditingOfferIndex] = useState(null);
    const [editForm, setEditForm] = useState({});

    // EMI Recalculate dialog (for ROI-band offers — user picks a specific rate within the band)
    const [recalcDialogVisible, setRecalcDialogVisible] = useState(false);
    const [recalcOffer, setRecalcOffer] = useState(null);
    // Per-offer recalc overrides keyed by offer id — applied on top of the offer when rendering.
    // Lives in component state only (not persisted); reopening the dialog or page refresh clears it
    // unless the parent chooses to persist it via props.
    const [recalcOverrides, setRecalcOverrides] = useState({});

    // Clear per-offer recalc overrides whenever the offers list is REGENERATED.
    // recalcOverrides are keyed by offer id/recalcKey (product+range+company+roi),
    // which a regenerated offer REUSES — so a stale override (e.g. a wrong ₹23cr
    // eligibility from an earlier modal recalc) would silently re-attach to the
    // fresh, correct offer via mergeRecalc, making regenerate show old data. The
    // fingerprint changes whenever offers are replaced (count + ids + eligibility),
    // so overrides reset to the fresh backend figures on every regenerate.
    const offersFingerprint = useMemo(
        () => (offers || []).map((o) => `${o._recalcKey ?? o.offer_id ?? o.id}:${o.max_loan_eligibility}:${o.roi}`).join('|'),
        [offers]
    );
    useEffect(() => {
        setRecalcOverrides({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offersFingerprint]);

    const openRecalcDialog = (offer) => {
        setRecalcOffer(offer);
        setRecalcDialogVisible(true);
    };

    const handleRecalcApply = (override) => {
        if (!recalcOffer?.id) return;
        setRecalcOverrides(prev => ({
            ...prev,
            [recalcOffer.id]: override,
        }));
    };

    // Merge any recalc override into the offer so downstream rendering just sees
    // the new values. Keyed on the STABLE per-offer id (offer_id), not offer.id —
    // offer.id can be missing or duplicated across a lender's offers, which made
    // the band slider only work on the first offer.
    const mergeRecalc = useCallback((offer) => {
        const key = offer?._recalcKey ?? offer?.offer_id ?? offer?.id;
        if (key == null) return offer;
        const override = recalcOverrides[key];
        if (!override) return offer;
        return { ...offer, ...override };
    }, [recalcOverrides]);

    // Calculate EMI from loan amount, ROI, tenure
    const calculateEmi = useCallback((principal, annualRate, tenureMonths) => {
        if (!principal || !annualRate || !tenureMonths) return 0;
        const monthlyRate = annualRate / 12 / 100;
        if (monthlyRate === 0) return Math.round(principal / tenureMonths);
        const factor = Math.pow(1 + monthlyRate, tenureMonths);
        return Math.round((principal * monthlyRate * factor) / (factor - 1));
    }, []);

    // Card-level band-rate recalc: when the user drags the slider ON the offer
    // card, recompute EMI / EMI-per-lac / eligibility / cost at the new rate and
    // write them onto recalcOverrides so mergeRecalc updates that card inline.
    // Mirrors the dialog's recalculateEligibility math. eligibilityParams supplies
    // net salary / existing EMI; FOIR comes from the offer's breakdown (default 50).
    const applyCardRoi = useCallback((offer, newRoi) => {
        const roi = Number(newRoi);
        const key = offer?._recalcKey ?? offer?.offer_id ?? offer?.id;
        if (key == null || !roi || roi <= 0) return;
        // Tenure priority: borrower-entered tenure → range's MIN tenure
        // (discovery-mode fallback the backend already uses for EMI/lakh).
        // Without min_tenure here, discovery-mode cards left tenure = 0 →
        // emiPerLac fell back to offer.emi_per_lac (unchanged) and the
        // ROI slider had no effect on the displayed EMI/lakh.
        const tenure = parseInt(offer.tenure || offer.tenure_months || offer.min_tenure || 0);
        const loanAmount = parseFloat(offer.loan_amount || 0);
        const emiPerLac = (roi > 0 && tenure > 0) ? calculateEmi(100000, roi, tenure) : parseFloat(offer.emi_per_lac || 0);

        // Eligibility (independent of entered amount)
        // Priority for finding "Available for EMI":
        //   1. offer.eligibility_breakdown.available_for_emi  — backend-computed, always correct
        //      (uses weighted income via multipliers + all obligations × their %s).
        //      Works for EVERY card because the backend ships eligibility_breakdown per offer.
        //   2. eligibilityParams (parent prop)               — older flat-prop fallback, only
        //      present in some pages (lead wizard); not in Available Offers / Marketplace.
        // Without this priority, dragging the ROI slider on a card whose parent doesn't
        // pass eligibilityParams (e.g. Available Offers) left Max Eligibility frozen.
        let maxElig = offer.max_loan_eligibility;
        let availableForEmi = null;
        const bd = offer.eligibility_breakdown;
        if (bd && Number(bd.available_for_emi) > 0) {
            // Backend's authoritative "available for EMI" already encapsulates
            // (totalIncome × FOIR) − totalObligations using the right multipliers.
            availableForEmi = Number(bd.available_for_emi);
        } else if (bd && Number(bd.total_income) > 0) {
            // Older breakdowns may not carry available_for_emi explicitly — rebuild it.
            const totalIncome = Number(bd.total_income) || 0;
            const totalObligations = Number(bd.total_obligations) || 0;
            const foir = Number(bd.foir) || 50;
            availableForEmi = Math.max(0, (totalIncome * (foir / 100)) - totalObligations);
        } else if (eligibilityParams) {
            // Last-resort fallback: parent-provided flat params (lead wizard path).
            const salary = eligibilityParams.net_salary_per_month || 0;
            const foir = offer.foir_percentage || bd?.foir || 50;
            const existingEmi = eligibilityParams.total_existing_loan_emi || 0;
            availableForEmi = Math.max(0, (salary * (foir / 100)) - existingEmi);
        }
        if (availableForEmi !== null && emiPerLac > 0) {
            maxElig = Math.round((availableForEmi / emiPerLac) * 100000);
        }

        // EMI + cost (only when an amount was entered)
        const pf = parseFloat(offer.processing_fee || 0);
        let emi = parseFloat(offer.emi || 0);
        let totalInterest = parseFloat(offer.total_interest || 0);
        let totalCost = parseFloat(offer.total_cost || 0);
        if (loanAmount > 0 && tenure > 0) {
            emi = calculateEmi(loanAmount, roi, tenure);
            totalInterest = Math.round(emi * tenure - loanAmount);
            totalCost = totalInterest + pf;
        }

        setRecalcOverrides(prev => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                roi,
                emi_per_lac: Math.round(emiPerLac),
                emi: emi > 0 ? Math.round(emi) : (prev[key]?.emi),
                total_interest: totalInterest > 0 ? totalInterest : (prev[key]?.total_interest),
                total_cost: totalCost > 0 ? totalCost : (prev[key]?.total_cost),
                max_loan_eligibility: maxElig,
            },
        }));
    }, [calculateEmi, eligibilityParams]);

    const handleEditOffer = (offer, index) => {
        // Copy ALL fields from the offer — spread the entire object
        setEditForm({ ...offer });
        setEditingOfferIndex(index);
        setEditDialogVisible(true);
    };

    const handleEditFieldChange = (field, value) => {
        setEditForm(prev => {
            const updated = { ...prev, [field]: value };
            // Auto-recalculate EMI when core fields change
            if (['loan_amount', 'roi', 'tenure'].includes(field)) {
                const la = field === 'loan_amount' ? value : updated.loan_amount;
                const r = field === 'roi' ? value : updated.roi;
                const t = field === 'tenure' ? value : (updated.tenure || updated.tenure_months);
                updated.emi = calculateEmi(la, r, t);
            }
            return updated;
        });
    };

    const handleSaveEdit = () => {
        if (editingOfferIndex === null || !onEditOffer) return;
        const f = editForm;
        const tenure = f.tenure || f.tenure_months || 0;
        const emi = f.emi || 0;
        const loanAmount = f.loan_amount || 0;
        const roi = f.roi || 0;
        const pf = typeof f.processing_fee === 'number' ? f.processing_fee : 0;
        const totalInterest = emi && tenure && loanAmount ? Math.round(emi * tenure - loanAmount) : (f.total_interest || 0);
        const emiPerLac = roi && tenure ? Math.round(calculateEmi(100000, roi, tenure)) : f.emi_per_lac;

        // Sync formatted display fields from raw editable fields
        const formatChargeSlabs = (raw) => {
            if (!raw || typeof raw !== 'string') return raw;
            return raw.split(',').map(slab => {
                const parts = slab.trim().split('-');
                if (parts.length === 3) {
                    const rate = parts[2].replace(/^p/i, '');
                    return `${rate}% for ${parts[0]}-${parts[1]} months`;
                }
                // Single token like "p0" / "p2.5" / "r500" / "0" — optionally with a
                // comparison operator (">p25"). Strip the type prefix, keep the operator;
                // a zero charge renders as a plain "0" (compact) per the 2026-07-04 UX.
                let s = slab.trim().toLowerCase();
                let op = '';
                const om = s.match(/^(>=|<=|>|<)/);
                if (om) { op = om[1]; s = s.slice(op.length).trim(); }
                const stripped = s.startsWith('p') || s.startsWith('r') ? s.substring(1) : s;
                if (stripped !== '' && !isNaN(stripped)) {
                    const num = Number(stripped);
                    if (num === 0 && op === '') return '0';
                    if (s.startsWith('r')) return op + '₹' + num.toLocaleString('en-IN');
                    if (s.startsWith('p') || num < 100) return op + num + '%';
                    return op + '₹' + num.toLocaleString('en-IN');
                }
                return slab;
            }).join(', ');
        };

        const updatedOffer = {
            ...f,
            roi: roi, interest_rate: roi,
            tenure: tenure, tenure_months: tenure,
            processing_fee: pf,
            emi: emi, emi_per_lac: emiPerLac,
            total_interest: totalInterest > 0 ? totalInterest : f.total_interest,
            total_cost: totalInterest > 0 ? totalInterest + pf : f.total_cost,
            // Sync formatted display fields
            pre_closure_charges: formatChargeSlabs(f.foreclosurecharges) || f.pre_closure_charges,
            part_payment_charges: formatChargeSlabs(f.ppr) || f.part_payment_charges,
            part_payment_amount: f.ppa,
            part_payment_times: f.ppt ? parseInt(f.ppt) : f.part_payment_times,
            part_payment_month: f.part_payment_month ? parseInt(f.part_payment_month) : f.part_payment_month,
            _edited: true,
            is_discovery: loanAmount > 0 ? false : f.is_discovery,
        };
        onEditOffer(editingOfferIndex, updatedOffer);
        setEditDialogVisible(false);
    };

    // Eligibility breakdown modal state
    const [eligibilityDialogVisible, setEligibilityDialogVisible] = useState(false);
    const [selectedEligibilityBreakdown, setSelectedEligibilityBreakdown] = useState(null);

    // Editable eligibility breakdown state
    const [editableBreakdown, setEditableBreakdown] = useState(null);
    // Max Eligibility (Multiplier) is a secondary/optional path — collapsed by
    // default so it doesn't take sticky space; the header row toggles it.
    const [multiplierExpanded, setMultiplierExpanded] = useState(false);
    // "How it's calculated" (the formula box) is reference detail — collapsed by
    // default; the tenure note below it stays visible regardless.
    const [formulaExpanded, setFormulaExpanded] = useState(false);
    const [isEdited, setIsEdited] = useState(false);

    // ── Flat all-offers layout state ─────────────────────────────────────
    // Each column = one offer. User can:
    //   • Lock up to 2 offer columns to keep them visible while paging others
    //   • Page the set of 4 visible offers with Prev/Next when there are >4
    //
    // lockedOfferIds — array of offer ids pinned (max 2).
    // currentOfferStartIndex — paging cursor over non-locked offers.
    const [lockedOfferIds, setLockedOfferIds] = useState([]);
    const [currentOfferStartIndex, setCurrentOfferStartIndex] = useState(0);

    // ── Bank-grouped (accordion) view state — used by the Table view, which now
    // renders Prototype A: offers grouped by lender into expandable bank cards.
    //   • lockedBankNames  — banks pinned to the top (max 2), survive scroll.
    //   • expandedBankNames — banks whose offer list is open. null = "not yet
    //     initialised", so we can auto-open the best bank on first render.
    const [lockedBankNames, setLockedBankNames] = useState([]);
    const [expandedBankNames, setExpandedBankNames] = useState(null);
    const MAX_LOCKED_BANKS = 2;
    // Cards view = bank LANES (Prototype C). Page cursor over banks (3 lanes/page);
    // pinned banks always stay visible.
    const [lanePage, setLanePage] = useState(0);
    const LANES_PER_PAGE = 3;

    // Mobile detection state
    const [isMobile, setIsMobile] = useState(false);
    // On mobile we show 1 offer at a time.
    const [mobileOfferIndex, setMobileOfferIndex] = useState(0);

    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 576);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const MAX_VISIBLE_OFFERS = 3;  // columns visible at once on desktop (3 to leave room for filter sidebar)
    const MAX_LOCKED_OFFERS = 2;   // max pinned offer columns

    // ── Filter sidebar state (cards view only) ───────────────────────────────
    // Live filtering — every toggle updates filteredOffers immediately.
    // Mobile shows the filters in a slide-in drawer (toggled by filterDrawerOpen).
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [filters, setFilters] = useState({
        quickEmiBudget: null,                     // numeric: max EMI user is comfortable with
        // selectedLenders: null = "all selected" (permissive default).
        // Once user starts checking/unchecking individual lenders this becomes a Set of bank_names.
        selectedLenders: null,
        maxInterestRate: 20,                      // upper bound (slider)
        // Borrower's CIBIL score — single number. When set, only offers whose
        // accepted-CIBIL band [min_cibil..max_cibil] contains this value are kept.
        // null = no filter.
        userCibil: null,
        listType: { listed: true, unlisted: true },
        verification: { verified: true, notVerified: true },
        features: {
            noPreClosure: false,
            zeroProcessingFee: false,
            partPaymentAllowed: false,
            bestRateOnly: false,                  // top 3 by ROI
        },
    });
    const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
    const toggleFilterChild = (groupKey, childKey) => setFilters((prev) => ({
        ...prev,
        [groupKey]: { ...prev[groupKey], [childKey]: !prev[groupKey][childKey] },
    }));
    const resetFilters = () => setFilters({
        quickEmiBudget: null,
        selectedLenders: null,
        maxInterestRate: 20,
        userCibil: null,
        listType: { listed: true, unlisted: true },
        verification: { verified: true, notVerified: true },
        features: {
            noPreClosure: false,
            zeroProcessingFee: false,
            partPaymentAllowed: false,
            bestRateOnly: false,
        },
    });

    // Toggle a single lender in the selectedLenders Set. If state is null
    // (the "all selected" sentinel), seed it from the full unique-lenders list
    // so unchecking exactly one feels right.
    const toggleLender = (bankName, allLenders) => {
        setFilters((prev) => {
            const seeded = prev.selectedLenders ?? new Set(allLenders);
            const next = new Set(seeded);
            if (next.has(bankName)) next.delete(bankName);
            else next.add(bankName);
            return { ...prev, selectedLenders: next };
        });
    };

    // Discovery mode = offers generated WITHOUT an entered loan amount. The
    // backend stamps each offer with an authoritative `is_discovery` flag
    // (calculation_method === 'discovery'); trust that first and fall back to
    // the loan_amount heuristic only when the flag is absent (older payloads).
    const isDiscoveryMode = offers.length > 0 && offers.every((o) =>
        (o.is_discovery === true || o.calculation_method === 'discovery')
        || !(o.loan_amount != null && Number(o.loan_amount) > 0)
    );

    // True when ABB offers were priced off a typed figure rather than a statement.
    // Each lender's own sampling days were skipped, so these amounts are
    // approximate — worth saying once above the list rather than on every card.
    const hasManualAbbOffers = offers.some((o) => o.abb_source === 'manual_entry');


    // View mode toggle: 'table' (horizontal rows, default) or 'cards' (vertical).
    // Persisted in localStorage so the user's choice survives page reloads — a
    // stored preference still wins over the default.
    // Mobile is forced to 'cards' — table view is unusable on phones.
    const [viewMode, setViewMode] = useState(() => {
        try {
            return localStorage.getItem('offersComparisonView') || 'table';
        } catch (e) {
            return 'table';
        }
    });
    const effectiveViewMode = isMobile ? 'cards' : viewMode;
    const setAndPersistViewMode = (mode) => {
        setViewMode(mode);
        try { localStorage.setItem('offersComparisonView', mode); } catch (e) { /* ignore */ }
    };

    // Per-row expand state in table view — Set of offer ids that are open.
    const [expandedOfferIds, setExpandedOfferIds] = useState(new Set());
    const toggleRowExpand = (offerId) => {
        setExpandedOfferIds((prev) => {
            const next = new Set(prev);
            if (next.has(offerId)) next.delete(offerId);
            else next.add(offerId);
            return next;
        });
    };

    // Salary Analysis expanded state — GLOBAL toggle (one switch for ALL cards).
    // Per-card toggling broke column alignment because opening one card grew it
    // by ~200px while siblings stayed short. Closed by default per client UX.
    const [salaryAnalysisOpen, setSalaryAnalysisOpen] = useState(false);
    const toggleSalaryAnalysis = () => setSalaryAnalysisOpen((v) => !v);

    // Per-card "Conditions" expanded state — first 3 lines shown by default,
    // user clicks "more" to expand the full text.
    const [conditionsOpenIds, setConditionsOpenIds] = useState(new Set());
    const toggleConditionsOpen = (offerId) => {
        setConditionsOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(offerId)) next.delete(offerId);
            else next.add(offerId);
            return next;
        });
    };

    // Parse multiplier string like "100%" or "50%" to decimal
    const parseMultiplier = (m) => {
        if (typeof m === 'number') return m;
        if (typeof m === 'string') return parseFloat(m) / 100;
        return 1;
    };

    // Recalculate eligibility from edited values
    const recalculateEligibility = useCallback(() => {
        if (!editableBreakdown) return;

        const bd = { ...editableBreakdown };
        let totalIncome = 0;
        let totalObligations = 0;

        // Some methods (notably NET_PROFIT) emit income_breakdown rows that are
        // ANNUAL and DUPLICATED (per-year cash profit + a "consider" row + a
        // "total annual" row) and carry NO multiplier. Summing them as if they were
        // monthly weighted income is wrong (it inflates income ~Nx and gives a
        // nonsense eligibility like ₹5.9 cr). Detect that: if any income row lacks a
        // multiplier, we do NOT re-sum the rows — we trust the backend's already-
        // correct monthly `total_income`. Only methods whose rows all carry a
        // multiplier (salary-style) are safe to re-sum here.
        const incomeRowsAreSummable = (bd.applicants_breakdown || []).every(applicant =>
            (applicant.income_breakdown || []).every(item => item.multiplier != null && item.multiplier !== '')
        );

        // Recalculate each applicant
        const updatedApplicants = (bd.applicants_breakdown || []).map(applicant => {
            let applicantIncome = 0;
            const updatedIncomeBreakdown = (applicant.income_breakdown || []).map(item => {
                const multiplier = parseMultiplier(item.multiplier);
                const adjusted = Math.round(parseFloat(item.value || 0) * multiplier);
                if (incomeRowsAreSummable) applicantIncome += adjusted;
                return { ...item, adjusted };
            });

            let applicantObligations = 0;
            const updatedObligationBreakdown = (applicant.obligation_breakdown || []).map(item => {
                const multiplier = parseMultiplier(item.multiplier);
                const adjusted = Math.round(parseFloat(item.value || 0) * multiplier);
                applicantObligations += adjusted;
                return { ...item, adjusted };
            });

            if (incomeRowsAreSummable) totalIncome += applicantIncome;
            totalObligations += applicantObligations;

            return {
                ...applicant,
                income: incomeRowsAreSummable ? applicantIncome : applicant.income,
                income_breakdown: updatedIncomeBreakdown,
                obligations: applicantObligations,
                obligation_breakdown: updatedObligationBreakdown,
            };
        });

        // Non-summable methods (Net Profit etc.): keep the backend's correct monthly
        // total_income instead of the (wrong) re-summed value.
        if (!incomeRowsAreSummable) {
            totalIncome = parseFloat(bd.total_income || 0);
        }

        // FOIR and ROI are now editable in the dialog. When ROI changes, EMI per Lac must
        // be recomputed from the PMT formula — else the "eligible amount = available / EMI per Lac"
        // formula uses a stale EMI per Lac and gives wrong eligibility.
        const foir = parseFloat(bd.foir || 0);
        const roi = parseFloat(bd.roi || 0);
        const tenure = parseInt(bd.tenure || bd.tenure_months || 0);
        let emiPerLac = parseFloat(bd.emi_per_lac || 0);
        if (roi > 0 && tenure > 0) {
            // FULL-PRECISION EMI per Lac for the eligibility formula — do NOT use
            // calculateEmi(), which rounds to a whole rupee. Rounding EMI/lac to an
            // integer (e.g. 2445.109 → 2445) makes `available / emiPerLac` slightly
            // larger, so clicking Recalculate WITHOUT changing anything drifted the
            // eligibility upward. This matches the backend's calculateEmiPerLac()
            // (unrounded), so an unchanged recalc reproduces the exact same figure.
            const r = roi / 12 / 100;
            const factor = Math.pow(1 + r, tenure);
            emiPerLac = r === 0 ? (100000 / tenure) : (100000 * r * factor) / (factor - 1);
        }
        // Match the backend EXACTLY: it does NOT round income-after-FOIR before
        // dividing (BaseEligibilityService line 139), so neither do we — otherwise
        // an unchanged Recalculate drifts by a few rupees. Only the final eligible
        // amount is rounded (as the backend does).
        const incomeAfterFoir = totalIncome * (foir / 100);
        const availableForEmi = Math.max(0, incomeAfterFoir - totalObligations);
        const eligibleAmount = emiPerLac > 0 ? Math.round((availableForEmi / emiPerLac) * 100000) : 0;

        // Eligibility as per multiplier: (Total Income - Total Obligations) × netsalarymultiple
        const netsalarymultiple = parseFloat(bd.netsalarymultiple || 0);
        const netIncomeAfterObligations = totalIncome - totalObligations;
        const eligibilityAsPerMultiplier = Math.round(netIncomeAfterObligations * netsalarymultiple);

        // Recompute the actual EMI + cost for the entered loan amount at the new ROI,
        // so the offer card's EMI / Total Interest / Total Cost stay in sync with the
        // rate the user picked on the band slider.
        const loanAmount = parseFloat(bd.loan_amount || 0);
        let newEmi = parseFloat(bd.emi || 0);
        let newTotalInterest = parseFloat(bd.total_interest || 0);
        let newTotalCost = parseFloat(bd.total_cost || 0);
        const pf = parseFloat(bd.processing_fee || 0);
        if (loanAmount > 0 && roi > 0 && tenure > 0) {
            newEmi = calculateEmi(loanAmount, roi, tenure);
            newTotalInterest = Math.round(newEmi * tenure - loanAmount);
            newTotalCost = newTotalInterest + pf;
        }

        setEditableBreakdown({
            ...bd,
            applicants_breakdown: updatedApplicants,
            total_income: totalIncome,
            income_after_foir: incomeAfterFoir,
            total_obligations: totalObligations,
            available_for_emi: availableForEmi,
            emi_per_lac: Math.round(emiPerLac * 100) / 100, // persist recomputed PMT result
            emi: newEmi,
            total_interest: newTotalInterest,
            total_cost: newTotalCost,
            eligible_amount: eligibleAmount,
            eligibility_as_per_multiplier: eligibilityAsPerMultiplier,
            is_eligible: eligibleAmount > 0,
            eligibility_status: eligibleAmount > 0 ? 'eligible' : 'not_eligible',
        });
        setIsEdited(true);

        // Push the new figures onto the offer card (dialog + card stay in sync).
        if (bd.offer_id) {
            setRecalcOverrides(prev => ({
                ...prev,
                [bd.offer_id]: {
                    ...(prev[bd.offer_id] || {}),
                    roi,
                    emi: newEmi > 0 ? Math.round(newEmi) : (prev[bd.offer_id]?.emi),
                    emi_per_lac: Math.round(emiPerLac),
                    total_interest: newTotalInterest > 0 ? newTotalInterest : (prev[bd.offer_id]?.total_interest),
                    total_cost: newTotalCost > 0 ? newTotalCost : (prev[bd.offer_id]?.total_cost),
                    max_loan_eligibility: eligibleAmount,
                },
            }));
        }
    }, [editableBreakdown, calculateEmi]);

    // Edit one date's closing balance in the ABB table (ABB method only).
    // Recomputes that month's ABB (mean of its picked balances), then the Overall
    // ABB (mean of the monthly ABBs), and writes the new ABB into the income
    // breakdown's `average_bank_balance` row so recalculateEligibility (which
    // re-sums summable rows) picks it up. Session-only what-if — mirrors how
    // ROI/FOIR edits work; nothing is persisted to the statement. A debounced
    // effect below runs the eligibility recalc so the final figure updates live.
    // `day` is the CONFIGURED sampling day being edited (e.g. 5), not an array
    // index — the table renders one cell per requested date, and a skipped date
    // has no picked_balances slot yet. Editing it inserts the (day, balance) into
    // dates_used/picked_balances (keeping them index-aligned), then recomputes.
    const updateAbbBalance = useCallback((monthIndex, day, newValue) => {
        setEditableBreakdown((prev) => {
            if (!prev?.abb_summary?.monthly_abbs) return prev;
            const abb = prev.abb_summary;
            const months = abb.monthly_abbs.map((m, mi) => {
                if (mi !== monthIndex) return m;
                const used = Array.isArray(m.dates_used) ? [...m.dates_used] : [];
                const picked = Array.isArray(m.picked_balances) ? [...m.picked_balances] : [];
                const at = used.indexOf(day);
                if (at >= 0) {
                    picked[at] = Number(newValue) || 0;   // existing sampled date
                } else {
                    used.push(day);                        // was skipped — now filled
                    picked.push(Number(newValue) || 0);
                }
                const monthlyAbb = picked.length
                    ? Math.round((picked.reduce((a, b) => a + (Number(b) || 0), 0) / picked.length) * 100) / 100
                    : 0;
                return { ...m, dates_used: used, picked_balances: picked, monthly_abb: monthlyAbb };
            });
            const monthAbbs = months.map((m) => Number(m.monthly_abb) || 0).filter((n) => n > 0);
            const overallAbb = monthAbbs.length
                ? Math.round((monthAbbs.reduce((a, b) => a + b, 0) / monthAbbs.length) * 100) / 100
                : 0;

            // Mirror the new ABB into the income breakdown so the eligibility
            // recalc (and the Income Breakdown display) use it. ABB's income row
            // is field='average_bank_balance', multiplier '100%'.
            const applicants = (prev.applicants_breakdown || []).map((ap, ai) => {
                if (ai !== 0) return ap; // ABB is a single-applicant (main) figure
                const rows = (ap.income_breakdown || []).map((r) =>
                    String(r.field) === 'average_bank_balance'
                        ? { ...r, value: overallAbb, adjusted: overallAbb }
                        : r);
                return { ...ap, income_breakdown: rows };
            });

            return {
                ...prev,
                abb_summary: { ...abb, monthly_abbs: months, abb: overallAbb },
                applicants_breakdown: applicants,
                _abb_edited: true, // signals the debounced effect to recalc eligibility
            };
        });
    }, []);

    // All-calendar-days mode: edit a MONTH's average balance directly (there are
    // no fixed sampling dates). The overall ABB is DAY-WEIGHTED here — the engine
    // pools every calendar day — so we recompute it as Σ(monthABB × days) ÷ Σ(days),
    // NOT a plain mean of the monthly figures, to stay faithful to the pooled
    // formula. Session-only what-if; mirrors into the income row for the recalc.
    const updateAbbMonthlyAllDays = useCallback((monthIndex, newValue) => {
        setEditableBreakdown((prev) => {
            if (!prev?.abb_summary?.monthly_abbs) return prev;
            const abb = prev.abb_summary;
            const months = abb.monthly_abbs.map((m, mi) =>
                mi === monthIndex ? { ...m, monthly_abb: Number(newValue) || 0 } : m);
            // Day-weighted overall = Σ(monthABB × days) ÷ Σ(days).
            let weighted = 0;
            let totalDays = 0;
            months.forEach((m) => {
                const days = Number(m.days_counted) || 0;
                weighted += (Number(m.monthly_abb) || 0) * days;
                totalDays += days;
            });
            const overallAbb = totalDays > 0 ? Math.round((weighted / totalDays) * 100) / 100 : 0;

            const applicants = (prev.applicants_breakdown || []).map((ap, ai) => {
                if (ai !== 0) return ap;
                const rows = (ap.income_breakdown || []).map((r) =>
                    String(r.field) === 'average_bank_balance'
                        ? { ...r, value: overallAbb, adjusted: overallAbb }
                        : r);
                return { ...ap, income_breakdown: rows };
            });

            return {
                ...prev,
                abb_summary: { ...abb, monthly_abbs: months, abb: overallAbb },
                applicants_breakdown: applicants,
                _abb_edited: true,
            };
        });
    }, []);

    // GST: edit one month's taxable turnover → recompute the AVERAGE monthly
    // turnover (sum ÷ months) → income = avg × margin% → eligibility, live.
    // Session-only what-if; mirrors into the income row so recalculateEligibility
    // picks it up. GST is monthly (no ÷12), matching the engine.
    const updateGstMonthly = useCallback((monthIndex, newValue) => {
        setEditableBreakdown((prev) => {
            if (!prev?.gst_summary?.monthly) return prev;
            const gs = prev.gst_summary;
            const months = gs.monthly.map((m, mi) =>
                mi === monthIndex ? { ...m, turnover: Number(newValue) || 0 } : m);
            const total = months.reduce((a, m) => a + (Number(m.turnover) || 0), 0);
            const avg = months.length ? Math.round(total / months.length) : 0;
            const margin = Number(gs.margin) || 0.08;

            // The GST income row is field='gst_turnover', value = the RAW average
            // turnover, multiplier = the margin ('8%'). recalculateEligibility does
            // value × margin itself, so we write the new average turnover into
            // `value` (NOT the post-margin income) and let the recalc apply the %.
            const applicants = (prev.applicants_breakdown || []).map((ap, ai) => {
                if (ai !== 0) return ap;
                const rows = (ap.income_breakdown || []).map((r) =>
                    String(r.field) === 'gst_turnover'
                        ? { ...r, value: avg, adjusted: Math.round(avg * margin) }
                        : r);
                return { ...ap, income_breakdown: rows };
            });

            return {
                ...prev,
                gst_summary: { ...gs, monthly: months, avg_turnover: avg },
                applicants_breakdown: applicants,
                _abb_edited: true, // reuse the same debounced eligibility recalc
            };
        });
    }, []);

    // GST: edit the MARGIN % (default 8% / nature-based). Income = avg turnover ×
    // margin, so changing it re-derives income → eligibility live. Stored as a
    // FRACTION on gst_summary.margin (0.08) and mirrored to the income row's
    // multiplier so recalculateEligibility applies it. `pct` is the whole-number
    // percent from the input (e.g. 10 → 0.10).
    const updateGstMargin = useCallback((pct) => {
        setEditableBreakdown((prev) => {
            if (!prev?.gst_summary) return prev;
            const gs = prev.gst_summary;
            const marginFraction = Math.max(0, (Number(pct) || 0) / 100);
            const avg = Number(gs.avg_turnover) || 0;
            const applicants = (prev.applicants_breakdown || []).map((ap, ai) => {
                if (ai !== 0) return ap;
                const rows = (ap.income_breakdown || []).map((r) =>
                    String(r.field) === 'gst_turnover'
                        // Keep value = raw avg turnover; update the multiplier label
                        // (recalculateEligibility parses '10%' → 0.10 and applies it).
                        ? { ...r, value: avg, multiplier: `${Math.round(marginFraction * 10000) / 100}%`, adjusted: Math.round(avg * marginFraction) }
                        : r);
                return { ...ap, income_breakdown: rows };
            });
            return {
                ...prev,
                gst_summary: { ...gs, margin: marginFraction },
                applicants_breakdown: applicants,
                _abb_edited: true,
            };
        });
    }, []);

    // NET PROFIT — recompute the monthly income + income row + dip analysis from
    // the per-year cash-profit table. Cash Profit(year) = PBT + Dep×70% + Salary +
    // Partner Interest; Considered = AVERAGE of the years; Monthly = Considered ÷ 12.
    // Dip: latest vs previous; if latest < previous and dip% > threshold → decline
    // (income 0). Shared by both the component-edit and the recompute path.
    const recomputeNetProfit = useCallback((years, analysis, prev) => {
        // Depreciation add-back % is editable — read it from state (np_dep_pct,
        // a fraction), else the year's stored 'depreciation_policy_percent'
        // ('70%'), else 0.70.
        const depPct = Number(prev?.np_dep_pct) > 0
            ? Number(prev.np_dep_pct)
            : (parseFloat(String(years?.[0]?.depreciation_policy_percent || '70')) / 100 || 0.70);
        const depLabel = `${Math.round(depPct * 10000) / 100}%`;
        const cp = (y) => Math.round(
            (Number(y.pbt) || 0)
            + (Number(y.depreciation_actual) || 0) * depPct
            + (Number(y.director_salary) || 0)
            + (Number(y.partner_interest) || 0)
        );
        const withCp = years.map((y) => ({ ...y, depreciation_policy_percent: depLabel, depreciation_considered: Math.round((Number(y.depreciation_actual) || 0) * depPct), cash_profit: cp(y) }));
        const cps = withCp.map((y) => Number(y.cash_profit) || 0);
        const avg = cps.length ? Math.round(cps.reduce((a, b) => a + b, 0) / cps.length) : 0;

        // Dip check — latest (first) vs previous (second). Decline zeroes income.
        const latest = cps[0] ?? 0;
        const previous = cps[1] ?? 0;
        const threshold = Number(analysis?.dip_threshold) || 50;
        let dipPercent = 0;
        let declined = false;
        if (previous > 0 && latest < previous) {
            dipPercent = Math.round(((previous - latest) / previous) * 100 * 10) / 10;
            declined = dipPercent > threshold;
        }
        const consideredYearly = declined ? 0 : avg;
        const monthlyIncome = Math.round(consideredYearly / 12);

        const applicants = (prev.applicants_breakdown || []).map((ap, ai) => {
            if (ai !== 0) return ap;
            const rows = (ap.income_breakdown || []).map((r) =>
                String(r.field) === 'net_profit'
                    ? { ...r, value: monthlyIncome, multiplier: '100%', adjusted: monthlyIncome }
                    : r);
            return { ...ap, income_breakdown: rows, year_wise_data: withCp };
        });
        const newAnalysis = {
            ...(analysis || {}),
            latest_cash_profit: latest,
            previous_cash_profit: previous,
            average_cash_profit: avg,
            dip_percent: dipPercent,
            dip_threshold: threshold,
            comparison_result: declined ? 'declined_dip' : 'average_considered',
            considered_value: consideredYearly,
        };
        return {
            ...prev,
            np_year_wise: withCp,
            np_year_analysis: newAnalysis,
            applicants_breakdown: applicants,
            _abb_edited: true,
        };
    }, []);

    // Edit a component (pbt / depreciation_actual / director_salary /
    // partner_interest) of one year → live recompute.
    const updateNpComponent = useCallback((yearIndex, field, newValue) => {
        setEditableBreakdown((prev) => {
            if (!Array.isArray(prev?.np_year_wise)) return prev;
            const years = prev.np_year_wise.map((y, i) =>
                i === yearIndex ? { ...y, [field]: Number(newValue) || 0 } : y);
            return recomputeNetProfit(years, prev.np_year_analysis, prev);
        });
    }, [recomputeNetProfit]);

    // Flat-only case: edit the single yearly Net Profit → ÷12 monthly income.
    const updateNpFlat = useCallback((yearlyValue) => {
        setEditableBreakdown((prev) => {
            const monthlyIncome = Math.round((Number(yearlyValue) || 0) / 12);
            const applicants = (prev.applicants_breakdown || []).map((ap, ai) => {
                if (ai !== 0) return ap;
                const rows = (ap.income_breakdown || []).map((r) =>
                    String(r.field) === 'net_profit'
                        ? { ...r, value: monthlyIncome, multiplier: '100%', adjusted: monthlyIncome }
                        : r);
                return { ...ap, income_breakdown: rows };
            });
            return { ...prev, income_value: Number(yearlyValue) || 0, applicants_breakdown: applicants, _abb_edited: true };
        });
    }, []);

    // Edit the Depreciation add-back % (default 70%). Stored as a fraction on
    // np_dep_pct; recomputeNetProfit re-derives every year's depreciation_considered
    // + cash profit → average → income → eligibility, live.
    const updateNpDepPct = useCallback((pct) => {
        setEditableBreakdown((prev) => {
            if (!Array.isArray(prev?.np_year_wise)) return prev;
            const withPct = { ...prev, np_dep_pct: Math.max(0, (Number(pct) || 0) / 100) };
            return recomputeNetProfit(withPct.np_year_wise, withPct.np_year_analysis, withPct);
        });
    }, [recomputeNetProfit]);

    // Edit the Dip threshold % (default 50%). recomputeNetProfit reads it from
    // np_year_analysis.dip_threshold, so update that then recompute.
    const updateNpThreshold = useCallback((pct) => {
        setEditableBreakdown((prev) => {
            if (!Array.isArray(prev?.np_year_wise)) return prev;
            const nextAnalysis = { ...(prev.np_year_analysis || {}), dip_threshold: Math.max(0, Number(pct) || 0) };
            return recomputeNetProfit(prev.np_year_wise, nextAnalysis, { ...prev, np_year_analysis: nextAnalysis });
        });
    }, [recomputeNetProfit]);

    // Auto-recalculate (debounced) when the user drags the rate slider / edits
    // ROI or FOIR — no need to click "Recalculate". Watches only the primitive
    // roi/foir values (recalc never changes them, so this can't loop). Skips
    // out-of-band / invalid values so a half-typed number doesn't thrash.
    //
    // IMPORTANT: must NOT fire on OPEN. The dialog opens with the backend's
    // already-correct figures; auto-recalculating on open re-derives them from the
    // income_breakdown rows (which for Net Profit are ANNUAL + duplicated) and
    // produces a wildly wrong number (e.g. ₹5.9 cr). So we remember the roi/foir the
    // dialog opened with and only recalc once the USER actually changes one of them.
    const eligRoi = editableBreakdown?.roi;
    const eligFoir = editableBreakdown?.foir;
    const openedRoiFoirRef = useRef(null);
    useEffect(() => {
        // Reset the "opened with" baseline each time the dialog opens.
        if (eligibilityDialogVisible && editableBreakdown) {
            openedRoiFoirRef.current = { roi: Number(eligRoi), foir: Number(eligFoir) };
        } else {
            openedRoiFoirRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eligibilityDialogVisible]);

    useEffect(() => {
        if (!eligibilityDialogVisible || !editableBreakdown) return;
        const roi = Number(eligRoi);
        const foir = Number(eligFoir);
        // Skip the recalc unless the user actually changed roi/foir from what the
        // dialog opened with — this stops the wrong auto-recalc on open.
        const baseline = openedRoiFoirRef.current;
        if (!baseline || (roi === baseline.roi && foir === baseline.foir)) return;
        const bandBad = editableBreakdown.roi_is_range
            && (roi < Number(editableBreakdown.roi_min) || roi > Number(editableBreakdown.roi_max));
        if (!roi || roi <= 0 || roi > 100 || bandBad) return;
        if (!foir || foir <= 0 || foir > 100) return;
        const t = setTimeout(() => recalculateEligibility(), 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eligRoi, eligFoir, eligibilityDialogVisible]);

    // Live recalc when an ABB date-balance is edited. updateAbbBalance sets
    // _abb_edited and updates abb_summary.abb + the income row; this debounced
    // effect then re-runs the eligibility math so the final figure updates on its
    // own (user chose live auto-recalc). Watches abb_summary.abb so it fires once
    // per edit, not on open.
    // Fire the debounced recalc on ANY income edit — not just ABB. ABB edits
    // change abb_summary.abb, but GST (turnover/margin) and Net Profit (per-year
    // components / dep% / dip threshold) edits change the applicant income row
    // instead. Watching only abb_summary.abb meant NP/GST tables updated but the
    // sticky header + eligibility stayed stale. So the trigger is a fingerprint of
    // the main applicant's income-row adjusted values, which every method updates.
    const eligIncomeFingerprint = useMemo(() => {
        const rows = editableBreakdown?.applicants_breakdown?.[0]?.income_breakdown || [];
        return rows.map((r) => `${r.field}:${r.adjusted}:${r.value}:${r.multiplier}`).join('|');
    }, [editableBreakdown?.applicants_breakdown]);
    useEffect(() => {
        if (!eligibilityDialogVisible || !editableBreakdown?._abb_edited) return;
        const t = setTimeout(() => recalculateEligibility(), 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eligIncomeFingerprint, eligibilityDialogVisible]);

    // Update a specific income/obligation value in editable breakdown
    const updateBreakdownValue = (applicantIdx, type, itemIdx, newValue) => {
        setEditableBreakdown(prev => {
            if (!prev) return prev;
            const updated = JSON.parse(JSON.stringify(prev));
            const applicant = updated.applicants_breakdown[applicantIdx];
            if (!applicant) return prev;

            const breakdown = type === 'income' ? applicant.income_breakdown : applicant.obligation_breakdown;
            if (breakdown && breakdown[itemIdx] !== undefined) {
                breakdown[itemIdx].value = newValue || 0;
            }
            return updated;
        });
    };

    // Update a multiplier value (income or obligation)
    const updateMultiplier = (type, key, newPercent) => {
        setEditableBreakdown(prev => {
            if (!prev) return prev;
            const updated = JSON.parse(JSON.stringify(prev));
            const multipliers = type === 'income' ? updated.income_multipliers : updated.obligation_multipliers;
            if (multipliers && key in multipliers) {
                multipliers[key] = (newPercent || 0) / 100;
                // Also update the multiplier string in each applicant's breakdown
                (updated.applicants_breakdown || []).forEach(applicant => {
                    const breakdown = type === 'income' ? applicant.income_breakdown : applicant.obligation_breakdown;
                    (breakdown || []).forEach(item => {
                        if (item.field === key) {
                            item.multiplier = `${newPercent || 0}%`;
                        }
                    });
                });
            }
            return updated;
        });
    };

    // Reset to original values
    const resetBreakdown = () => {
        if (selectedEligibilityBreakdown) {
            setEditableBreakdown(JSON.parse(JSON.stringify(selectedEligibilityBreakdown)));
            setIsEdited(false);
        }
    };

    // Update a top-level numeric field on the editable breakdown (used for FOIR and ROI).
    // Marks the breakdown as edited so the UI shows "Modified" + enables Reset.
    const updateEligibilityField = (field, newValue) => {
        setEditableBreakdown(prev => {
            if (!prev) return prev;
            return { ...prev, [field]: newValue || 0 };
        });
        setIsEdited(true);
    };

    if (!offers || offers.length === 0) {
        return (
            <Message
                severity="info"
                text="No offers available. Please generate offers first."
                className="w-full"
            />
        );
    }

    // Filter offers based on selected filter (All/Old/New)
    let filteredOffers = offerFilter === 'all' ? offers
        : offerFilter === 'new' ? offers.filter(o => o.is_new === true)
        : offers.filter(o => !o.is_new);

    // Apply sidebar filters. The filter sidebar is shared by BOTH Cards and
    // Table views (both render from this same filteredOffers → bankGroups), so
    // the filters must apply in either view. Each filter is independent and
    // ANDed together; default state is permissive so empty filters return all.
    {
        // Quick EMI budget — keep offers where EMI <= user's max comfortable EMI.
        if (filters.quickEmiBudget && Number(filters.quickEmiBudget) > 0) {
            filteredOffers = filteredOffers.filter((o) => !o.emi || Number(o.emi) <= Number(filters.quickEmiBudget));
        }
        // Lenders — keep offers whose bank_name is in the selected set. null = all.
        if (filters.selectedLenders) {
            filteredOffers = filteredOffers.filter((o) => filters.selectedLenders.has(o.bank_name));
        }
        // Max Interest Rate
        if (filters.maxInterestRate < 20) {
            filteredOffers = filteredOffers.filter((o) => {
                const roi = Number(o.roi || o.interest_rate || 0);
                return !roi || roi <= filters.maxInterestRate;
            });
        }
        // CIBIL Score — single value the borrower has. Keep offers whose accepted-CIBIL
        // band [min_cibil..max_cibil] CONTAINS that value. When the offer doesn't declare
        // a band, treat it as accepting any score (no exclusion).
        if (filters.userCibil != null && Number(filters.userCibil) > 0) {
            const u = Number(filters.userCibil);
            filteredOffers = filteredOffers.filter((o) => {
                const min = o.min_cibil != null ? Number(o.min_cibil) : null;
                const max = o.max_cibil != null ? Number(o.max_cibil) : null;
                if (min != null && u < min) return false;
                if (max != null && u > max) return false;
                return true;
            });
        }
        // Listed / Unlisted toggle
        const allListTypesOn = filters.listType.listed && filters.listType.unlisted;
        if (!allListTypesOn) {
            filteredOffers = filteredOffers.filter((o) => {
                const lt = o.list_type || 'listed';
                return filters.listType[lt];
            });
        }
        // Verified / Not Verified toggle — same UX as List Type. Both checked
        // by default (permissive). Unchecking one hides that bucket. Both
        // unchecked → empty result (matches List Type behavior).
        const allVerifOn = filters.verification.verified && filters.verification.notVerified;
        if (!allVerifOn) {
            filteredOffers = filteredOffers.filter((o) => {
                return o.is_verified ? filters.verification.verified : filters.verification.notVerified;
            });
        }
        // Special features
        if (filters.features.noPreClosure) {
            filteredOffers = filteredOffers.filter((o) => {
                const c = String(o.pre_closure_charges || '').toLowerCase();
                return !c || c.includes('0%') || c.includes('nil') || c === '—' || c === '-';
            });
        }
        if (filters.features.zeroProcessingFee) {
            filteredOffers = filteredOffers.filter((o) => Number(o.processing_fee || 0) === 0);
        }
        if (filters.features.partPaymentAllowed) {
            filteredOffers = filteredOffers.filter((o) => o.part_payment_charges || o.ppr || o.part_payment_amount);
        }
        if (filters.features.bestRateOnly) {
            // Sort by ROI ascending and keep the lowest 3
            const sorted = [...filteredOffers].sort((a, b) => Number(a.roi || a.interest_rate || 999) - Number(b.roi || b.interest_rate || 999));
            const topIds = new Set(sorted.slice(0, 3).map((o) => o.offer_id || o.id));
            filteredOffers = filteredOffers.filter((o) => topIds.has(o.offer_id || o.id));
        }
    }

    // ── Verified-first sort ───────────────────────────────────────────────────
    // Verified offers float to the top of whatever sort the service produced.
    // Stable: among verified offers (and among non-verified offers) the
    // original order is preserved. Same effect as the parent service's
    // listed-first sort but keyed on `is_verified` instead of list_type.
    filteredOffers = [...filteredOffers].sort((a, b) => {
        const av = a.is_verified ? 1 : 0;
        const bv = b.is_verified ? 1 : 0;
        return bv - av;
    });

    // ── Flat offer list — each entry is one offer card ───────────────────────
    // Preserves the service's global sort order (listed-first → ROI ascending).
    // originalIndex carries the position in the parent `offers` array so the
    // "best offer" (#1) flag stays correct even after filter/lock reordering.
    const offerList = filteredOffers.map((offer, idx) => {
        // offer_id = the real backend id (kept for React keys, selection, lock,
        // expand — parent passes selectedOffer keyed on this).
        const offer_id = offer.offer_id || offer.id || `offer_${offers.indexOf(offer)}`;
        // _recalcKey = ALWAYS-unique per card (map index), used ONLY by the
        // band-rate recalc (slider). The backend can return DUPLICATE offers
        // sharing the same offer_id/id (e.g. two identical 17.99-18 rows) — recalc
        // keyed on the shared id made the slider only affect the first duplicate.
        // The map index is unique for every card, so each recalculates independently.
        const _recalcKey = `rk_${idx}`;
        return {
            offer: { ...offer, offer_id, _recalcKey },
            offer_id,
            _recalcKey, // also on the slot — used as the UNIQUE React key + expand key
            originalIndex: offers.indexOf(offer),
        };
    });

    // ── Bank grouping (Table = Prototype A accordion) ────────────────────────
    // Group the already-filtered/sorted offerList by lender. Each group keeps the
    // slots' original order (so #1 within a bank stays best), and groups are
    // ordered by the bank's BEST (lowest) ROI — mirroring the global "best first"
    // sort. Pinned banks float to the top regardless.
    const parseRoiNum = (o) => {
        const r = parseFloat(o?.roi ?? o?.interest_rate ?? o?.roi_min);
        return Number.isFinite(r) ? r : Infinity;
    };
    const bankGroups = (() => {
        const map = new Map();
        offerList.forEach((slot) => {
            const name = slot.offer.bank_name || 'Lender';
            if (!map.has(name)) map.set(name, []);
            map.get(name).push(slot);
        });
        const groups = Array.from(map.entries()).map(([bankName, slots]) => {
            const bestRoi = Math.min(...slots.map((s) => parseRoiNum(s.offer)));
            const containsGlobalBest = slots.some((s) => s.originalIndex === 0);
            // A bank is treated as a "band-ROI" bank when ANY of its offers has
            // roi_is_range. Band-ROI banks get pushed to the end of the list
            // because their final rate is uncertain — single-rate banks give
            // the borrower a definite number and deserve top placement.
            const isBandBank = slots.some((s) => s.offer?.roi_is_range === true);
            const sample = slots[0].offer;
            return { bankName, slots, bestRoi, containsGlobalBest, isBandBank, sample };
        });
        // Order:
        //   1. Pinned banks first (in pin order — user choice always wins)
        //   2. Single-ROI banks before band-ROI banks (definite rate beats uncertain)
        //   3. Within each ROI-kind bucket, sort by best ROI ascending
        groups.sort((a, b) => {
            const ap = lockedBankNames.indexOf(a.bankName);
            const bp = lockedBankNames.indexOf(b.bankName);
            const aPinned = ap !== -1;
            const bPinned = bp !== -1;
            if (aPinned && bPinned) return ap - bp;          // keep pin order
            if (aPinned) return -1;
            if (bPinned) return 1;
            // Single-rate banks rank above band-rate banks.
            if (a.isBandBank !== b.isBandBank) return a.isBandBank ? 1 : -1;
            return a.bestRoi - b.bestRoi;                    // best ROI first within bucket
        });
        return groups;
    })();

    // Default-open the best (first, non-pinned-sorted) bank once groups exist.
    const isBankOpen = (bankName) => {
        if (expandedBankNames === null) {
            // Not yet initialised — auto-open only the top bank by best ROI.
            const best = [...bankGroups].sort((a, b) => a.bestRoi - b.bestRoi)[0];
            return best ? bankName === best.bankName : false;
        }
        return expandedBankNames.includes(bankName);
    };
    const toggleBankOpen = (bankName) => {
        setExpandedBankNames((prev) => {
            // Initialise from the current open set (best bank) so the first click
            // doesn't lose the auto-opened state.
            const base = prev === null
                ? bankGroups.filter((g) => isBankOpen(g.bankName)).map((g) => g.bankName)
                : prev;
            return base.includes(bankName)
                ? base.filter((n) => n !== bankName)
                : [...base, bankName];
        });
    };

    // Bank-level pin (max 2). Mirrors handleToggleOfferLock semantics.
    const handleToggleBankLock = (bankName) => {
        setLockedBankNames((prev) => {
            if (prev.includes(bankName)) return prev.filter((n) => n !== bankName);
            if (prev.length >= MAX_LOCKED_BANKS) return prev; // ignore beyond max
            return [...prev, bankName];
        });
    };

    // ── Offer pagination ─────────────────────────────────────────────────────
    // Locked offers are ALWAYS visible (up to MAX_LOCKED_OFFERS = 2 slots).
    // Remaining slots are filled from the non-locked offers starting at
    // currentOfferStartIndex, so Prev/Next rotates only the non-locked ones.
    const getVisibleOffers = () => {
        if (offerList.length <= MAX_VISIBLE_OFFERS) {
            return offerList.map((o) => ({ ...o, isLocked: lockedOfferIds.includes(o.offer_id) }));
        }

        const visible = [];
        // 1. Locked offers first (in the order they were locked)
        lockedOfferIds.forEach((lockedId) => {
            const o = offerList.find((x) => x.offer_id === lockedId);
            if (o) visible.push({ ...o, isLocked: true });
        });

        const remainingSlots = MAX_VISIBLE_OFFERS - visible.length;
        if (remainingSlots > 0) {
            const unlocked = offerList.filter((o) => !lockedOfferIds.includes(o.offer_id));
            const start = Math.min(currentOfferStartIndex, Math.max(0, unlocked.length - remainingSlots));
            unlocked.slice(start, start + remainingSlots).forEach((o) => visible.push({ ...o, isLocked: false }));
        }

        return visible;
    };

    const getMaxOfferStartIndex = () => {
        const unlockedCount = offerList.length - lockedOfferIds.length;
        const slotsForPagination = MAX_VISIBLE_OFFERS - lockedOfferIds.length;
        return Math.max(0, unlockedCount - slotsForPagination);
    };

    const handlePrevOffer = () => setCurrentOfferStartIndex((prev) => Math.max(0, prev - 1));
    const handleNextOffer = () => setCurrentOfferStartIndex((prev) => Math.min(getMaxOfferStartIndex(), prev + 1));

    // ── Offer lock toggle — up to MAX_LOCKED_OFFERS (2) pinned at a time ─────
    const handleToggleOfferLock = (offerId) => {
        if (lockedOfferIds.includes(offerId)) {
            setLockedOfferIds((prev) => prev.filter((id) => id !== offerId));
        } else if (lockedOfferIds.length < MAX_LOCKED_OFFERS) {
            setLockedOfferIds((prev) => [...prev, offerId]);
            setCurrentOfferStartIndex(0);
        }
    };

    // ── Mobile: 1 offer at a time ────────────────────────────────────────────
    const handleMobilePrevOffer = () => setMobileOfferIndex((prev) => Math.max(0, prev - 1));
    const handleMobileNextOffer = () => setMobileOfferIndex((prev) => Math.min(offerList.length - 1, prev + 1));

    const canNavigatePrevious = isMobile ? mobileOfferIndex > 0 : currentOfferStartIndex > 0;
    const canNavigateNext = isMobile ? mobileOfferIndex < offerList.length - 1 : currentOfferStartIndex < getMaxOfferStartIndex();
    const showNavigation = isMobile ? offerList.length > 1 : offerList.length > MAX_VISIBLE_OFFERS;

    // Offers to render on screen right now (1 on mobile, up to 4 on desktop)
    const displayOffers = (() => {
        if (isMobile) {
            const idx = Math.min(mobileOfferIndex, offerList.length - 1);
            const o = offerList[idx];
            return o ? [{ ...o, isLocked: lockedOfferIds.includes(o.offer_id) }] : [];
        }
        return getVisibleOffers();
    })();

    // Format currency
    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') return 'N/A';
        const num = parseFloat(value);
        if (isNaN(num)) return 'N/A';
        return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    // EMI/MO display: when a loan amount + tenure are entered the offer carries a
    // real EMI → show it (₹/mo). Otherwise fall back to the per-lakh EMI
    // (₹X / lakh) computed at the offer's ROI + standard tenure, so the user
    // still sees a meaningful instalment before entering an amount. Eligibility
    // is independent and shown separately.
    //
    // For BAND-ROI offers (roi_is_range), the per-lakh value is shown as a
    // RANGE: "₹X – ₹Y / lakh" where X = EMI at the offer's calc ROI (which is
    // the band MIN initially, but moves up as the user drags the rate slider
    // on the card) and Y = EMI at the band MAX (fixed — worst case for the
    // borrower). This mirrors how the ROI is shown ("15.99% – 17%") so the
    // EMI per lakh communicates the same uncertainty.
    const formatEmiDisplay = (offer) => {
        const emi = parseFloat(offer?.emi);
        const loanAmount = parseFloat(offer?.loan_amount);
        const tenureForCalc = parseInt(offer?.tenure || offer?.tenure_months || offer?.min_tenure || 0);
        const isBand = !!offer?.roi_is_range;
        const roiMin = parseFloat(offer?.roi_min);
        const roiMax = parseFloat(offer?.roi_max);
        const roiCalc = parseFloat(offer?.roi);

        // Only treat `emi` as a REAL monthly instalment when the borrower actually
        // ENTERED a loan amount. In discovery mode the engine still assigns a
        // loan_amount (derived from eligibility) and computes an emi on it, but
        // showing that as "EMI / mo" implies a commitment the borrower never made.
        // loan_amount_provided is the authoritative flag; when it's false we fall
        // through to the EMI-per-lakh display below (matches the Personal Loan card).
        const amountEntered = offer?.loan_amount_provided === true
            || (offer?.loan_amount_provided == null && loanAmount > 0 && !isNaN(emi));

        // FULL MODE — borrower entered a loan amount AND tenure, so `emi` is real.
        // For band-ROI offers, show the EMI as a RANGE computed on the actual
        // loan amount at both the calc rate (lower bound) and the band MAX (upper
        // bound). Stays consistent with how ROI ("15.99% – 17%") and EMI/lakh
        // ("₹X – ₹Y / lakh") are shown on the same card.
        if (amountEntered && !isNaN(emi) && emi > 0) {
            if (isBand
                && Number.isFinite(roiMax)
                && Number.isFinite(roiCalc)
                && roiMax > roiCalc
                && loanAmount > 0
                && tenureForCalc > 0) {
                const emiAtCalc = calculateEmi(loanAmount, roiCalc, tenureForCalc);
                const emiAtMax = calculateEmi(loanAmount, roiMax, tenureForCalc);
                const lowFmt = formatCurrency(emiAtCalc);
                const highFmt = formatCurrency(emiAtMax);
                if (lowFmt === highFmt) {
                    // Tight band — both ends round to same rupee. ≈ prefix is honest:
                    // the calc IS spanning the band, the visible effect is sub-rupee.
                    return `≈ ${lowFmt}`;
                }
                return `${lowFmt} – ${highFmt}`;
            }
            return formatCurrency(emi);
        }

        // DISCOVERY MODE — no loan amount entered. Show EMI per lakh instead.
        const perLac = parseFloat(offer?.emi_per_lac);
        if (isNaN(perLac) || perLac <= 0) return 'N/A';

        // Band-ROI? Show the EMI/lakh as a RANGE too. The lower bound is computed
        // at the offer's current `roi` (band MIN initially, moves up as the user
        // drags the rate slider). The upper bound is fixed at the band MAX —
        // worst case for the borrower.
        //
        // We compute BOTH sides explicitly (not reusing offer.emi_per_lac for
        // the lower bound) so:
        //   1. The range is symmetric — both ends use the same calculateEmi()
        //      with the same tenure → no off-by-rounding between sides.
        //   2. We can render a "≈ ₹X / lakh" hint when the band is so narrow
        //      the two ends round to the same integer (e.g. 17.99%-18% at a
        //      short tenure produces sub-rupee differences). This is honest:
        //      the slider IS recalculating, but the visible effect is below
        //      rupee precision at this loan size.
        if (isBand
            && Number.isFinite(roiMax)
            && Number.isFinite(roiCalc)
            && Number.isFinite(roiMin)
            && tenureForCalc > 0) {
            // Lower bound = at borrower's CURRENT chosen rate (slider position)
            const perLacAtCalc = calculateEmi(100000, roiCalc, tenureForCalc);
            // Upper bound = at band's worst-case rate (anchored, never moves)
            const perLacAtMax = calculateEmi(100000, roiMax, tenureForCalc);
            const lowFmt = formatCurrency(perLacAtCalc);
            const highFmt = formatCurrency(perLacAtMax);
            if (lowFmt === highFmt) {
                return `≈ ${lowFmt} / lakh`;
            }
            return `${lowFmt} – ${highFmt} / lakh`;
        }
        return `${formatCurrency(perLac)} / lakh`;
    };

    // Interest p.a. display. Band-ROI offers (e.g. "15.99-17") arrive with
    // roi_is_range + roi_display (the full band) + roi (the calc rate = band MIN
    // that EMI/cost are computed at). Show the band so the user sees the real
    // spread, and a small "calc @ X%" note so the figures are transparent.
    // Single-value offers just show "10.5%".
    // Slider step adaptive to band width. A tiny band like 17.99–18 (width 0.01)
    // with a fixed 0.01 step gives ONE step → the thumb can't move. Aim for ~100
    // increments across whatever the band is, clamped to a sensible range.
    const bandStep = (min, max) => {
        const width = Math.max(0, Number(max) - Number(min));
        if (width <= 0) return 0.01;
        const raw = width / 100;
        // round to a clean 0.001 / 0.005 / 0.01 etc., min 0.001, max 0.05
        return Math.min(0.05, Math.max(0.001, Math.round(raw * 1000) / 1000));
    };

    const roiText = (offer) => {
        if (offer?.roi_is_range && offer?.roi_display) return offer.roi_display;
        const single = offer?.roi ?? offer?.interest_rate;
        return (single || single === 0) ? `${single}%` : '—';
    };
    const roiCalcNote = (offer) => {
        if (!offer?.roi_is_range) return null;
        const calc = offer?.roi ?? offer?.roi_min;
        return (calc || calc === 0) ? `calc @ ${calc}%` : null;
    };

    // Front-card "Loan Amount" cell: when the borrower entered an amount the
    // offer carries it (offer.loan_amount) → show that. The lender's allowed
    // min–max RANGE moves into View details. When no amount is entered, the
    // front falls back to showing the allowed range so the card isn't empty.
    // Loan band rendering helpers.
    //
    // Lenders phrase their loan-amount bands in policy documents using
    // comparison operators ('>=1,00,000 to <=10,00,000'). The backend now ships
    // both the stripped numeric values (min_loan_amount, max_loan_amount — used
    // for math) and the raw CSV strings WITH operators (min_loan_amount_raw,
    // max_loan_amount_raw — used here for display). We prefer the raw form so
    // the on-screen card matches the lender's own wording.
    //
    // parseBand(rawStr) → { op, value } | null
    //   "≥100000", ">=100000", "> 100000", "100000+" → { op: '>=', value: 100000 }
    //   "<=500000", "<500000", "≤500000"             → { op: '<=', value: 500000 }
    //   "100000" (bare number)                       → { op: '=',  value: 100000 }
    // Unknown / empty → null. The caller decides the default operator to apply
    // when the raw value didn't carry one (we default minloan→'>=' and
    // maxloan→'<=' because that's the universal CSV convention).
    const parseBand = (raw) => {
        if (raw === null || raw === undefined || raw === '') return null;
        const s = String(raw).trim();
        const m = s.match(/^\s*(>=|<=|>|<|≥|≤|=)?\s*([0-9.,]+)\s*$/);
        if (!m) return null;
        const num = parseFloat(String(m[2]).replace(/,/g, ''));
        if (!Number.isFinite(num)) return null;
        const opRaw = m[1] || '';
        const op = opRaw === '≥' ? '>=' : opRaw === '≤' ? '<=' : opRaw;
        return { op, value: num };
    };
    // formatBand(rawStr, fallbackOp, fallbackNum) → "≥ ₹X,XX,XXX" / "≤ ₹Y,YY,YYY"
    // Falls back to the stripped numeric value when the raw string is missing
    // (older offer payloads, regen flows) so existing data still renders cleanly.
    const formatBand = (rawStr, fallbackOp, fallbackNum) => {
        const parsed = parseBand(rawStr);
        let op = fallbackOp;
        let value = fallbackNum;
        if (parsed) {
            value = parsed.value;
            if (parsed.op) op = parsed.op;
        }
        if (!Number.isFinite(value) || value <= 0) return null;
        const num = formatCurrency(value);
        if (op === '>=' || op === '<=') return `${op} ${num}`;
        if (op === '>' || op === '<') return `${op} ${num}`;
        return num; // bare number — no operator
    };
    const loanRangeText = (offer) => {
        const lo = formatBand(offer?.min_loan_amount_raw, '>=', Number(offer?.min_loan_amount));
        const hi = formatBand(offer?.max_loan_amount_raw, '<=', Number(offer?.max_loan_amount));
        if (!lo && !hi) return '—';
        return `${lo || 'Any'} to ${hi || 'Any'}`;
    };

    // Front-card "Loan Amount" cell — ONE value, never both:
    //   borrower entered an amount  → that amount
    //   no amount entered           → the lender's slab band, as written in the CSV
    //
    // `loan_amount_provided` is authoritative and must be preferred over any
    // heuristic on loan_amount: the Business Loan engine DERIVES an amount for
    // every slab when none was requested, so a non-zero loan_amount does not
    // mean the borrower typed one. Older payloads without the flag fall back to
    // the heuristic.
    // '1' → '1st', '22' → '22nd'. Mirrors AverageBankingBalanceMethodService::ordinalDay
    // so a rate-card-derived caption reads the same as a backend-derived one.
    const ordinalDay = (d) => {
        const n = parseInt(d, 10);
        if (!Number.isFinite(n)) return String(d);
        if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
        return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
    };

    // Caption under the ABB figure: which days THIS lender sampled.
    //
    // Resolution order, so a missing abb_summary can never render "on undefined":
    //   1. manual entry           → say so; the lender's dates were not applied
    //   2. abb_summary.dates_display ('1st, 5th, 15th, 25th' / 'Every calendar day')
    //   3. abb_dates_raw straight off the rate card ('1,5,15,25') — always present
    //      on an ABB offer even when no eligibility run produced a summary
    //   4. blank rate-card dates  → the all-calendar-days fallback
    const abbDatesCaption = (offer) => {
        if (offer?.abb_source === 'manual_entry') return 'entered manually';
        if (offer?.abb_summary?.mode === 'all_calendar_days') return 'all days';
        const display = offer?.abb_summary?.dates_display;
        if (display) return `on ${display}`;
        const raw = String(offer?.abb_dates_raw ?? '').trim();
        if (raw !== '' && raw !== '-') {
            const days = raw.split(',').map((d) => d.trim()).filter(Boolean);
            if (days.length) return `on ${days.map(ordinalDay).join(', ')}`;
        }
        return 'all days';
    };

    const loanAmountCellText = (offer) => {
        const entered = offer?.loan_amount_provided != null
            ? offer.loan_amount_provided === true
            : (offer?.loan_amount != null && Number(offer.loan_amount) > 0);
        if (entered && Number(offer?.loan_amount) > 0) {
            return formatCurrency(offer.loan_amount);
        }
        return loanRangeText(offer);
    };

    // Income-range band for the offer's calculation_method. Home Loan offers can
    // qualify via one of four income methods (SALARY / ABB / NET_PROFIT /
    // GST_TURNOVER); each method has its OWN range columns. This shows the band
    // the borrower's ENTERED method maps to, so a net-profit borrower sees the
    // net-profit range (not the salary range) in the offer details. Returns
    // { label, text } or null when the range isn't set for this offer's method.
    // Personal Loan offers (method 'full'/'discovery'/absent) fall back to salary.
    const incomeRangeInfo = (offer) => {
        const method = String(offer?.calculation_method || '').toUpperCase();
        const map = {
            ABB:          { label: 'Avg Banking Balance Range (lender)', min: 'min_abb',         max: 'max_abb',         minRaw: 'min_abb_raw',         maxRaw: 'max_abb_raw' },
            NET_PROFIT:   { label: 'Net Profit Range (lender)',          min: 'min_net_profit',  max: 'max_net_profit',  minRaw: 'min_net_profit_raw',  maxRaw: 'max_net_profit_raw' },
            GST_TURNOVER: { label: 'GST Turnover Range (lender)',        min: 'min_gst_turnover',max: 'max_gst_turnover',minRaw: 'min_gst_turnover_raw',maxRaw: 'max_gst_turnover_raw' },
        };
        // SALARY / PL default → salary range.
        const cfg = map[method] || { label: 'Salary Range (lender)', min: 'min_salary', max: 'max_salary', minRaw: 'min_salary_raw', maxRaw: 'max_salary_raw' };
        const hasAny = offer?.[cfg.min] || offer?.[cfg.max] || offer?.[cfg.minRaw] || offer?.[cfg.maxRaw];
        if (!hasAny) return null;
        const lo = formatBand(offer?.[cfg.minRaw], '>=', Number(offer?.[cfg.min]));
        const hi = formatBand(offer?.[cfg.maxRaw], '<=', Number(offer?.[cfg.max]));
        if (!lo && !hi) return null;
        return { label: cfg.label, text: `${lo || 'Any'} to ${hi || 'Any'}` };
    };

    // Lender's accepted CIBIL band, e.g. ">=700 to <=900". Used on the front when
    // the borrower hasn't entered their score, and always in View details.
    const cibilRangeText = (offer) => (offer?.min_cibil || offer?.max_cibil)
        ? `${offer.min_cibil || '-'} to ${offer.max_cibil || '-'}`
        : '—';
    // Borrower's entered CIBIL score. Priority: the wizard's "Cibil Score" field
    // (passed via eligibilityParams.cibil_score) → the filter sidebar's
    // "YOUR CIBIL SCORE" input. When present, the card front shows this score
    // instead of the lender's accepted range.
    const enteredCibil = (() => {
        const fromWizard = eligibilityParams?.cibil_score;
        if (fromWizard != null && Number(fromWizard) > 0) return Number(fromWizard);
        if (filters?.userCibil != null && Number(filters.userCibil) > 0) return Number(filters.userCibil);
        return null;
    })();

    // Compact Indian-style INR — produces "50K", "1L", "10L", "1.5Cr".
    // Used for range pills (e.g. "Loan Amount Range: 50K - 10L") so the card
    // stays scannable. Falls back to full formatCurrency() for awkward sub-1K
    // values where compaction would be misleading.
    const formatINRCompact = (value) => {
        if (value === null || value === undefined || value === '') return 'N/A';
        const num = parseFloat(value);
        if (isNaN(num) || num === 0) return 'N/A';
        const CR = 10000000;
        const L  = 100000;
        const K  = 1000;
        if (num >= CR) {
            const v = num / CR;
            return (Number.isInteger(v) ? v : Number(v.toFixed(1))) + 'Cr';
        }
        if (num >= L) {
            const v = num / L;
            return (Number.isInteger(v) ? v : Number(v.toFixed(1))) + 'L';
        }
        if (num >= K) {
            const v = num / K;
            return (Number.isInteger(v) ? v : Number(v.toFixed(1))) + 'K';
        }
        return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    };

    // Format loan amount — show range if discovery mode
    const formatLoanAmount = (offer) => {
        if (offer.loan_amount) return formatCurrency(offer.loan_amount);
        if (offer.min_loan_amount || offer.max_loan_amount) {
            const min = offer.min_loan_amount ? formatCurrency(offer.min_loan_amount) : 'Any';
            const max = offer.max_loan_amount ? formatCurrency(offer.max_loan_amount) : 'Any';
            return `${min} - ${max}`;
        }
        return 'N/A';
    };

    // Format tenure — show range if discovery mode
    const formatTenure = (offer) => {
        if (offer.tenure || offer.tenure_months) {
            const months = offer.tenure || offer.tenure_months;
            let display = `${months} months`;
            if (offer.tenure_capped_by_age && offer.original_tenure_requested && offer.original_tenure_requested > months) {
                display += ` (capped from ${offer.original_tenure_requested}mo, max age ${offer.max_age_allowed})`;
            }
            return display;
        }
        if (offer.min_tenure || offer.max_tenure) {
            const min = offer.min_tenure || '?';
            const max = offer.max_tenure || '?';
            return `${min} - ${max} months`;
        }
        return 'N/A';
    };

    // Format processing fee display
    const formatPF = (pfRaw) => {
        if (!pfRaw) return 'N/A';
        const pf = String(pfRaw).toLowerCase().trim();
        if (pf.startsWith('p')) return pf.substring(1) + '%';
        if (pf.startsWith('r')) return '₹' + Number(pf.substring(1)).toLocaleString('en-IN');
        if (pf.includes('%')) return pf;
        if (!isNaN(pf)) return Number(pf) < 100 ? pf + '%' : '₹' + Number(pf).toLocaleString('en-IN');
        return pfRaw;
    };

    const GST_RATE = 18; // GST % on processing fee (matches backend)

    // Processing-fee display with GST applied. CSV fees EXCLUDE GST.
    //  • Fixed amount (₹3,499)            → ₹3,499 × 1.18 = ₹4,129 (incl. GST)
    //  • Percentage + loan amount entered → backend processing_fee (already the
    //    rupee fee incl. GST, computed from the amount) e.g. ₹37,760
    //  • Percentage, NO loan amount yet   → "2%" + a muted "excl. GST" note
    //    (can't compute the rupee fee until an amount is entered)
    // Returns { text, note } — note is null when nothing extra to say.
    const formatProcessingFee = (offer) => {
        const display = offer?.processing_fee_display ?? (offer?.processing_fee != null ? formatPF(offer.processing_fee) : null);
        if (display == null || display === '') return { text: 'N/A', note: null };
        const isPercent = String(display).includes('%');
        // "Has amount" means the borrower ACTUALLY ENTERED one — not an amount the
        // engine auto-derived from eligibility (discovery mode). For a percentage
        // PF we only convert to a rupee figure when a real amount was entered;
        // otherwise we show the raw rate ("2%"), matching the Personal Loan card.
        const hasAmount = offer?.loan_amount_provided === true
            || (offer?.loan_amount_provided == null && offer?.loan_amount != null && Number(offer.loan_amount) > 0);

        // Percentage case
        if (isPercent) {
            if (hasAmount && offer?.processing_fee != null && Number(offer.processing_fee) > 0) {
                // backend processing_fee is the rupee fee WITH GST already added.
                // Keep the lender's quoted RATE in the note — the rupee figure
                // alone hides what was actually agreed, and the rate is what a
                // DSA repeats to the customer.
                return { text: formatCurrency(offer.processing_fee), note: `${display} · incl. GST` };
            }
            // No amount → can't compute rupee; show the % and flag GST excluded
            return { text: display, note: 'excl. GST' };
        }

        // Fixed-amount case (e.g. 'r7600') → the CSV amount is ALREADY GST-inclusive,
        // so show it AS-IS (no extra 18%). Prefer the backend rupee value (now
        // returned GST-inclusive, un-grossed); else parse the display number.
        if (offer?.processing_fee != null && Number(offer.processing_fee) > 0) {
            return { text: formatCurrency(offer.processing_fee), note: 'incl. GST' };
        }
        const rawNum = parseFloat(String(display).replace(/[^0-9.]/g, ''));
        if (!isNaN(rawNum) && rawNum > 0) {
            return { text: formatCurrency(Math.round(rawNum)), note: 'incl. GST' };
        }
        return { text: display, note: null };
    };

    // Format slab-based charges into readable inline string
    // "1-24-p4,24-36-p3,36-60-p2" → "4% for 1-24 months, 3% for 24-36 months, 2% for 36-60 months"
    // Already formatted strings pass through as-is
    const formatChargeSlabs = (raw) => {
        if (!raw) return null;
        const str = String(raw).trim();

        // Try raw coded format: "1-24-p4,24-36-p3,36-60-p2"
        const codedSlabs = str.split(',').map(s => s.trim()).filter(Boolean);
        const parts = [];
        let allCoded = true;
        for (const slab of codedSlabs) {
            const segments = slab.split('-');
            if (segments.length === 3) {
                const [from, to, charge] = segments;
                const c = charge.toLowerCase();
                let chargeText;
                if (c.startsWith('p')) chargeText = c.substring(1) + '%';
                else if (c.startsWith('r')) chargeText = '₹' + Number(c.substring(1)).toLocaleString('en-IN');
                else if (!isNaN(c)) chargeText = Number(c) < 100 ? c + '%' : '₹' + Number(c).toLocaleString('en-IN');
                else chargeText = charge;
                parts.push(`${chargeText} for ${from}-${to} months`);
            } else {
                allCoded = false;
                break;
            }
        }
        if (allCoded && parts.length > 0) return parts.join(', ');

        // Single token (no month-slab segments), e.g. "p0", "p2.5", "r500", "0",
        // optionally with a comparison operator: ">p25", ">=p10", "<p5".
        // The CSV stores charges with a type prefix ('p'=percent, 'r'=rupees);
        // strip it for display, preserving any operator. A zero charge shows as a
        // plain "0" (user request 2026-07-04) rather than "p0" / "0%".
        let single = str.toLowerCase();
        let opPrefix = '';
        const opMatch = single.match(/^(>=|<=|>|<)/);
        if (opMatch) { opPrefix = opMatch[1]; single = single.slice(opPrefix.length).trim(); }
        const stripped = single.startsWith('p') || single.startsWith('r') ? single.substring(1) : single;
        if (stripped !== '' && !isNaN(stripped)) {
            const num = Number(stripped);
            if (num === 0 && opPrefix === '') return '0';
            if (single.startsWith('r')) return opPrefix + '₹' + num.toLocaleString('en-IN');
            // 'p'-prefixed or bare number < 100 → percentage; >=100 → rupees.
            if (single.startsWith('p') || num < 100) return opPrefix + num + '%';
            return opPrefix + '₹' + num.toLocaleString('en-IN');
        }

        // Already formatted or non-numeric — return as-is
        return str;
    };

    // Base rate as uploaded on the lender's range. Two shapes reach us:
    //   • composite  "5.25+2.1"  — repo rate + margin, from the rate sheet's
    //     "reporate+margin" column (Home Loan / Car Loan ranges)
    //   • plain      "12"        — the product-level base_rate the Personal /
    //     Business engines fall back to when the range has none
    // Only the plain form gets a "%" appended: "5.25+2.1%" would read as if the
    // unit belonged to the margin alone. The composite is spaced out so the two
    // components are legible at a glance.
    const formatBaseRate = (raw) => {
        if (raw === null || raw === undefined || raw === '') return null;
        const str = String(raw).trim();
        if (str === '') return null;
        if (/^\d+(\.\d+)?$/.test(str)) return `${str}%`;
        return str.replace(/\s*\+\s*/g, ' + ');
    };

    // View details handler
    const handleViewDetails = (offer) => {
        setSelectedOfferDetails(offer);
        setDetailsDialogVisible(true);
    };

    // View eligibility breakdown handler
    const handleViewEligibilityBreakdown = (offer) => {
        let breakdownData = offer.eligibility_breakdown;

        // Build breakdown from offer + eligibilityParams when backend didn't provide it
        // (e.g. older saved offers with no eligibility_breakdown). This is METHOD-AWARE:
        // it shows ONLY the income field the offer's calculation method actually uses —
        // ABB → "Average Banking Balance", NET_PROFIT → "Net Profit", GST_TURNOVER →
        // "GST Turnover", otherwise SALARY → "Net Salary". It never fabricates a salary
        // row for a business-loan (ABB/NP/GST) offer.
        if (!breakdownData && eligibilityParams) {
            const method = String(offer.calculation_method || eligibilityParams.calculation_method || 'SALARY').toUpperCase();
            const rentalIncome = parseFloat(eligibilityParams.rental_income || 0);
            const bonusIncome = parseFloat(eligibilityParams.bonus_income || 0);
            const otherIncome = parseFloat(eligibilityParams.other_income || 0);
            const existingEmi = parseFloat(eligibilityParams.existing_emi || eligibilityParams.total_existing_loan_emi || 0);
            const creditCard = parseFloat(eligibilityParams.credit_card_outstanding || 0);
            const goldLoan = parseFloat(eligibilityParams.gold_loan || 0);
            const overdraft = parseFloat(eligibilityParams.overdraft_outstanding || 0);

            const om = {
                existing_emi: 1, credit_card_outstanding: 0.05, gold_loan: 0.01,
                overdraft_outstanding: 0.05, other_obligations: 1,
                ...(offer.obligation_multipliers || {}),
            };
            const pct = (m) => `${Math.round((Number(m) || 0) * 100)}%`;

            // The PRIMARY income row depends on the method. For business methods we read
            // the value the offer already computed (abb_value_used / income_value), so the
            // modal ties out with the card even without a backend breakdown.
            let primaryRow;
            if (method === 'ABB') {
                const abb = parseFloat(offer.abb_value_used || offer.income_value || eligibilityParams.abb || 0);
                primaryRow = { field: 'average_bank_balance', label: 'Average Banking Balance', value: abb, multiplier: '100%', adjusted: abb };
            } else if (method === 'NET_PROFIT' || method === 'GROSS_PROFIT') {
                // Net Profit is entered YEARLY; the eligibility income is monthly
                // (÷12). The flat income_value is the yearly figure, so divide by 12
                // here — otherwise the modal used the raw yearly value as monthly
                // income, which (with FOIR reverse-derived) produced the phantom 5%
                // / inflated eligibility. When multi-year data exists the section
                // below overrides this with the averaged cash profit ÷ 12.
                const npYearly = parseFloat(offer.income_value || eligibilityParams.net_profit || 0);
                const npMonthly = Math.round(npYearly / 12);
                primaryRow = { field: 'net_profit', label: 'Net Profit (monthly = yearly ÷ 12)', value: npMonthly, multiplier: '100%', adjusted: npMonthly };
            } else if (method === 'GST_TURNOVER') {
                // GST income = turnover × MARGIN (default 8%, or nature-based), NOT
                // 100%. Using 100% here inflated income to the raw turnover (e.g.
                // ₹55.6L) → FOIR on that → ~₹23cr eligibility. Read the margin from
                // the offer's gst_summary (fraction, e.g. 0.08) so the modal ties
                // out with the card and the backend.
                const gst = parseFloat(offer.income_value || eligibilityParams.gst_turnover || 0);
                const gstMargin = Number(offer.gst_summary?.margin) > 0 ? Number(offer.gst_summary.margin) : 0.08;
                const marginPctLabel = `${Math.round(gstMargin * 10000) / 100}%`;
                primaryRow = { field: 'gst_turnover', label: 'GST Turnover', value: gst, multiplier: marginPctLabel, adjusted: Math.round(gst * gstMargin) };
            } else {
                const salary = parseFloat(eligibilityParams.salary || eligibilityParams.net_salary_per_month || 0);
                primaryRow = { field: 'net_salary', label: 'Net Salary', value: salary, multiplier: '100%', adjusted: salary };
            }

            // Rental only added for methods that consider it (all business methods + salary).
            const rentalMult = method === 'SALARY' ? 0.65 : 0.50;
            const incomeRows = [primaryRow];
            if (rentalIncome > 0) incomeRows.push({ field: 'rental_income', label: 'Rental Income', value: rentalIncome, multiplier: pct(rentalMult), adjusted: rentalIncome * rentalMult });
            if (bonusIncome > 0)  incomeRows.push({ field: 'bonus_income', label: 'Bonus Income', value: bonusIncome, multiplier: '50%', adjusted: bonusIncome * 0.5 });
            if (otherIncome > 0)  incomeRows.push({ field: 'other_income', label: 'Other Income', value: otherIncome, multiplier: '100%', adjusted: otherIncome });

            const totalIncome = incomeRows.reduce((s, r) => s + (Number(r.adjusted) || 0), 0);

            const obligationRows = [];
            if (existingEmi > 0) obligationRows.push({ field: 'existing_emi', value: existingEmi, multiplier: pct(om.existing_emi), adjusted: existingEmi * om.existing_emi });
            if (creditCard > 0)  obligationRows.push({ field: 'credit_card_outstanding', value: creditCard, multiplier: pct(om.credit_card_outstanding), adjusted: creditCard * om.credit_card_outstanding });
            if (goldLoan > 0)    obligationRows.push({ field: 'gold_loan', value: goldLoan, multiplier: pct(om.gold_loan), adjusted: goldLoan * om.gold_loan });
            if (overdraft > 0)   obligationRows.push({ field: 'overdraft_outstanding', value: overdraft, multiplier: pct(om.overdraft_outstanding), adjusted: overdraft * om.overdraft_outstanding });
            const totalObligations = obligationRows.reduce((s, r) => s + (Number(r.adjusted) || 0), 0);

            // FOIR — the lender's UPLOADED value wins. It arrives on the offer as
            // foir_min / foir (parsed from the rate card, which writes 'p65' / '65%' /
            // '60-75'), so use that first. Only when the card carries no FOIR at all do
            // we reverse-derive it from foir_available ÷ total income, and only if THAT
            // is unavailable do we fall back to a nominal 65% — previously the 65 was
            // reached whenever foir_available was null, which is every self-employed
            // (ABB / Net Profit / GST) lead, so an uploaded FOIR of 100 displayed as 65.
            // Prefer the explicit FOIR PERCENTAGE the engine now sends
            // (foir_percentage, parsed from the range's 'p65'). foir_min/foir are
            // legacy aliases. Only if NONE of those exist do we reverse-derive —
            // and that derivation must divide foir_available (income-after-FOIR, a
            // rupee amount) by the same income base the FOIR was applied to. For
            // GST the income is turnover×margin, NOT the raw turnover, so dividing
            // by totalIncome (=turnover here) gave a bogus 5%. Guard it: only
            // derive when foir_available < totalIncome AND yields a sane %, else 65.
            const cardFoir = Number(offer.foir_percentage ?? offer.foir_min ?? offer.foir ?? 0);
            let foir = 0;
            if (cardFoir > 0 && cardFoir <= 100) {
                foir = cardFoir;
            }
            if (!foir || foir <= 0 || foir > 100) foir = 65;

            const incomeAfterFoir = totalIncome * (foir / 100);
            const availableForEmi = Math.max(0, incomeAfterFoir - totalObligations);

            breakdownData = {
                // Derive when the card carries no figure (discovery cards, older saved
                // offers). Every input is already on hand, so showing a blank Eligible
                // Amount here is a presentation gap, not missing data. Same formula the
                // backend uses: available-for-EMI ÷ EMI-per-lac × 1,00,000.
                eligible_amount: Number(offer.max_loan_eligibility) > 0
                    ? offer.max_loan_eligibility
                    : (Number(offer.emi_per_lac) > 0
                        ? Math.round((availableForEmi / Number(offer.emi_per_lac)) * 100000)
                        : 0),
                foir,
                roi: offer.roi,
                emi_per_lac: offer.emi_per_lac,
                total_income: totalIncome,
                income_after_foir: incomeAfterFoir,
                total_obligations: totalObligations,
                available_for_emi: availableForEmi,
                eligibility_method: method,
                netsalarymultiple: 0,
                eligibility_as_per_multiplier: 0,
                obligation_multipliers: om,
                applicants_breakdown: [{
                    name: 'Main Applicant',
                    type: 'main',
                    income: totalIncome,
                    obligations: totalObligations,
                    income_breakdown: incomeRows,
                    obligation_breakdown: obligationRows,
                }],
            };
        }

        if (!breakdownData) return;

        const breakdown = {
            ...breakdownData,
            bank_name: offer.bank_name,
            offer_id: offer._recalcKey ?? offer.offer_id ?? offer.id, // unique recalc key — patches THIS card even for duplicate offers
            roi: offer.roi,
            loan_amount: offer.loan_amount,
            // ROI band fields — needed so the "Adjust Rate & FOIR" editor can clamp the
            // ROI input to [roi_min, roi_max] and show an inline error for out-of-band
            // values. Without these the band validation silently passes any ROI.
            roi_is_range: offer.roi_is_range,
            roi_min: offer.roi_min,
            roi_max: offer.roi_max,
            roi_display: offer.roi_display,
            roi_calc_note: offer.roi_calc_note,
            // Tenure — seed with the EFFECTIVE tenure (entered tenure → range's
            // MIN tenure fallback). This matches the backend's discovery-mode behavior
            // (PersonalLoanOfferCalculationService.php line ~1466) and gives the user
            // an editable, non-zero starting value so recalculateEligibility can
            // re-run PMT when they change ROI or tenure.
            tenure: (Number(offer.tenure || offer.tenure_months) > 0)
                ? Number(offer.tenure || offer.tenure_months)
                : (Number(offer.min_tenure) > 0 ? Number(offer.min_tenure) : null),
            tenure_months: (Number(offer.tenure || offer.tenure_months) > 0)
                ? Number(offer.tenure || offer.tenure_months)
                : (Number(offer.min_tenure) > 0 ? Number(offer.min_tenure) : null),
            // Whether the borrower actually entered a tenure on the lead — used by the
            // dialog to badge "minimum" on the auto-filled tenure so the user knows
            // they're seeing the conservative default. The tenure_input_filled flag
            // is true when offer.tenure was provided by the borrower (not a fallback).
            tenure_input_filled: Number(offer.tenure || offer.tenure_months) > 0,
            // Range tenure boundaries — used to validate the editable tenure input
            // (must stay within [min_tenure, max_tenure]) and to display the allowed
            // band in the input's helper label.
            min_tenure: offer.min_tenure,
            max_tenure: offer.max_tenure,
            // ABB context — so the modal can render the full per-lender ABB
            // calculation (month-wise dates→balances table). Only used when
            // calculation_method === 'ABB'; harmless (undefined) otherwise.
            calculation_method: offer.calculation_method,
            abb_summary: offer.abb_summary,
            abb_source: offer.abb_source,
            abb_dates_raw: offer.abb_dates_raw,
            // GST context — month-wise turnover breakdown for the editable GST
            // table. Only used when calculation_method === 'GST_TURNOVER'.
            gst_summary: offer.gst_summary,
            // Net Profit context — the per-year cash-profit build-up + dip analysis
            // (from the offer's applicant breakdown) for the editable NP table.
            np_year_wise: offer.eligibility_breakdown?.applicants_breakdown?.[0]?.year_wise_data
                ?? breakdownData?.applicants_breakdown?.[0]?.year_wise_data ?? null,
            np_year_analysis: offer.eligibility_breakdown?.applicants_breakdown?.[0]?.year_analysis
                ?? breakdownData?.applicants_breakdown?.[0]?.year_analysis ?? null,
            income_value: offer.income_value,
        };
        setSelectedEligibilityBreakdown(breakdown);
        // Deep clone for editable state
        setEditableBreakdown(JSON.parse(JSON.stringify(breakdown)));
        setIsEdited(false);
        setMultiplierExpanded(false); // start collapsed each time the dialog opens
        setFormulaExpanded(false);
        setEligibilityDialogVisible(true);
    };

    // Select offer handler
    const handleSelectOffer = (offer) => {
        if (onSelectOffer) {
            onSelectOffer(offer);
        }
    };

    // Details dialog footer
    const detailsDialogFooter = (
        <Button label="Close" icon="pi pi-times" onClick={() => setDetailsDialogVisible(false)} className="p-button-text p-button-sm" />
    );

    // Metric block — label on TOP (small grey), value BELOW (large bold). Matches
    // eLoan reference layout. Lets us pack 4 columns per row at desktop without
    // squeezing label+value onto one line.
    // Locked min-height on every row so columns line up across offer cards even
    // when values vary in length / wrap differently. Null/undefined/empty values
    // render as "—" so the row still occupies its slot for alignment.
    const renderRow = (label, value, valueClass = '') => {
        const has = value !== null && value !== undefined && value !== '' && value !== '-';
        return (
            <div className="flex flex-column py-2 px-2 border-bottom-1 surface-border" style={{ minHeight: '3rem' }}>
                <span className="text-xs text-600">{label}</span>
                <span className={`text-sm font-semibold text-900 ${valueClass}`}>{has ? value : '—'}</span>
            </div>
        );
    };

    // Filter sidebar — extracted so BOTH the Cards view and the Table (accordion)
    // view render the same constant left-hand filter panel. Returns the <aside>.
    const renderFilterSidebar = () => {
        // Build the unique-lenders list from the raw offers (NOT filteredOffers,
        // so unchecking a lender doesn't remove it from the list mid-toggle).
        const lenderCounts = new Map();
        offers.forEach((o) => {
            const name = o.bank_name || 'Unknown';
            lenderCounts.set(name, (lenderCounts.get(name) || 0) + 1);
        });
        const uniqueLenders = Array.from(lenderCounts.keys()).sort((a, b) => a.localeCompare(b));
        const allLendersSelected = !filters.selectedLenders || filters.selectedLenders.size === uniqueLenders.length;

        return (
            <aside
                className="surface-0 border-1 surface-border border-round p-3"
                style={{
                    width: '17rem',
                    flexShrink: 0,
                    position: (inModal || scrollContained) ? 'relative' : 'sticky',
                    top: (inModal || scrollContained) ? 'auto' : '8.5rem',
                    maxHeight: inModal ? 'none' : (scrollContained ? scrollHeight : 'calc(100vh - 9.5rem)'),
                    overflowY: inModal ? 'visible' : 'auto',
                }}
            >
                <div className="flex justify-content-between align-items-center mb-2">
                    <span className="text-sm font-semibold text-700 uppercase">Filters</span>
                    <Button label="Clear All" text size="small" onClick={resetFilters} className="p-0 text-xs" />
                </div>
                <div className="text-xs text-600 mb-2">
                    Showing <strong className="text-900">{filteredOffers.length}</strong> of {offers.length} offers
                </div>
                {/* Table first — it is the default view, and the toggle should read
                    in the same order as the options' precedence. */}
                <div className="flex gap-1 mb-3" role="tablist" aria-label="View mode">
                    <Button
                        label="Table" icon="pi pi-list" size="small"
                        severity={effectiveViewMode === 'table' ? 'success' : undefined}
                        outlined={effectiveViewMode !== 'table'}
                        onClick={() => setAndPersistViewMode('table')}
                        aria-pressed={effectiveViewMode === 'table'} className="flex-1"
                    />
                    <Button
                        label="Cards" icon="pi pi-th-large" size="small"
                        severity={effectiveViewMode === 'cards' ? 'success' : undefined}
                        outlined={effectiveViewMode !== 'cards'}
                        onClick={() => setAndPersistViewMode('cards')}
                        aria-pressed={effectiveViewMode === 'cards'} className="flex-1"
                    />
                </div>
                {onShareViaWhatsApp && (
                    <Button
                        label="Share via WhatsApp" icon="pi pi-whatsapp" size="small"
                        severity="success" outlined className="w-full mb-3" onClick={onShareViaWhatsApp}
                    />
                )}
                {/* Single-payslip notice — tells the DSA that the eligibility/EMI
                    figures are derived from just ONE payslip's net salary, treated
                    as the average. Helps the DSA explain to the borrower why upload
                    of 2-3 months' payslips would give a more accurate picture (and
                    flag variance, like a one-off bonus month). Only shown when the
                    parent passes payslipCount === 1; suppressed otherwise. */}
                {Number(payslipCount) === 1 && (
                    <div className="mb-3 p-2 border-round flex align-items-start gap-2" style={{ fontSize: '0.7rem', backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}>
                        <i className="pi pi-info-circle text-orange-500 mt-1" style={{ fontSize: '0.75rem' }} />
                        <div className="flex-1">
                            <div className="font-semibold text-900 mb-1">Only 1 payslip uploaded</div>
                            <div className="text-color-secondary">
                                Eligibility and EMI are calculated assuming the <strong>same income for 3 months</strong>.
                                Upload upto 3 payslips for a more accurate, variance-aware estimate.
                            </div>
                        </div>
                    </div>
                )}
                <div className="mb-3 surface-50 border-round p-2">
                    <div className="text-xs font-semibold text-700 uppercase mb-1">
                        <i className="pi pi-bolt text-primary text-xs mr-1"></i>Quick EMI
                    </div>
                    <InputNumber
                        value={filters.quickEmiBudget}
                        onValueChange={(e) => updateFilter('quickEmiBudget', e.value)}
                        placeholder="Max EMI you can afford"
                        mode="currency" currency="INR" locale="en-IN"
                        className="w-full" inputClassName="w-full p-inputtext-sm"
                    />
                    {filters.quickEmiBudget > 0 && (
                        <div className="text-xs text-600 mt-1">EMI ≤ ₹{Number(filters.quickEmiBudget).toLocaleString('en-IN')}/mo</div>
                    )}
                </div>
                <div className="mb-3">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="text-xs font-semibold text-700 uppercase">Lenders</span>
                        <Button
                            label={allLendersSelected ? 'Clear' : 'Select all'} text size="small"
                            onClick={() => updateFilter('selectedLenders', allLendersSelected ? new Set() : null)}
                            className="p-0 text-xs"
                        />
                    </div>
                    <div className="overflow-y-auto" style={{ maxHeight: '12rem' }}>
                        {uniqueLenders.map((name) => {
                            const checked = !filters.selectedLenders || filters.selectedLenders.has(name);
                            const id = `o-lender-${name}`;
                            return (
                                <div key={name} className="flex align-items-center gap-2 mb-1 text-sm">
                                    <Checkbox inputId={id} checked={checked} onChange={() => toggleLender(name, uniqueLenders)} />
                                    <label htmlFor={id} className="flex-1 white-space-nowrap overflow-hidden text-overflow-ellipsis cursor-pointer" title={name}>{name}</label>
                                    <span className="text-xs text-500">({lenderCounts.get(name)})</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="mb-3">
                    <div className="text-xs font-semibold text-700 uppercase mb-2">Max Interest Rate</div>
                    <Slider
                        value={filters.maxInterestRate}
                        onChange={(e) => updateFilter('maxInterestRate', typeof e.value === 'number' ? e.value : 20)}
                        min={7} max={20} step={0.25}
                    />
                    <div className="flex justify-content-between text-xs text-600 mt-2">
                        <span>7%</span>
                        <strong className="text-primary">≤ {filters.maxInterestRate}%</strong>
                        <span>20%</span>
                    </div>
                </div>
                <div className="mb-3">
                    <div className="text-xs font-semibold text-700 uppercase mb-2">Your CIBIL Score</div>
                    <InputText
                        value={filters.userCibil ?? ''}
                        onChange={(e) => {
                            const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                            if (raw === '') return updateFilter('userCibil', null);
                            const n = Number(raw);
                            updateFilter('userCibil', Math.min(900, Math.max(0, n)));
                        }}
                        inputMode="numeric" placeholder="e.g. 750"
                        className="w-full p-inputtext-sm" maxLength={3}
                    />
                    {filters.userCibil > 0 && (
                        <div className="text-xs text-600 mt-1">Showing offers that accept CIBIL ≥ {filters.userCibil}</div>
                    )}
                </div>
                <div className="mb-3">
                    <div className="text-xs font-semibold text-700 uppercase mb-2">List Type</div>
                    {[
                        { key: 'listed', label: 'Listed (your employer)' },
                        { key: 'unlisted', label: 'Unlisted' },
                    ].map((opt) => {
                        const id = `o-list-${opt.key}`;
                        return (
                            <div key={opt.key} className="flex align-items-center gap-2 mb-1 text-sm">
                                <Checkbox inputId={id} checked={filters.listType[opt.key]} onChange={() => toggleFilterChild('listType', opt.key)} />
                                <label htmlFor={id} className="cursor-pointer">{opt.label}</label>
                            </div>
                        );
                    })}
                </div>
                {(() => {
                    const verifiedCount = offers.filter(o => o.is_verified).length;
                    const notVerifiedCount = offers.length - verifiedCount;
                    return (
                        <div className="mb-3">
                            <div className="text-xs font-semibold text-700 uppercase mb-2">Verification</div>
                            {[
                                { key: 'verified', label: 'Verified', count: verifiedCount },
                                { key: 'notVerified', label: 'Not Verified', count: notVerifiedCount },
                            ].map((opt) => {
                                const id = `o-verif-${opt.key}`;
                                return (
                                    <div key={opt.key} className="flex align-items-center justify-content-between gap-2 mb-1 text-sm">
                                        <div className="flex align-items-center gap-2">
                                            <Checkbox inputId={id} checked={filters.verification[opt.key]} onChange={() => toggleFilterChild('verification', opt.key)} />
                                            <label htmlFor={id} className="cursor-pointer">{opt.label}</label>
                                        </div>
                                        <span className="text-xs text-500">({opt.count})</span>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}
                <div className="mb-2">
                    <div className="text-xs font-semibold text-700 uppercase mb-2">Special Features</div>
                    {[
                        { key: 'noPreClosure', label: 'No Pre-closure charges' },
                        { key: 'zeroProcessingFee', label: 'Zero Processing Fee' },
                        { key: 'partPaymentAllowed', label: 'Part Payment Allowed' },
                        { key: 'bestRateOnly', label: 'Best Rate (top 3)' },
                    ].map((opt) => {
                        const id = `o-feat-${opt.key}`;
                        return (
                            <div key={opt.key} className="flex align-items-center gap-2 mb-1 text-sm">
                                <Checkbox inputId={id} checked={filters.features[opt.key]} onChange={() => toggleFilterChild('features', opt.key)} />
                                <label htmlFor={id} className="cursor-pointer">{opt.label}</label>
                            </div>
                        );
                    })}
                </div>
                <div className="mt-3 pt-2 border-top-1 surface-border text-xs text-600 text-center">
                    Showing <strong className="text-900">{filteredOffers.length}</strong> of {offers.length} offers
                </div>
            </aside>
        );
    };

    // Compact label/value cell — used by both Table (accordion) and Cards (lanes).
    const fieldCell = (label, value, valueClass = 'text-900') => (
        <div className="flex flex-column">
            <span className="text-xs text-400 uppercase" style={{ letterSpacing: '0.03em' }}>{label}</span>
            <span className={`text-sm font-bold ${valueClass}`}>{value}</span>
        </div>
    );

    // Full per-offer detail grid — shared by the accordion (Table) and lanes (Cards)
    // inline-expand. Cost / eligibility / charges / salary analysis / conditions.
    // `compact` (lanes view) stacks every section full-width (col-12) so the
    // narrow 1/3-width lane doesn't squeeze 4 columns into unreadable text.
    const renderOfferDetails = (offer, compact = false) => {
        // Cost is the densest section (~9 rows); give it half width on large
        // screens so it doesn't tower over the sparse Pre-closure / Part-payment
        // sections (2-3 rows each), which share the other half. Fixes the
        // 2026-07-04 spacing imbalance where Cost took a narrow tall column and
        // the charge sections looked empty beside it.
        // Each data point is a GRID CELL so a section lays its rows out
        // horizontally — 3 across on desktop (2 on small screens; stacked in the
        // narrow compact lane). Sections themselves stack vertically below.
        const itemCol = compact ? 'col-12' : 'col-12 sm:col-6 md:col-4';
        // A single label→value cell. Digits are tabular so money columns align.
        const detailRow = (label, value, valueClass = '') => {
            const has = value !== null && value !== undefined && value !== '' && value !== '-';
            return (
                <div className={`ps-detail-row ${itemCol} flex flex-column gap-1 py-2`}>
                    <span className="text-xs text-500" style={{ letterSpacing: '0.02em' }}>{label}</span>
                    <span className={`text-sm font-semibold text-900 ${valueClass}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {has ? value : '—'}
                    </span>
                </div>
            );
        };
        // Section header — a small tinted icon chip + uppercase label, with a
        // hairline rule beneath. Consistent shape across every section so the
        // panel reads as one professional block, not a stack of loose rows.
        const sectionHeader = (icon, color, text) => (
            <div className="flex align-items-center gap-2 pb-2 mb-1 border-bottom-1 surface-border">
                <span className={`ps-sec-chip flex align-items-center justify-content-center border-round ${color}`}>
                    <i className={`pi ${icon}`} style={{ fontSize: '0.7rem' }}></i>
                </span>
                <span className={`text-base font-bold uppercase ${color}`} style={{ letterSpacing: '0.05em' }}>{text}</span>
            </div>
        );
        // Company / employer name for the offer. Prefer a matched company_name,
        // but fall back to the free-text employer_name (payslip OCR / wizard
        // input) so Home Loan offers — which usually have no matched company —
        // still show the borrower's employer. Skip the 'General' placeholder and
        // any value that just echoes the bank name.
        // Business-loan methods (ABB / Net Profit / GST) have no employer — the
        // engine sometimes puts the method name into company_name (e.g. "Average
        // Banking Balance"). Never show a Company section for those; it only
        // applies to salaried offers with a real matched/typed employer.
        const isBusinessMethodOffer = ['ABB', 'NET_PROFIT', 'GST_TURNOVER'].includes(
            String(offer.calculation_method || '').toUpperCase()
        );
        const methodLabels = ['Average Banking Balance', 'Net Profit', 'GST Turnover'];
        const companyName = (() => {
            if (isBusinessMethodOffer) return null;
            const candidates = [offer.company_name, offer.employer_name, offer.employer_name_text];
            for (const c of candidates) {
                if (c && c !== 'General' && c !== offer.bank_name && !methodLabels.includes(c)) return c;
            }
            return null;
        })();
        return (
            // No card background — sections stack vertically (Cost → Pre-closure →
            // Part Payment → Salary Analysis), and each section lays its data out
            // 3-across via its own inner grid.
            <div className="flex flex-column gap-3 mt-2">
                {companyName && (
                    <div>
                        {sectionHeader('pi-building', 'text-blue-700', 'Company')}
                        <div className="text-sm font-semibold text-900 px-2 pt-1">{companyName}</div>
                    </div>
                )}
                {/* RATE & ELIGIBILITY section removed: Effective ROI was wrongly
                    calculated and CIBIL is already shown on the front card. Base
                    Rate now lives in Cost, directly under Interest p.a. — it's the
                    repo+margin breakdown of that same number, so the two belong
                    side by side rather than in a section of their own. */}
                <div>
                    {sectionHeader('pi-wallet', 'text-purple-700', 'Cost')}
                    <div className="grid">
                        {/* Single "Loan Amount" removed — the "Loan Range (allowed)" below
                            already shows the lender's slab, so a lone amount was redundant. */}
                        {detailRow('Loan Range (allowed)', (offer.min_loan_amount || offer.max_loan_amount || offer.min_loan_amount_raw || offer.max_loan_amount_raw)
                            ? loanRangeText(offer)
                            : null)}
                        {/* Lender's accepted CIBIL band — always shown here; front shows
                            the borrower's entered score when given. */}
                        {detailRow('CIBIL Range (accepted)', (offer.min_cibil || offer.max_cibil)
                            ? `${offer.min_cibil || '-'} to ${offer.max_cibil || '-'}`
                            : null)}
                        {enteredCibil != null && detailRow('Your CIBIL', String(enteredCibil), 'text-primary')}
                        {/* Income range for the offer's calculation_method — salary /
                            ABB / net profit / GST turnover. Always shown here (all
                            methods) so a self-employed HL borrower sees the band their
                            entered income maps to, not just salaried borrowers. */}
                        {(() => { const ir = incomeRangeInfo(offer); return ir ? detailRow(ir.label, ir.text) : null; })()}
                        {/* Interest rate — band offers show the full spread + calc note */}
                        {detailRow('Interest p.a.', offer.roi_is_range && offer.roi_display ? offer.roi_display : ((offer.roi || offer.roi === 0) ? `${offer.roi}%` : null), 'text-blue-700')}
                        {/* How that rate is built up — repo rate + margin, as uploaded on
                            the range. Skipped entirely (not shown as a dash) when the
                            lender's sheet carried no base rate, so Personal / Business
                            offers don't gain an empty row. */}
                        {formatBaseRate(offer.base_rate) && detailRow('Base Rate (Repo + Margin)', formatBaseRate(offer.base_rate))}
                        {detailRow('Total Interest', offer.total_interest != null ? formatCurrency(offer.total_interest) : null)}
                        {detailRow('Total Cost', offer.total_cost != null ? formatCurrency(offer.total_cost) : null)}
                        {detailRow('Tenure', (offer.tenure || offer.tenure_months) ? `${offer.tenure || offer.tenure_months} months` : null)}
                        {offer.roi_is_range && offer.roi_calc_note && (
                            <div className="col-12 px-2 pb-1 text-xs text-500">{offer.roi_calc_note}</div>
                        )}
                    </div>
                </div>
                <div>
                    {sectionHeader('pi-undo', 'text-purple-700', 'Pre-closure')}
                    <div className="grid">
                        {detailRow('Pre-closure Charges', formatChargeSlabs(offer.foreclosurecharges) || offer.pre_closure_charges)}
                        {detailRow('Lock-in', offer.pre_closure_month ? `${offer.pre_closure_month} months` : null)}
                    </div>
                </div>
                <div>
                    {sectionHeader('pi-money-bill', 'text-purple-700', 'Part Payment')}
                    <div className="grid">
                        {detailRow('Part Payment Charges', formatChargeSlabs(offer.ppr) || offer.part_payment_charges)}
                        {/* ppa is stored as a coded token ("p25" = 25%); run it through
                            the same slab formatter so it reads "25%" not "p25". */}
                        {detailRow('Max %', formatChargeSlabs(offer.ppa) || offer.part_payment_amount)}
                        {detailRow('Frequency', offer.ppt ? `${offer.ppt}x/year` : (offer.part_payment_times ? `${offer.part_payment_times}x/year` : null))}
                    </div>
                </div>
                {(() => {
                    const plMethods = ['full', 'discovery', 'SALARY'];
                    const isPersonalLoanOffer = !offer.calculation_method || plMethods.includes(offer.calculation_method);
                    const salaryVal = offer.salary || offer.net_salary_per_month
                        || ((offer.effective_salary || 0) + (offer.existing_emi_deducted || 0));
                    if (!isPersonalLoanOffer || !salaryVal) return null;
                    const existingEmi = Number(offer.existing_emi_deducted || 0);
                    const effectiveSalary = Number(offer.effective_salary || salaryVal - existingEmi);
                    const foirAvailable = offer.foir_income || offer.foir_available;
                    // Derive "Salary After All EMIs" + ratio LIVE from the current EMI so they
                    // recalc when the band-rate slider changes (offer.emi gets patched via
                    // mergeRecalc). Fall back to the backend-stored values when no EMI.
                    const newEmiNum = Number(offer.emi || 0);
                    const salaryAfterAllEmi = newEmiNum > 0
                        ? Math.round(effectiveSalary - newEmiNum)
                        : offer.salary_after_emi;
                    const emiToSalaryRatio = (newEmiNum > 0 && salaryVal > 0)
                        ? `${((newEmiNum / salaryVal) * 100).toFixed(2)}%`
                        : offer.emi_to_salary_ratio;
                    const hasLenderNth = offer.nth_source === 'lender_selection'
                        && Number(offer.nth_used) > 0 && Number(offer.nth_default) > 0
                        && Number(offer.nth_used) !== Number(offer.nth_default);
                    return (
                        <div>
                            {sectionHeader('pi-wallet', 'text-purple-700', 'Salary Analysis')}
                            {/* One flat grid so every salary figure flows 3-across too. */}
                            <div className="grid">
                                {hasLenderNth ? (
                                    <>
                                        {detailRow('Default Salary', formatCurrency(offer.nth_default))}
                                        {detailRow('Recalculated NTH', formatCurrency(offer.nth_used), 'text-blue-700')}
                                    </>
                                ) : detailRow('Net Salary', formatCurrency(salaryVal))}
                                {existingEmi > 0 && detailRow('Existing EMI', `- ${formatCurrency(existingEmi)}`, 'text-orange-600')}
                                {existingEmi > 0 && detailRow('Effective Salary', formatCurrency(effectiveSalary), 'text-green-600')}
                                {(offer.foir_display || offer.foir_percentage) && detailRow('FOIR (as per lender)', offer.foir_display || `${offer.foir_percentage}%`)}
                                {foirAvailable > 0 && detailRow('FOIR Available for EMI', formatCurrency(foirAvailable))}
                                {offer.emi > 0 && detailRow('New EMI (this offer)', formatCurrency(offer.emi))}
                                {salaryAfterAllEmi !== null && salaryAfterAllEmi !== undefined && detailRow('Salary After All EMIs', formatCurrency(salaryAfterAllEmi), salaryAfterAllEmi < 0 ? 'text-red-600' : 'text-green-600')}
                                {emiToSalaryRatio && detailRow('EMI-to-Salary Ratio', emiToSalaryRatio)}
                            </div>
                        </div>
                    );
                })()}
                {/* ABB Analysis — the banking balance THIS lender computed, and the
                    days it sampled to get there. Two lenders reading the same
                    statement legitimately differ (Axis samples 7,11,19,28 while
                    ICICI uses 1,5,15,25), so the figure belongs on the card next
                    to that lender's dates rather than as one shared number. */}
                {(() => {
                    const abb = offer.abb_summary;
                    if (!abb || !(abb.abb > 0)) return null;
                    const isAllDays = abb.mode === 'all_calendar_days';
                    const monthly = Array.isArray(abb.monthly_abbs) ? abb.monthly_abbs : [];
                    return (
                        <div>
                            {sectionHeader('pi-building', 'text-purple-700', 'ABB Analysis')}
                            {/* Flat grid so every ABB figure flows 3-across like the other sections. */}
                            <div className="grid">
                                {detailRow('ABB (this lender)', formatCurrency(abb.abb), 'text-teal-700')}
                                {/* Falls back to the raw rate-card token when the summary
                                    carries no rendered list, so this row can never go blank
                                    on an offer that does have sampling dates configured. */}
                                {detailRow('ABB Dates (rate card)', isAllDays
                                    ? 'Not specified — every calendar day'
                                    : (abb.dates_display
                                        || (String(offer.abb_dates_raw ?? '').trim()
                                            ? String(offer.abb_dates_raw).split(',').map((d) => ordinalDay(d.trim())).filter(Boolean).join(', ')
                                            : 'Not specified — every calendar day')))}
                                {/* Only meaningful when a statement was actually sampled.
                                    A typed ABB has no months behind it, so this would
                                    otherwise read "Average of 0 monthly average(s)" — the
                                    note at the bottom explains that case instead. */}
                                {abb.source === 'bank_statement' && detailRow('Calculation', isAllDays
                                    ? `Sum of daily closing balances ÷ ${abb.total_calendar_days} days`
                                    : `Average of ${abb.months_count} monthly average(s)`)}
                                {isAllDays && abb.carried_forward_days > 0 && detailRow(
                                    'Days Carried Forward', `${abb.carried_forward_days} (no transaction)`)}
                                {abb.multiplier && detailRow('Banking Multiplier', String(abb.multiplier))}
                                {abb.monthly_income > 0 && detailRow('Income Considered', formatCurrency(abb.monthly_income), 'text-green-600')}
                                {detailRow('Source', abb.source === 'bank_statement'
                                    ? 'Bank statement'
                                    : 'Manually entered', abb.source === 'bank_statement' ? 'text-green-600' : 'text-orange-600')}
                            </div>
                            {monthly.length > 0 && (
                                <div className="px-2 pb-2">
                                    <div className="text-xs text-500 mb-1">Month-wise ABB</div>
                                    <div className="flex flex-wrap gap-2">
                                        {monthly.map((m) => (
                                            <span key={m.month} className="text-xs surface-100 border-round px-2 py-1">
                                                <span className="text-600">{m.month}</span>
                                                <span className="ml-2 font-semibold">{formatCurrency(m.monthly_abb)}</span>
                                                {m.days_counted && <span className="ml-1 text-500">({m.days_counted}d)</span>}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {abb.source !== 'bank_statement' && (
                                <div className="px-2 pb-1 text-xs text-orange-600">
                                    Entered manually, so this lender's ABB dates were not applied.
                                    Upload the bank statement to calculate ABB per lender.
                                </div>
                            )}
                        </div>
                    );
                })()}
                {offer.conditions && (
                    <div>
                        {sectionHeader('pi-info-circle', 'text-purple-700', 'Conditions')}
                        <div className="text-sm text-700 px-2 py-1 white-space-pre-line">{offer.conditions}</div>
                    </div>
                )}
            </div>
        );
    };

    // Attribution lines — "Updated by / Verified by / Regenerated by <email> · <time>".
    // Shared by both lanes and accordion so the editor/verifier/claimer is shown
    // (e.g. "Verified by keerti@gmail.com"). Returns null when no attribution.
    const renderOfferAttribution = (offer) => {
        const updatedBy = offer.updated_by_email || offer.updated_by_name;
        const verifiedBy = offer.is_verified && (offer.verified_by_email || offer.verified_by_name);
        const regeneratedBy = offer.regenerated_by_email || offer.regenerated_by_name;
        if (!updatedBy && !verifiedBy && !regeneratedBy) return null;
        const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
        const sameUpdaterVerifier = updatedBy && verifiedBy && updatedBy === verifiedBy;
        return (
            <div className="mt-1">
                {sameUpdaterVerifier ? (
                    <div className="text-center">
                        <span className="text-xs text-green-700 font-italic">
                            <i className="pi pi-check-circle text-xs mr-1"></i>
                            Updated &amp; Verified by <span className="font-medium not-italic">{updatedBy}</span>
                            {(() => { const latest = (offer.verified_at && offer.updated_at) ? (new Date(offer.verified_at) > new Date(offer.updated_at) ? offer.verified_at : offer.updated_at) : (offer.verified_at || offer.updated_at); return fmt(latest) ? <>{' · '}{fmt(latest)}</> : null; })()}
                        </span>
                    </div>
                ) : (
                    <>
                        {updatedBy && (
                            <div className="text-center">
                                <span className="text-xs text-color-secondary font-italic">
                                    <i className="pi pi-pencil text-xs mr-1"></i>
                                    Updated by <span className="text-700 font-medium not-italic">{updatedBy}</span>
                                    {fmt(offer.updated_at) && <>{' · '}{fmt(offer.updated_at)}</>}
                                </span>
                            </div>
                        )}
                        {verifiedBy && (
                            <div className="text-center">
                                <span className="text-xs text-green-700 font-italic">
                                    <i className="pi pi-check-circle text-xs mr-1"></i>
                                    Verified by <span className="font-medium not-italic">{verifiedBy}</span>
                                    {fmt(offer.verified_at) && <>{' · '}{fmt(offer.verified_at)}</>}
                                </span>
                            </div>
                        )}
                    </>
                )}
                {regeneratedBy && (
                    <div className="text-center">
                        <span className="text-xs text-color-secondary font-italic">
                            <i className="pi pi-refresh text-xs mr-1"></i>
                            Regenerated by <span className="text-700 font-medium not-italic">{regeneratedBy}</span>
                            {fmt(offer.regenerated_at) && <>{' · '}{fmt(offer.regenerated_at)}</>}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="offers-compact">
            <style>{`
                .offers-compact .p-card .p-card-body { padding: 0.5rem; }
                .offers-compact .p-card .p-card-content { padding: 0.25rem 0; }
                .offers-compact .p-divider { margin: 0.35rem 0; }
                .offers-compact .p-tag { font-size: 0.65rem; padding: 0.1rem 0.35rem; }
                .offers-compact h4, .offers-compact .text-xl { font-size: 0.95rem !important; }
                .offers-compact h5 { font-size: 0.85rem !important; }
                .offers-compact h6 { font-size: 0.8rem !important; }
                .offers-compact .p-button.p-button-sm { font-size: 0.7rem; padding: 0.3rem 0.6rem; }
                .offers-compact .p-button.p-button-rounded { width: 1.8rem; height: 1.8rem; }
                .offers-compact .p-button.p-button-rounded .p-button-icon { font-size: 0.75rem; }
                .offers-compact .p-accordion .p-accordion-header-link { padding: 0.5rem 0.75rem; font-size: 0.78rem; }
                .offers-compact .p-accordion .p-accordion-content { padding: 0.5rem; }
                .offers-compact .p-inputtext { font-size: 0.75rem; padding: 0.3rem 0.5rem; }
                /* Slim, subtle scrollbar for bank lanes — doesn't consume card width. */
                /* overscroll-behavior: contain keeps a lane's wheel-scroll INSIDE the
                   lane — when it reaches its top/bottom it stops instead of bleeding
                   the scroll through to the page. Scroll the page only when the cursor
                   is outside the lanes. */
                .lane-scroll { scrollbar-width: thin; scrollbar-color: var(--surface-300) transparent; overscroll-behavior: contain; }
                .lane-scroll::-webkit-scrollbar { width: 6px; }
                .lane-scroll::-webkit-scrollbar-track { background: transparent; }
                .lane-scroll::-webkit-scrollbar-thumb { background: var(--surface-300); border-radius: 3px; }
                .lane-scroll::-webkit-scrollbar-thumb:hover { background: var(--surface-400); }
                /* overlay so the bar floats over content, not reserving a gutter */
                @supports (overflow: overlay) { .lane-scroll { overflow-y: overlay; } }
                .offers-compact .p-inputnumber-input { font-size: 0.75rem; padding: 0.3rem 0.5rem; }
                .offers-compact .p-message { font-size: 0.75rem; }
                .offers-compact .p-message .p-message-wrapper { padding: 0.4rem 0.75rem; }
                .offers-compact .p-dialog .p-dialog-header { padding: 0.75rem 1rem; }
                .offers-compact .p-dialog .p-dialog-content { padding: 0.5rem 1rem; }
                .offers-compact .p-dialog .p-dialog-footer { padding: 0.5rem 1rem; }
                /* View-details panel — section header chip + row hairlines. */
                .offers-compact .ps-sec-chip {
                    width: 1.5rem; height: 1.5rem; flex-shrink: 0;
                    background: var(--surface-100);
                }
                .offers-compact .ps-detail-row { border-bottom: 1px solid var(--surface-100); }
                .offers-compact .ps-detail-row:last-child { border-bottom: 0 none; }
            `}</style>

            {/* Header strip + Cards/Table toggle + Lock instructions all moved
                INTO the filter sidebar (Cards view) or the table-view top row to
                free up vertical space. The Cards/Table toggle for table-view only
                lives below since the sidebar isn't shown there. */}
            {effectiveViewMode === 'table' && !isMobile && (
                // Action row sits under the parent's sticky offer-tabs strip
                // (position:sticky top:4rem z:100 + ::after 1rem seal). Without
                // an explicit z-index and top margin, this row scrolls UNDER
                // the seal and the labels visually clip. Position:relative +
                // z-index ABOVE the strip's ::after (which has no z-index of
                // its own, so any positive z gets above it) + a small top
                // margin to clear the bottom of the strip's drop-shadow.
                <div
                    className="flex justify-content-end align-items-center gap-2 mb-2 mt-3"
                    style={{ position: 'relative', zIndex: 1 }}
                >
                    <button
                        type="button"
                        onClick={() => {
                            if (expandedOfferIds.size === offerList.length) {
                                setExpandedOfferIds(new Set());
                            } else {
                                setExpandedOfferIds(new Set(offerList.map(o => o._recalcKey || o.offer_id)));
                            }
                        }}
                        className="inline-flex align-items-center gap-1 px-3 py-1 text-xs font-medium cursor-pointer border-1 surface-border border-round surface-0 text-700 hover:surface-100"
                        title={expandedOfferIds.size === offerList.length ? 'Collapse all rows' : 'Expand all rows'}
                    >
                        <i className={`pi ${expandedOfferIds.size === offerList.length ? 'pi-chevron-up' : 'pi-chevron-down'} text-xs`} aria-hidden="true"></i>
                        <span>{expandedOfferIds.size === offerList.length ? 'Collapse all' : 'Expand all'}</span>
                    </button>
                    {/* The Cards / Table toggle that used to sit here was removed —
                        the same control already lives at the top of the Filters
                        sidebar, and two copies of one toggle on the same screen is
                        just noise. */}
                </div>
            )}


            {/* ABB priced from a typed figure, not a statement. Each lender's own
                sampling days were skipped, so these are approximate — say it once
                here rather than repeating it on every card. */}
            {hasManualAbbOffers && (
                <div className="flex align-items-start gap-2 p-2 mb-2 border-round surface-100">
                    <i className="pi pi-info-circle text-primary mt-1" style={{ fontSize: '0.8rem' }} aria-hidden="true"></i>
                    <div className="text-xs text-700 line-height-3">
                        These offers use the <strong>ABB you entered manually</strong>.
                        Banks calculate ABB differently — some average <strong>every day</strong> of
                        the statement, most use <strong>fixed dates each month</strong>.{' '}
                        <strong>Upload the bank statement</strong> to get each lender&apos;s own
                        ABB and a more accurate eligibility.
                    </div>
                </div>
            )}

            {/* Filter sidebar JSX — used twice: as a fixed-position desktop sidebar
                AND as the body of the mobile drawer. Defining it once avoids drift. */}
            {(() => { return null; })()}

            {/* Mobile-only: Filter toggle button at the top */}
            {effectiveViewMode === 'cards' && isMobile && !hideFilters && (
                <div className="flex justify-content-between align-items-center mt-3 mb-2 px-2">
                    <Button
                        label="Filters"
                        icon="pi pi-sliders-h"
                        size="small"
                        outlined
                        onClick={() => setFilterDrawerOpen(true)}
                    />
                    <span className="text-xs text-600">{filteredOffers.length} of {offers.length} offers</span>
                </div>
            )}

            {/* Cards view — sidebar (left) + offers grid (right) on desktop;
                offers grid full-width on mobile (filters live in a slide-in drawer). */}
            {effectiveViewMode === 'cards' && (
            <div style={{ position: 'relative', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>

                {/* Desktop filter sidebar — shared with Table (accordion) view via renderFilterSidebar() */}
                {!isMobile && !hideFilters && renderFilterSidebar()}

                {/* Cards view = BANK LANES (Prototype C). Each bank is a column; its
                    offers stack below. 3 lanes per page with ◀▶ paging; pinned banks
                    (max 2) float left and stay visible. Front of each offer mini-card
                    shows the same 5 fields as the accordion; View details expands inline. */}
                <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                    {(() => {
                        const pinned = bankGroups.filter((g) => lockedBankNames.includes(g.bankName));
                        const unpinned = bankGroups.filter((g) => !lockedBankNames.includes(g.bankName));
                        const totalUnpinned = unpinned.length;

                        // Mobile: show exactly ONE bank per page (with all its offers),
                        // paged through the full bank list via the ◀▶ arrows. Desktop
                        // keeps the 3-across sliding window with pinned banks floated left.
                        // (lanePage is a sliding start offset that advances one bank at a
                        // time; clamped so the window never runs past the last bank.)
                        let visibleGroups, canPrev, canNext, start, maxStart, slots;
                        if (isMobile) {
                            maxStart = Math.max(0, bankGroups.length - 1);
                            start = Math.min(lanePage, maxStart);
                            slots = 1;
                            visibleGroups = bankGroups.length > 0 ? [bankGroups[start]] : [];
                            canPrev = start > 0;
                            canNext = start < maxStart;
                        } else {
                            slots = Math.max(1, LANES_PER_PAGE - pinned.length);
                            maxStart = Math.max(0, totalUnpinned - slots);
                            start = Math.min(lanePage, maxStart);
                            const pageUnpinned = unpinned.slice(start, start + slots);
                            visibleGroups = [...pinned, ...pageUnpinned];
                            canPrev = start > 0;
                            canNext = start < maxStart;
                        }

                        return (
                            <>
                                <div className="flex justify-content-between align-items-center mb-2 px-2">
                                    <span className="text-xs text-600">
                                        {isMobile
                                            ? (bankGroups.length > 0 ? `Bank ${start + 1} of ${bankGroups.length}` : 'No banks')
                                            : (totalUnpinned > 0 ? `Banks ${start + 1}–${Math.min(start + slots, totalUnpinned)} of ${totalUnpinned}` : 'All banks')}
                                        {!isMobile && pinned.length ? ` (+${pinned.length} pinned)` : ''}
                                    </span>
                                    {/* Mobile prev/next bank nav — lives in this bar next to
                                        the counter instead of arrows overlapping the card edges. */}
                                    {isMobile && (canPrev || canNext) && (
                                        <div className="flex gap-2">
                                            <Button
                                                icon="pi pi-chevron-left" rounded size="small" outlined
                                                aria-label="Previous bank" disabled={!canPrev}
                                                onClick={() => setLanePage((p) => Math.max(0, p - 1))}
                                            />
                                            <Button
                                                icon="pi pi-chevron-right" rounded size="small" outlined
                                                aria-label="Next bank" disabled={!canNext}
                                                onClick={() => setLanePage((p) => Math.min(maxStart, p + 1))}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Lanes row, with ◀▶ paging arrows vertically centered
                                    on the left/right edges (side navigation). */}
                                <div style={{ position: 'relative' }}>
                                {/* Desktop-only edge arrows. On mobile the prev/next
                                    nav lives in the "Bank X of N" bar above (no overlap). */}
                                {!isMobile && canPrev && (
                                    <Button
                                        icon="pi pi-chevron-left" rounded size="small" aria-label="Previous banks"
                                        onClick={() => setLanePage((p) => Math.max(0, p - 1))}
                                        className="shadow-3"
                                        style={{ position: 'absolute', left: '-1.1rem', top: '1.25rem', zIndex: 3, width: '2.25rem', height: '2.25rem' }}
                                    />
                                )}
                                {!isMobile && canNext && (
                                    <Button
                                        icon="pi pi-chevron-right" rounded size="small" aria-label="Next banks"
                                        onClick={() => setLanePage((p) => Math.min(maxStart, p + 1))}
                                        className="shadow-3"
                                        style={{ position: 'absolute', right: '-1.1rem', top: '1.25rem', zIndex: 3, width: '2.25rem', height: '2.25rem' }}
                                    />
                                )}
                                <div
                                    className={`flex gap-3 align-items-start ${isMobile ? 'flex-column' : ''}`}
                                    style={(scrollContained && !isMobile)
                                        ? { maxHeight: scrollHeight, overflow: 'hidden' }
                                        : undefined}
                                >
                                    {visibleGroups.map((group) => {
                                        const { bankName, slots: bankSlots, bestRoi, containsGlobalBest } = group;
                                        const bankPinned = lockedBankNames.includes(bankName);
                                        const canPinBank = lockedBankNames.length < MAX_LOCKED_BANKS || bankPinned;
                                        const initials = bankName.replace(/[^A-Za-z ]/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'BK';
                                        const fromRoi = Number.isFinite(bestRoi) ? `${bestRoi}%` : '—';
                                        // Each lane fills 1/3 of the row; maxWidth keeps it card-sized
                                        // when fewer than 3 banks are shown (so 1–2 lanes don't stretch).
                                        return (
                                            <div key={bankName} className={isMobile ? 'w-full' : 'flex-1'} style={isMobile ? { width: '100%', minWidth: 0 } : {
                                                minWidth: 0, flexBasis: 0, maxWidth: 'calc((100% - 1.5rem) / 3)',
                                                // Contained panel: give the column the box height and make it a
                                                // flex column so its offers list (below the fixed header) fills the
                                                // remaining space and scrolls INDEPENDENTLY — scrolling one bank
                                                // never moves the others.
                                                ...(scrollContained ? { display: 'flex', flexDirection: 'column', height: scrollHeight, minHeight: 0 } : {}),
                                            }}>
                                                {/* Bank header — STICKY to the top of the lane while the offers
                                                    below scroll. All lane headers reserve the same height (the
                                                    BEST BANK tag row is always present, just empty when not best)
                                                    so every column's header aligns. */}
                                                <div className={`surface-card border-1 border-round-top p-3 ${bankPinned ? 'border-primary' : (containsGlobalBest ? 'border-green-400' : 'surface-border')}`}
                                                     style={{
                                                         // Sticky so the lender stays identified while its offers
                                                         // scroll. In a modal/contained box it pins to the box top
                                                         // (0); on the page it must clear the FULL sticky offer-tabs
                                                         // strip (.oe-tabs-sticky: top 4rem + ~3.5rem tabs + 1rem
                                                         // seal ≈ 8.5rem) — otherwise the strip covers the header's
                                                         // top half.
                                                         position: isMobile ? 'static' : 'sticky',
                                                         top: (inModal || scrollContained) ? 0 : stickyHeaderTop,
                                                         zIndex: 2,
                                                         ...(containsGlobalBest && !bankPinned ? { background: 'var(--green-50)' } : {}),
                                                     }}>
                                                    <div className="flex align-items-center gap-2">
                                                        <div className="flex align-items-center justify-content-center border-round font-bold text-blue-700 bg-blue-50 flex-shrink-0" style={{ width: '2.25rem', height: '2.25rem', fontSize: '0.8rem' }}>{initials}</div>
                                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                            <div className="font-bold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" title={bankName}>{bankName}</div>
                                                            <div className="text-xs text-600">{bankSlots.length} offer{bankSlots.length > 1 ? 's' : ''} · from {fromRoi}</div>
                                                        </div>
                                                        <Button icon={bankPinned ? 'pi pi-lock' : 'pi pi-lock-open'} rounded text size="small" aria-label={bankPinned ? 'Unpin bank' : 'Pin bank'} onClick={() => { if (canPinBank) { handleToggleBankLock(bankName); setLanePage(0); } }} disabled={!canPinBank} tooltip={bankPinned ? 'Unpin this bank' : (canPinBank ? 'Pin this bank' : 'Unpin one first (max 2)')} tooltipOptions={{ position: 'top' }} className={bankPinned ? 'text-primary' : 'text-600'} />
                                                    </div>
                                                    {/* Reserved tag row — keeps every header the same height */}
                                                    <div className="mt-2" style={{ minHeight: '1.5rem' }}>
                                                        {containsGlobalBest && <Tag severity="success" value="BEST BANK" className="text-xs" />}
                                                    </div>
                                                </div>

                                                <div
                                                    className={`lane-scroll border-1 border-top-none border-round-bottom p-2 flex flex-column gap-3 ${bankPinned ? 'border-primary' : 'surface-border'}`}
                                                    style={(scrollContained && !isMobile)
                                                        ? { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }
                                                        : {
                                                            maxHeight: (inModal || isMobile) ? 'none' : 'calc(100vh - 16rem)',
                                                            overflowY: (inModal || isMobile) ? 'visible' : 'auto',
                                                        }}
                                                >
                                                    {bankSlots.map((slot) => {
                                                        const offer = mergeRecalc(slot.offer);
                                                        // Unique per card (handles duplicate backend ids → no duplicate React keys)
                                                        const offerId = slot._recalcKey || slot.offer_id;
                                                        const isSelected = selectedOffer?.offer_id === offer.offer_id;
                                                        const isBestOffer = slot.originalIndex === 0;
                                                        const isExpanded = expandedOfferIds.has(offerId);
                                                        let maxElig = offer.max_loan_eligibility;
                                                        if ((maxElig === null || maxElig === undefined) && eligibilityParams && offer.emi_per_lac > 0) {
                                                            const salary = eligibilityParams.net_salary_per_month || 0;
                                                            const foir = offer.eligibility_breakdown?.foir || 50;
                                                            const existingEmi = eligibilityParams.total_existing_loan_emi || 0;
                                                            const available = Math.max(0, (salary * (foir / 100)) - existingEmi);
                                                            maxElig = Math.round((available / offer.emi_per_lac) * 100000);
                                                        }
                                                        // Front shows the borrower's entered CIBIL when given; else the lender's range.
                                                        const cibil = enteredCibil != null ? String(enteredCibil) : cibilRangeText(offer);
                                                        const note = roiCalcNote(offer);
                                                        const offerCat = offer.matched_category_name && offer.matched_category_name !== 'General' && offer.matched_category_name !== offer.company_name
                                                            ? offer.matched_category_name
                                                            : (offer.category && offer.category !== 'General' && offer.category !== offer.company_name ? offer.category : null);
                                                        return (
                                                            <div key={offerId} className={`border-round p-2 ${isBestOffer ? 'bg-green-50' : 'surface-50'} ${isSelected ? 'border-2 border-green-500' : 'border-1 surface-border'}`}>
                                                                <div className="flex align-items-center gap-2 mb-2 flex-wrap">
                                                                    <span className="font-bold text-600 text-sm">#{offer.offer_number || (slot.originalIndex + 1)}</span>
                                                                    {isBestOffer && <Tag severity="success" value="BEST" className="text-xs" />}
                                                                    {offerCat && <Tag value={offerCat} severity="info" className="text-xs" />}
                                                                    {/* Which eligibility method calculated this offer — matters when the
                                                                        borrower entered multiple incomes (ABB + Net Profit + GST) and the
                                                                        list mixes methods. */}
                                                                    {offer.calculation_method && (
                                                                        <Tag
                                                                            value={offer.calculation_method_label
                                                                                || ({ ABB: 'ABB', NET_PROFIT: 'Net Profit', GST_TURNOVER: 'GST Turnover' }[offer.calculation_method] || offer.calculation_method)}
                                                                            severity="warning"
                                                                            className="text-xs"
                                                                            icon="pi pi-calculator"
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className="flex flex-column gap-2">
                                                                    {fieldCell('Interest p.a.', (
                                                                        <span className="flex flex-column">
                                                                            <span>{roiText(offer)}</span>
                                                                            {note && <span className="text-xs font-normal text-500">{note}</span>}
                                                                        </span>
                                                                    ), 'text-blue-700')}
                                                                    {/* Band-rate slider on the card — drag to pick a rate in
                                                                        [roi_min, roi_max]; EMI / eligibility / cost recalc live. */}
                                                                    {offer.roi_is_range && !readOnly && (
                                                                        <div className="px-1 -mt-1">
                                                                            <Slider
                                                                                value={Number(offer.roi) || Number(offer.roi_min)}
                                                                                onChange={(e) => applyCardRoi(offer, e.value)}
                                                                                min={Number(offer.roi_min)}
                                                                                max={Number(offer.roi_max)}
                                                                                step={bandStep(offer.roi_min, offer.roi_max)}
                                                                                className="w-full"
                                                                            />
                                                                            <div className="flex justify-content-between align-items-center text-500 mt-1" style={{ fontSize: '0.6rem' }}>
                                                                                <span>{offer.roi_min}%</span>
                                                                                {/* Direct entry — type an exact rate inside the band */}
                                                                                <InputNumber
                                                                                    value={Number(offer.roi) || Number(offer.roi_min)}
                                                                                    onValueChange={(e) => {
                                                                                        const lo = Number(offer.roi_min);
                                                                                        const hi = Number(offer.roi_max);
                                                                                        let v = Number(e.value);
                                                                                        if (!Number.isFinite(v)) return;
                                                                                        v = Math.min(hi, Math.max(lo, v));
                                                                                        applyCardRoi(offer, v);
                                                                                    }}
                                                                                    min={Number(offer.roi_min)}
                                                                                    max={Number(offer.roi_max)}
                                                                                    minFractionDigits={0}
                                                                                    maxFractionDigits={2}
                                                                                    suffix="%"
                                                                                    inputClassName="p-inputtext-sm text-center"
                                                                                    inputStyle={{ width: '4.5rem', padding: '0.15rem 0.3rem' }}
                                                                                    aria-label="Enter exact interest rate"
                                                                                />
                                                                                <span>{offer.roi_max}%</span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {fieldCell('EMI / mo', formatEmiDisplay(offer))}
                                                                    {(() => { const pf = formatProcessingFee(offer); return fieldCell('Processing Fee', (
                                                                        <span className="flex flex-column">
                                                                            <span>{pf.text}</span>
                                                                            {pf.note && <span className="text-xs font-normal text-500">{pf.note}</span>}
                                                                        </span>
                                                                    )); })()}
                                                                    {/* Entered amount, else the lender's slab band — one or the other */}
                                                                    {fieldCell('Loan Amount', loanAmountCellText(offer))}
                                                                    {fieldCell('CIBIL', cibil)}
                                                                    {/* ABB is lender-specific — each rate card samples its
                                                                        own days — so show it on the card, not only in Details. */}
                                                                    {(offer.abb_summary?.abb > 0 || offer.abb_value_used > 0) && fieldCell('ABB', (
                                                                        <span className="flex flex-column">
                                                                            <span>{formatCurrency(offer.abb_summary?.abb || offer.abb_value_used)}</span>
                                                                            <span className="text-xs font-normal text-500">
                                                                                {abbDatesCaption(offer)}
                                                                            </span>
                                                                        </span>
                                                                    ), offer.abb_source === 'manual_entry' ? 'text-600' : 'text-teal-700')}
                                                                    <div className="flex flex-column">
                                                                        <span className="text-xs text-400 uppercase" style={{ letterSpacing: '0.03em' }}>Max Eligibility</span>
                                                                        {(offer.eligibility_breakdown || eligibilityParams) ? (
                                                                            <button type="button" onClick={() => handleViewEligibilityBreakdown(offer)} className="p-0 border-none bg-transparent cursor-pointer text-left" title="Click to view / recalculate eligibility">
                                                                                <span className={`text-sm font-bold inline-flex align-items-center gap-1 ${maxElig === 0 ? 'text-orange-600' : 'text-green-700'}`}>
                                                                                    {maxElig ? formatCurrency(maxElig) : '—'}
                                                                                    <i className="pi pi-external-link text-xs text-600" aria-hidden="true"></i>
                                                                                </span>
                                                                            </button>
                                                                        ) : (
                                                                            <span className={`text-sm font-bold ${maxElig === 0 ? 'text-orange-600' : 'text-green-700'}`}>{maxElig ? formatCurrency(maxElig) : '—'}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {/* Custom header slot (status badges, claim status) — e.g. Available Offers */}
                                                                {renderOfferHeader && <div className="mt-2">{renderOfferHeader(offer)}</div>}
                                                                <div className="flex flex-column gap-1 mt-2">
                                                                    <Button label="View details" icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} outlined size="small" className="w-full" onClick={() => toggleRowExpand(offerId)} />
                                                                    {/* Context actions: Available Offers passes renderOfferFooter
                                                                        (Claim This Offer + Verify). When absent (wizard), show the
                                                                        Interested button. Edit shows whenever onEditOffer is given
                                                                        (both contexts). */}
                                                                    {renderOfferFooter
                                                                        ? renderOfferFooter(offer, 'cards')
                                                                        : (!readOnly && (
                                                                            isSelected
                                                                                ? <Tag value="Interested" icon="pi pi-check" severity="success" className="w-full justify-content-center" />
                                                                                : <Button label="Interested" icon="pi pi-heart" severity="success" size="small" className="w-full" onClick={() => handleSelectOffer(offer)} />
                                                                        ))}
                                                                    {!readOnly && onEditOffer && (
                                                                        <Button label="Edit" icon="pi pi-pencil" text size="small" className="w-full" onClick={() => handleEditOffer(offer, slot.originalIndex)} />
                                                                    )}
                                                                </div>
                                                                {/* Edited / Verified / Claimed-by attribution */}
                                                                {renderOfferAttribution(offer)}
                                                                {isExpanded && renderOfferDetails(offer, true)}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                </div>
                            </>
                        );
                    })()}
                </div>

            </div>
            )}

            {/* Mobile filter drawer */}
            {effectiveViewMode === 'cards' && isMobile && !hideFilters && (
                <Sidebar
                    visible={filterDrawerOpen}
                    onHide={() => setFilterDrawerOpen(false)}
                    position="left"
                    className="w-18rem"
                    header={<span className="text-base font-semibold">Filters</span>}
                    pt={{
                        // Make the drawer body fill the viewport height with internal scroll
                        // so all filter sections (List Type, Special Features, etc. at the
                        // bottom) are reachable. Default PrimeReact body has no overflow rule.
                        content: { style: { overflowY: 'auto', flex: 1, padding: '1rem' } },
                        root: { style: { display: 'flex', flexDirection: 'column' } },
                    }}
                >
                    <div className="flex flex-column gap-3 pb-5">
                        <div className="flex justify-content-between align-items-center">
                            <Button label="Clear All" text size="small" onClick={resetFilters} className="p-0 text-xs" />
                            <span className="text-xs text-600">{filteredOffers.length} of {offers.length} offers</span>
                        </div>
                        {onShareViaWhatsApp && (
                            <Button
                                label="Share via WhatsApp"
                                icon="pi pi-whatsapp"
                                size="small"
                                severity="success"
                                outlined
                                className="w-full"
                                onClick={() => { onShareViaWhatsApp(); setFilterDrawerOpen(false); }}
                            />
                        )}
                        <div className="surface-50 border-round p-2">
                            <div className="text-xs font-semibold text-700 uppercase mb-1">
                                <i className="pi pi-bolt text-primary text-xs mr-1"></i>Quick EMI
                            </div>
                            <InputNumber
                                value={filters.quickEmiBudget}
                                onValueChange={(e) => updateFilter('quickEmiBudget', e.value)}
                                placeholder="Max EMI"
                                mode="currency" currency="INR" locale="en-IN"
                                className="w-full"
                                inputClassName="w-full p-inputtext-sm"
                            />
                        </div>
                        {(() => {
                            const lenderCounts = new Map();
                            offers.forEach((o) => {
                                const name = o.bank_name || 'Unknown';
                                lenderCounts.set(name, (lenderCounts.get(name) || 0) + 1);
                            });
                            const uniqueLenders = Array.from(lenderCounts.keys()).sort((a, b) => a.localeCompare(b));
                            const allLendersSelected = !filters.selectedLenders || filters.selectedLenders.size === uniqueLenders.length;
                            return (
                                <div>
                                    <div className="flex justify-content-between align-items-center mb-2">
                                        <span className="text-xs font-semibold text-700 uppercase">Lenders</span>
                                        <Button
                                            label={allLendersSelected ? 'Clear' : 'Select all'}
                                            text
                                            size="small"
                                            onClick={() => updateFilter('selectedLenders', allLendersSelected ? new Set() : null)}
                                            className="p-0 text-xs"
                                        />
                                    </div>
                                    <div className="overflow-y-auto" style={{ maxHeight: '12rem' }}>
                                        {uniqueLenders.map((name) => {
                                            const checked = !filters.selectedLenders || filters.selectedLenders.has(name);
                                            const id = `om-lender-${name}`;
                                            return (
                                                <div key={name} className="flex align-items-center gap-2 mb-1 text-sm">
                                                    <Checkbox
                                                        inputId={id}
                                                        checked={checked}
                                                        onChange={() => toggleLender(name, uniqueLenders)}
                                                    />
                                                    <label htmlFor={id} className="flex-1 white-space-nowrap overflow-hidden text-overflow-ellipsis cursor-pointer" title={name}>{name}</label>
                                                    <span className="text-xs text-500">({lenderCounts.get(name)})</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                        <div>
                            <div className="text-xs font-semibold text-700 uppercase mb-2">Max Interest Rate</div>
                            <Slider
                                value={filters.maxInterestRate}
                                onChange={(e) => updateFilter('maxInterestRate', typeof e.value === 'number' ? e.value : 20)}
                                min={7}
                                max={20}
                                step={0.25}
                            />
                            <div className="flex justify-content-between text-xs text-600 mt-2">
                                <span>7%</span>
                                <strong className="text-primary">≤ {filters.maxInterestRate}%</strong>
                                <span>20%</span>
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold text-700 uppercase mb-2">Your CIBIL Score</div>
                            <InputText
                                value={filters.userCibil ?? ''}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                                    if (raw === '') return updateFilter('userCibil', null);
                                    const n = Number(raw);
                                    updateFilter('userCibil', Math.min(900, Math.max(0, n)));
                                }}
                                inputMode="numeric"
                                placeholder="e.g. 750"
                                className="w-full p-inputtext-sm"
                                maxLength={3}
                            />
                        </div>
                        <div>
                            <div className="text-xs font-semibold text-700 uppercase mb-2">List Type</div>
                            {[
                                { key: 'listed', label: 'Listed (your employer)' },
                                { key: 'unlisted', label: 'Unlisted' },
                            ].map((opt) => {
                                const id = `om-list-${opt.key}`;
                                return (
                                    <div key={opt.key} className="flex align-items-center gap-2 mb-1 text-sm">
                                        <Checkbox inputId={id} checked={filters.listType[opt.key]} onChange={() => toggleFilterChild('listType', opt.key)} />
                                        <label htmlFor={id} className="cursor-pointer">{opt.label}</label>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Verification (mobile overlay copy of the desktop sidebar filter) */}
                        {(() => {
                            const verifiedCount = offers.filter(o => o.is_verified).length;
                            const notVerifiedCount = offers.length - verifiedCount;
                            return (
                                <div>
                                    <div className="text-xs font-semibold text-700 uppercase mb-2">Verification</div>
                                    {[
                                        { key: 'verified', label: 'Verified', count: verifiedCount },
                                        { key: 'notVerified', label: 'Not Verified', count: notVerifiedCount },
                                    ].map((opt) => {
                                        const id = `om-verif-${opt.key}`;
                                        return (
                                            <div key={opt.key} className="flex align-items-center justify-content-between gap-2 mb-1 text-sm">
                                                <div className="flex align-items-center gap-2">
                                                    <Checkbox inputId={id} checked={filters.verification[opt.key]} onChange={() => toggleFilterChild('verification', opt.key)} />
                                                    <label htmlFor={id} className="cursor-pointer">{opt.label}</label>
                                                </div>
                                                <span className="text-xs text-500">({opt.count})</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                        <div>
                            <div className="text-xs font-semibold text-700 uppercase mb-2">Special Features</div>
                            {[
                                { key: 'noPreClosure', label: 'No Pre-closure charges' },
                                { key: 'zeroProcessingFee', label: 'Zero Processing Fee' },
                                { key: 'partPaymentAllowed', label: 'Part Payment Allowed' },
                                { key: 'bestRateOnly', label: 'Best Rate (top 3)' },
                            ].map((opt) => {
                                const id = `om-feat-${opt.key}`;
                                return (
                                    <div key={opt.key} className="flex align-items-center gap-2 mb-1 text-sm">
                                        <Checkbox inputId={id} checked={filters.features[opt.key]} onChange={() => toggleFilterChild('features', opt.key)} />
                                        <label htmlFor={id} className="cursor-pointer">{opt.label}</label>
                                    </div>
                                );
                            })}
                        </div>
                        <Button
                            label={`Show ${filteredOffers.length} offers`}
                            size="small"
                            className="w-full mt-2"
                            onClick={() => setFilterDrawerOpen(false)}
                        />
                    </div>
                </Sidebar>
            )}

            {/* Table view — now renders the BANK ACCORDION (Prototype A).
                Offers grouped by lender into expandable bank cards; each offer row
                shows the 5 key fields (ROI · EMI · Processing Fee · CIBIL · Max
                Eligibility) with Interested + Details actions, and a per-offer
                expand for the rest (pre-closure / part-payment / rate / conditions).
                Banks ordered by best ROI; pin a bank (max 2) to float it to top.
                Mobile is forced to cards view above; this branch is desktop-only. */}
            {effectiveViewMode === 'table' && (() => {
                return (
                    <div style={{ position: 'relative', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                        {/* Constant filter sidebar — same panel as Cards view */}
                        {!isMobile && !hideFilters && renderFilterSidebar()}

                        {/* Bank accordion (right side) */}
                        <div className="flex flex-column gap-3" style={{ flex: 1, minWidth: 0 }}>
                        {bankGroups.map((group) => {
                            const { bankName, slots, bestRoi, containsGlobalBest, sample } = group;
                            const open = isBankOpen(bankName);
                            const bankPinned = lockedBankNames.includes(bankName);
                            const canPinBank = lockedBankNames.length < MAX_LOCKED_BANKS || bankPinned;
                            const initials = bankName.replace(/[^A-Za-z ]/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'BK';
                            const fromRoi = Number.isFinite(bestRoi) ? `${bestRoi}%` : '—';

                            return (
                                <div
                                    key={bankName}
                                    className={`surface-card border-1 border-round overflow-hidden ${bankPinned ? 'border-primary' : 'surface-border'}`}
                                    style={bankPinned ? { boxShadow: '0 0 0 1px var(--primary-color)' } : {}}
                                >
                                    {/* Bank header */}
                                    <div
                                        className="flex align-items-center gap-3 p-3 cursor-pointer hover:surface-50"
                                        onClick={() => toggleBankOpen(bankName)}
                                    >
                                        <div className="flex align-items-center justify-content-center border-round font-bold text-blue-700 bg-blue-50 flex-shrink-0"
                                             style={{ width: '2.5rem', height: '2.5rem' }}>
                                            {initials}
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="flex align-items-center gap-2">
                                                <span className="font-bold text-900">{bankName}</span>
                                                {containsGlobalBest && <Tag severity="success" value="BEST BANK" className="text-xs" />}
                                            </div>
                                            <div className="text-xs text-600">
                                                {/* Category is per-offer (varies within a bank), so it's NOT shown
                                                    here — it appears on each offer row instead. */}
                                                {slots.length} offer{slots.length > 1 ? 's' : ''}
                                                {sample.list_type ? ` · ${sample.list_type === 'listed' ? 'Listed' : 'Unlisted'}` : ''}
                                            </div>
                                        </div>
                                        <div className="text-right mr-2">
                                            <div className="text-xs text-400 uppercase">from</div>
                                            <div className="text-lg font-bold text-blue-700">{fromRoi}</div>
                                        </div>
                                        <Button
                                            icon={bankPinned ? 'pi pi-lock' : 'pi pi-lock-open'}
                                            rounded text size="small"
                                            aria-label={bankPinned ? 'Unpin bank' : 'Pin bank'}
                                            onClick={(e) => { e.stopPropagation(); if (canPinBank) handleToggleBankLock(bankName); }}
                                            disabled={!canPinBank}
                                            tooltip={bankPinned ? 'Unpin this bank' : (canPinBank ? 'Pin this bank' : 'Unpin one first (max 2)')}
                                            tooltipOptions={{ position: 'top' }}
                                            className={bankPinned ? 'text-primary' : 'text-600'}
                                        />
                                        <i className={`pi ${open ? 'pi-chevron-down' : 'pi-chevron-right'} text-600`} />
                                    </div>

                                    {/* Bank body — offer rows */}
                                    {open && (
                                        <div className="border-top-1 surface-border">
                                            {slots.map((slot) => {
                                                const offer = mergeRecalc(slot.offer);
                                                // Unique per card (handles duplicate backend ids → no duplicate React keys)
                                                const offerId = slot._recalcKey || slot.offer_id;
                                                const isSelected = selectedOffer?.offer_id === offer.offer_id;
                                                const isBestOffer = slot.originalIndex === 0;
                                                const isExpanded = expandedOfferIds.has(offerId);

                                                let maxElig = offer.max_loan_eligibility;
                                                if ((maxElig === null || maxElig === undefined) && eligibilityParams && offer.emi_per_lac > 0) {
                                                    const salary = eligibilityParams.net_salary_per_month || 0;
                                                    const foir = offer.eligibility_breakdown?.foir || 50;
                                                    const existingEmi = eligibilityParams.total_existing_loan_emi || 0;
                                                    const available = Math.max(0, (salary * (foir / 100)) - existingEmi);
                                                    maxElig = Math.round((available / offer.emi_per_lac) * 100000);
                                                }
                                                // Front shows the borrower's entered CIBIL when given; else the lender's range.
                                                const cibil = enteredCibil != null ? String(enteredCibil) : cibilRangeText(offer);
                                                const note = roiCalcNote(offer);
                                                // Per-offer category (CAT A / CAT B …). Category varies PER OFFER
                                                // even within the same bank, so it's shown on each row — not just
                                                // the bank header. Skip generic/company-name values.
                                                const offerCat = offer.matched_category_name && offer.matched_category_name !== 'General' && offer.matched_category_name !== offer.company_name
                                                    ? offer.matched_category_name
                                                    : (offer.category && offer.category !== 'General' && offer.category !== offer.company_name ? offer.category : null);

                                                return (
                                                    <div
                                                        key={offerId}
                                                        className={`p-3 border-bottom-1 surface-border ${isBestOffer ? 'bg-green-50' : ''} ${isSelected ? 'bg-green-100' : ''}`}
                                                    >
                                                        {/* Company name is NOT shown on the row — it's in the Details expand. */}
                                                        {/* flex-nowrap (was flex-wrap) so the action buttons stay on the
                                                            SAME line as the fields on every row — not just the shortest one.
                                                            The fields grid gets minWidth:0 so it can shrink; long values
                                                            (e.g. the loan-amount band) wrap INSIDE their own cell instead of
                                                            pushing the buttons onto a second line. No data is dropped. */}
                                                        <div className="flex align-items-center gap-3 flex-nowrap">
                                                            <div className="flex flex-column align-items-start flex-shrink-0" style={{ minWidth: '4rem' }}>
                                                                <span className="font-bold text-600">#{offer.offer_number || (slot.originalIndex + 1)}</span>
                                                                {isBestOffer && <span className="text-xs font-bold text-green-700">BEST</span>}
                                                                {offerCat && <Tag value={offerCat} severity="info" className="text-xs mt-1" />}
                                                                {/* Method that calculated this offer (ABB / Net Profit / GST). */}
                                                                {offer.calculation_method && (
                                                                    <Tag
                                                                        value={offer.calculation_method_label
                                                                            || ({ ABB: 'ABB', NET_PROFIT: 'Net Profit', GST_TURNOVER: 'GST Turnover' }[offer.calculation_method] || offer.calculation_method)}
                                                                        severity="warning"
                                                                        className="text-xs mt-1"
                                                                    />
                                                                )}
                                                                {/* Below-minimum flag. In discovery mode (no amount
                                                                    requested) the engine still shows a slab the borrower
                                                                    MATCHED even when their eligibility can't reach the
                                                                    slab's minimum loan — pinned to the slab floor with
                                                                    is_eligible=false. Badge it so the DSA sees at a glance
                                                                    the borrower qualifies on criteria but not on amount. */}
                                                                {offer.is_eligible === false && !offer.loan_amount_provided && (
                                                                    <Tag
                                                                        value="Below min"
                                                                        severity="danger"
                                                                        icon="pi pi-exclamation-triangle"
                                                                        className="text-xs mt-1"
                                                                        tooltip="Borrower matches this range but their eligibility is below the slab's minimum loan"
                                                                    />
                                                                )}
                                                            </div>
                                                            <div className="flex-grow-1 grid" style={{ rowGap: '8px', minWidth: 0 }}>
                                                                <div className="col-6 md:col-2">
                                                                    {fieldCell('Interest p.a.', (
                                                                        <span className="flex flex-column">
                                                                            <span>{roiText(offer)}</span>
                                                                            {note && <span className="text-xs font-normal text-500">{note}</span>}
                                                                        </span>
                                                                    ), 'text-blue-700')}
                                                                    {/* Band-rate slider — drag to pick a rate; EMI / eligibility / cost recalc live.
                                                                        Breathing room above (mt-3) + a flex-column with a gap so the
                                                                        value, the slider track, and the rate input don't collide in
                                                                        this narrow column. */}
                                                                    {offer.roi_is_range && !readOnly && (
                                                                        <div className="mt-3 pr-2 flex flex-column gap-2">
                                                                            <Slider
                                                                                value={Number(offer.roi) || Number(offer.roi_min)}
                                                                                onChange={(e) => applyCardRoi(offer, e.value)}
                                                                                min={Number(offer.roi_min)}
                                                                                max={Number(offer.roi_max)}
                                                                                step={bandStep(offer.roi_min, offer.roi_max)}
                                                                                className="w-full my-1"
                                                                            />
                                                                            {/* Direct entry — type an exact rate inside the band */}
                                                                            <InputNumber
                                                                                value={Number(offer.roi) || Number(offer.roi_min)}
                                                                                onValueChange={(e) => {
                                                                                    const lo = Number(offer.roi_min);
                                                                                    const hi = Number(offer.roi_max);
                                                                                    let v = Number(e.value);
                                                                                    if (!Number.isFinite(v)) return;
                                                                                    v = Math.min(hi, Math.max(lo, v));
                                                                                    applyCardRoi(offer, v);
                                                                                }}
                                                                                min={Number(offer.roi_min)}
                                                                                max={Number(offer.roi_max)}
                                                                                minFractionDigits={0}
                                                                                maxFractionDigits={2}
                                                                                suffix="%"
                                                                                inputClassName="p-inputtext-sm text-center"
                                                                                inputStyle={{ width: '4.5rem', padding: '0.15rem 0.3rem' }}
                                                                                aria-label="Enter exact interest rate"
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="col-6 md:col-2">{fieldCell('EMI / mo', formatEmiDisplay(offer))}</div>
                                                                <div className="col-6 md:col-2">{(() => { const pf = formatProcessingFee(offer); return fieldCell('Processing Fee', (
                                                                    <span className="flex flex-column">
                                                                        <span>{pf.text}</span>
                                                                        {pf.note && <span className="text-xs font-normal text-500">{pf.note}</span>}
                                                                    </span>
                                                                )); })()}</div>
                                                                {/* Entered amount, else the lender's slab band — one or the other */}
                                                                <div className="col-6 md:col-2">{fieldCell('Loan Amount', loanAmountCellText(offer))}</div>
                                                                <div className="col-6 md:col-2">{fieldCell('CIBIL', cibil)}</div>
                                                                {/* ABB on the collapsed row for ABB-method offers: it is
                                                                    lender-specific (each rate card samples its own days),
                                                                    so it belongs next to that lender rather than only
                                                                    behind Details. */}
                                                                {(offer.abb_summary?.abb > 0 || offer.abb_value_used > 0) && (
                                                                    <div className="col-6 md:col-2">{fieldCell('ABB', (
                                                                        <span className="flex flex-column">
                                                                            <span>{formatCurrency(offer.abb_summary?.abb || offer.abb_value_used)}</span>
                                                                            {/* Manual-entry offers have no per-lender figure to
                                                                                show, so say what would produce one instead of
                                                                                implying these dates were applied. */}
                                                                            <span className="text-xs font-normal text-500">
                                                                                {abbDatesCaption(offer)}
                                                                            </span>
                                                                        </span>
                                                                    ), offer.abb_source === 'manual_entry' ? 'text-600' : 'text-teal-700')}</div>
                                                                )}
                                                                <div className="col-6 md:col-2">
                                                                    {/* Max Eligibility — clickable to open the recalculatable
                                                                        Eligibility Breakdown dialog (same as Cards view). */}
                                                                    <div className="flex flex-column">
                                                                        <span className="text-xs text-400 uppercase" style={{ letterSpacing: '0.03em' }}>Max Eligibility</span>
                                                                        {(offer.eligibility_breakdown || eligibilityParams) ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleViewEligibilityBreakdown(offer)}
                                                                                className="p-0 border-none bg-transparent cursor-pointer text-left"
                                                                                title="Click to view / recalculate eligibility"
                                                                            >
                                                                                <span className={`text-sm font-bold inline-flex align-items-center gap-1 ${maxElig === 0 ? 'text-orange-600' : 'text-green-700'}`}>
                                                                                    {maxElig ? formatCurrency(maxElig) : '—'}
                                                                                    <i className="pi pi-external-link text-xs text-600" aria-hidden="true"></i>
                                                                                </span>
                                                                            </button>
                                                                        ) : (
                                                                            <span className={`text-sm font-bold ${maxElig === 0 ? 'text-orange-600' : 'text-green-700'}`}>{maxElig ? formatCurrency(maxElig) : '—'}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex align-items-center gap-2 flex-shrink-0">
                                                                <Button
                                                                    label="Details"
                                                                    icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                                                                    outlined size="small"
                                                                    onClick={() => toggleRowExpand(offerId)}
                                                                />
                                                                {/* Inline quick-actions only when NO custom footer is supplied
                                                                    (wizard: Interested + Edit). When a custom footer IS
                                                                    supplied it may be a full-width panel (e.g. the claims
                                                                    list), so it's rendered below the row at full width. */}
                                                                {!renderOfferFooter && !readOnly && (
                                                                    isSelected
                                                                        ? <Tag value="Interested" icon="pi pi-check" severity="success" />
                                                                        : <Button label="Interested" icon="pi pi-heart" severity="success" size="small" onClick={() => handleSelectOffer(offer)} />
                                                                )}
                                                                {!readOnly && onEditOffer && (
                                                                    <Button icon="pi pi-pencil" rounded text size="small" aria-label="Edit offer" onClick={() => handleEditOffer(offer, slot.originalIndex)} tooltip="Edit offer" tooltipOptions={{ position: 'top' }} />
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* Custom header slot (claim badges / status) — full width below
                                                            the field row so it doesn't crowd the action buttons. */}
                                                        {renderOfferHeader && (
                                                            <div className="mt-2">{renderOfferHeader(offer)}</div>
                                                        )}
                                                        {/* Custom footer (Available Offers: Claim+Verify buttons;
                                                            Claims page: full claims panel) — full width below the row. */}
                                                        {renderOfferFooter && (
                                                            <div className="mt-2">{renderOfferFooter(offer, 'table')}</div>
                                                        )}
                                                        {/* Edited / Verified / Claimed-by attribution */}
                                                        {renderOfferAttribution(offer)}
                                                        {isExpanded && renderOfferDetails(offer)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        </div>
                    </div>
                );
            })()}

            {/* Details Dialog */}
            <Dialog
                visible={detailsDialogVisible}
                onHide={() => setDetailsDialogVisible(false)}
                header={`Offer Details - ${selectedOfferDetails?.bank_name || 'Offer'}`}
                footer={detailsDialogFooter}
                style={{ width: '90vw', maxWidth: '800px' }}
                maximizable
            >
                {selectedOfferDetails && (
                    <div className="flex flex-column gap-2">
                        {/* Calculation Details */}
                        {selectedOfferDetails.offer_details && (
                            <Accordion>
                                <AccordionTab header={<span><i className="pi pi-calculator mr-2"></i>Calculation Details</span>}>
                                    <div className="flex flex-column gap-1" style={{ fontSize: '0.75rem' }}>
                                        {selectedOfferDetails.offer_details.eligibility_formula && (
                                            <div className="p-2 surface-100 border-round">
                                                <div className="text-color-secondary font-medium mb-1" style={{ fontSize: '0.7rem' }}>Eligibility Formula:</div>
                                                <div className="font-mono" style={{ fontSize: '0.65rem' }}>{selectedOfferDetails.offer_details.eligibility_formula}</div>
                                            </div>
                                        )}
                                        {selectedOfferDetails.offer_details.emi_formula && (
                                            <div className="p-2 surface-100 border-round">
                                                <div className="text-color-secondary font-medium mb-1" style={{ fontSize: '0.7rem' }}>EMI Formula:</div>
                                                <div className="font-mono" style={{ fontSize: '0.65rem' }}>{selectedOfferDetails.offer_details.emi_formula}</div>
                                            </div>
                                        )}
                                        {selectedOfferDetails.offer_details.pf_formula && (
                                            <div className="p-2 surface-100 border-round">
                                                <div className="text-color-secondary font-medium mb-1" style={{ fontSize: '0.7rem' }}>Processing Fee Formula:</div>
                                                <div className="font-mono" style={{ fontSize: '0.65rem' }}>{selectedOfferDetails.offer_details.pf_formula}</div>
                                            </div>
                                        )}
                                        {selectedOfferDetails.offer_details.total_interest_formula && (
                                            <div className="p-2 surface-100 border-round">
                                                <div className="text-color-secondary font-medium mb-1" style={{ fontSize: '0.7rem' }}>Total Interest Formula:</div>
                                                <div className="font-mono" style={{ fontSize: '0.65rem' }}>{selectedOfferDetails.offer_details.total_interest_formula}</div>
                                            </div>
                                        )}
                                        {selectedOfferDetails.offer_details.total_cost_formula && (
                                            <div className="p-2 surface-100 border-round">
                                                <div className="text-color-secondary font-medium mb-1" style={{ fontSize: '0.7rem' }}>Total Cost Formula:</div>
                                                <div className="font-mono" style={{ fontSize: '0.65rem' }}>{selectedOfferDetails.offer_details.total_cost_formula}</div>
                                            </div>
                                        )}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        )}

                        {/* Additional Info */}
                        <div className="surface-50 p-2 border-round">
                            <h6 className="mt-0 mb-1" style={{ fontSize: '0.8rem' }}>Summary</h6>
                            <div className="grid" style={{ fontSize: '0.75rem' }}>
                                <div className="col-6">
                                    <div className="text-color-secondary mb-1" style={{ fontSize: '0.7rem' }}>Bank:</div>
                                    <div className="font-bold">{selectedOfferDetails.bank_name}</div>
                                </div>
                                {selectedOfferDetails.product_name && (
                                    <div className="col-6">
                                        <div className="text-color-secondary mb-1" style={{ fontSize: '0.7rem' }}>Product:</div>
                                        <div className="font-bold">{selectedOfferDetails.product_name}</div>
                                    </div>
                                )}
                                <div className="col-6">
                                    <div className="text-color-secondary mb-1" style={{ fontSize: '0.7rem' }}>EMI:</div>
                                    <div className="font-bold text-blue-600">{formatCurrency(selectedOfferDetails.emi)}</div>
                                </div>
                                <div className="col-6">
                                    <div className="text-color-secondary mb-1" style={{ fontSize: '0.7rem' }}>Total Cost:</div>
                                    <div className="font-bold text-orange-600">{formatCurrency(selectedOfferDetails.total_cost)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Eligibility Breakdown Dialog - Editable.
                 ROI/FOIR editor section is rendered just above "Multipliers (From Range)" below.
                 When the offer has a ROI band (roi_is_range), the entered ROI MUST be inside
                 [roi_min, roi_max]. Outside values show a red inline error and disable the
                 Recalculate button (validity computed inside the footer prop below). */}
            <Dialog
                visible={eligibilityDialogVisible}
                onHide={() => setEligibilityDialogVisible(false)}
                header={
                    <div className="flex align-items-center" style={{ fontSize: '0.85rem' }}>
                        <i className="pi pi-calculator mr-2"></i>
                        Eligibility Breakdown - {editableBreakdown?.bank_name || 'Offer'}
                        {isEdited && <Tag value="Modified" severity="warning" className="ml-2" />}
                    </div>
                }
                footer={(() => {
                    // Compute validity here so both buttons + inline error use the same logic
                    const bd = editableBreakdown || {};
                    const roiVal = Number(bd.roi);
                    const foirVal = Number(bd.foir);
                    const tenureVal = Number(bd.tenure || bd.tenure_months);
                    const minTen = Number(bd.min_tenure) || 0;
                    const maxTen = Number(bd.max_tenure) || 0;
                    const roiBandInvalid = bd.roi_is_range
                        && (roiVal < Number(bd.roi_min) || roiVal > Number(bd.roi_max));
                    const roiInvalid = !roiVal || roiVal <= 0 || roiVal > 100 || roiBandInvalid;
                    const foirInvalid = !foirVal || foirVal <= 0 || foirVal > 100;
                    // Tenure must be > 0 and (when the range exposes bounds) inside [min, max].
                    // 600 months hard cap matches the global default fallback in BaseEligibilityService.
                    const tenureBandInvalid = (minTen > 0 && tenureVal < minTen)
                        || (maxTen > 0 && tenureVal > maxTen);
                    const tenureInvalid = !tenureVal || tenureVal <= 0 || tenureVal > 600 || tenureBandInvalid;
                    const canRecalc = !roiInvalid && !foirInvalid && !tenureInvalid;
                    return (
                        <div className="flex justify-content-between">
                            <div>
                                {isEdited && (
                                    <Button label="Reset" icon="pi pi-undo" onClick={resetBreakdown} className="p-button-text p-button-secondary p-button-sm" />
                                )}
                            </div>
                            <div className="flex gap-1">
                                <Button
                                    label="Recalculate"
                                    icon="pi pi-refresh"
                                    onClick={recalculateEligibility}
                                    disabled={!canRecalc}
                                    tooltip={!canRecalc ? 'Fix ROI / FOIR / Tenure value before recalculating' : undefined}
                                    tooltipOptions={{ position: 'top' }}
                                    className="p-button-outlined p-button-sm"
                                />
                                <Button label="Close" icon="pi pi-times" onClick={() => setEligibilityDialogVisible(false)} className="p-button-text p-button-sm" />
                            </div>
                        </div>
                    );
                })()}
                style={{ width: '95vw', maxWidth: '750px' }}
                maximizable
            >
                {editableBreakdown && (
                    <div className="flex flex-column gap-2">
                        {/* Summary Card — sticky so the headline figures (Max Eligibility,
                            ROI, FOIR, EMI/Lac, EMI/mo, interest, cost) stay visible while the
                            user scrolls the long month-by-month ABB table below. order:-1 keeps
                            it first even though the ABB block uses order:99. Solid background +
                            z-index so scrolled content passes cleanly underneath. */}
                        <div
                            className="surface-100 p-2 border-round"
                            style={{ position: 'sticky', top: 0, zIndex: 5, order: -1, background: 'var(--surface-100)' }}
                        >
                            {/* Single compact row of headline stats — all figures on ONE line
                                so the sticky header stays short. Each stat is a tight flex item
                                (label above value); dividers separate them. EMI/Interest/Cost
                                only appear when a loan amount is entered. Horizontal scroll on
                                very narrow widths rather than wrapping to a second row. */}
                            {(() => {
                                const tenureUsed = Number(editableBreakdown.tenure || editableBreakdown.tenure_months) || 0;
                                const isAutoFilledMin = tenureUsed
                                    && !editableBreakdown.tenure_input_filled
                                    && Number(editableBreakdown.min_tenure) === tenureUsed;
                                const hasLoan = Number(editableBreakdown.loan_amount) > 0 && Number(editableBreakdown.emi) > 0;
                                const stat = (label, value, valueClass, sub) => (
                                    <div className="text-center px-2" style={{ flex: '1 1 0', minWidth: '5.5rem' }}>
                                        <div className="text-color-secondary mb-1 white-space-nowrap" style={{ fontSize: '0.6rem' }}>{label}</div>
                                        <div className={`font-bold white-space-nowrap ${valueClass}`} style={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
                                        {sub && <div className="text-color-secondary mt-1" style={{ fontSize: '0.55rem' }}>{sub}</div>}
                                    </div>
                                );
                                const divider = <div style={{ width: '1px', alignSelf: 'stretch', background: 'var(--surface-border)' }} />;
                                return (
                                    <div className="flex align-items-center" style={{ gap: '2px', overflowX: 'auto' }}>
                                        {stat('Max Eligibility', formatCurrency(editableBreakdown.eligible_amount), isEdited ? 'text-orange-600' : 'text-green-600',
                                            isEdited && selectedEligibilityBreakdown && editableBreakdown.eligible_amount !== selectedEligibilityBreakdown.eligible_amount
                                                ? <span className="line-through">{formatCurrency(selectedEligibilityBreakdown.eligible_amount)}</span> : null)}
                                        {divider}
                                        {stat('ROI', `${editableBreakdown.roi}%`, 'text-blue-600')}
                                        {divider}
                                        {stat('FOIR', `${editableBreakdown.foir}%`, 'text-orange-600')}
                                        {divider}
                                        {stat('EMI per Lac', `₹${editableBreakdown.emi_per_lac}`, 'text-purple-600',
                                            tenureUsed ? `@ ${tenureUsed} mo${isAutoFilledMin ? ' (min)' : ''}` : null)}
                                        {divider}
                                        {stat(String(editableBreakdown.calculation_method || '').toUpperCase() === 'ABB' ? 'Monthly Income' : 'Total Income',
                                            formatCurrency(editableBreakdown.total_income), 'text-900')}
                                        {divider}
                                        {stat(`After FOIR (${editableBreakdown.foir}%)`, formatCurrency(editableBreakdown.income_after_foir), 'text-900')}
                                        {divider}
                                        {stat('Obligations', formatCurrency(editableBreakdown.total_obligations),
                                            Number(editableBreakdown.total_obligations) > 0 ? 'text-red-500' : 'text-color-secondary')}
                                        {divider}
                                        {stat('Available for EMI', formatCurrency(editableBreakdown.available_for_emi), 'text-green-600')}
                                        {hasLoan && divider}
                                        {hasLoan && stat('EMI / mo', formatCurrency(editableBreakdown.emi), isEdited ? 'text-orange-600' : 'text-900')}
                                        {hasLoan && divider}
                                        {hasLoan && stat('Total Interest', formatCurrency(editableBreakdown.total_interest), 'text-900')}
                                        {hasLoan && divider}
                                        {hasLoan && stat('Total Cost', formatCurrency(editableBreakdown.total_cost), 'text-primary')}
                                    </div>
                                );
                            })()}

                            {/* Eligibility as per Multiplier — ALWAYS shown so the user can
                                see (and edit) the multiplier even when the lender's CSV doesn't
                                supply one. The CSV-derived value is the seed; editing it triggers
                                a fresh Multiplier Eligibility figure on Recalculate.

                                Formula: (Total Income − Total Obligations) × Multiplier
                                When multiplier = 0 (no CSV value AND user hasn't entered one),
                                the right-hand column shows "—" and prompts the user. */}
                            <div className="mt-2 pt-2 border-top-1 surface-border">
                                {/* Header row — click to expand/collapse the (optional) multiplier
                                    editor. The label, chevron and the resulting figure stay visible
                                    when collapsed; only the editable input is hidden. */}
                                <div
                                    className="flex justify-content-between align-items-center mb-1 cursor-pointer"
                                    onClick={() => setMultiplierExpanded((v) => !v)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMultiplierExpanded((v) => !v); } }}
                                    aria-expanded={multiplierExpanded}
                                >
                                    <div className="text-color-secondary font-semibold flex align-items-center" style={{ fontSize: '0.7rem' }}>
                                        <i className={`pi ${multiplierExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} mr-1`} style={{ fontSize: '0.6rem' }}></i>
                                        <i className="pi pi-sliders-h text-blue-500 mr-1" style={{ fontSize: '0.65rem' }}></i>
                                        Max Eligibility (Multiplier)
                                    </div>
                                    <div className={`font-bold ${isEdited ? 'text-orange-600' : 'text-blue-600'}`} style={{ fontSize: '0.95rem' }}>
                                        {Number(editableBreakdown.netsalarymultiple) > 0
                                            ? formatCurrency(editableBreakdown.eligibility_as_per_multiplier)
                                            : <span className="text-400">—</span>}
                                    </div>
                                </div>
                                {/* Editable multiplier — seeded from lender CSV (range.netsalarymultiple).
                                    Common values: 6× / 10× / 12× / 18× / 24× of net monthly income.
                                    Allow 0–60× so banks with very generous multipliers are accommodated.
                                    Hidden until the header above is expanded. */}
                                {multiplierExpanded && (
                                <div className="flex align-items-center gap-2">
                                    <label className="text-color-secondary white-space-nowrap" style={{ fontSize: '0.65rem' }}>
                                        Multiplier (× net income):
                                    </label>
                                    <InputNumber
                                        value={editableBreakdown.netsalarymultiple}
                                        onValueChange={(e) => updateEligibilityField('netsalarymultiple', e.value)}
                                        mode="decimal"
                                        minFractionDigits={0}
                                        maxFractionDigits={2}
                                        min={0}
                                        max={60}
                                        step={1}
                                        suffix="×"
                                        showButtons
                                        buttonLayout="horizontal"
                                        decrementButtonClassName="p-button-text p-button-sm"
                                        incrementButtonClassName="p-button-text p-button-sm"
                                        className="p-inputtext-sm"
                                        inputClassName="text-center font-bold text-blue-600 p-1"
                                        inputStyle={{ fontSize: '0.8rem', width: '5rem' }}
                                    />
                                    {Number(editableBreakdown.netsalarymultiple) > 0
                                        ? (
                                            <span className="text-color-secondary text-xs" style={{ fontSize: '0.65rem' }}>
                                                = (Income − Obligations) × {editableBreakdown.netsalarymultiple}
                                                {selectedEligibilityBreakdown
                                                    && Number(selectedEligibilityBreakdown.netsalarymultiple) > 0
                                                    && Number(selectedEligibilityBreakdown.netsalarymultiple) !== Number(editableBreakdown.netsalarymultiple)
                                                    && (
                                                        <span className="text-orange-600 ml-2">
                                                            (CSV: {selectedEligibilityBreakdown.netsalarymultiple}×)
                                                        </span>
                                                    )}
                                            </span>
                                        )
                                        : (
                                            <span className="text-color-secondary text-xs" style={{ fontSize: '0.65rem' }}>
                                                Not set by lender — enter a value to compute
                                            </span>
                                        )}
                                </div>
                                )}
                            </div>
                        </div>

                        {/* How it's calculated — the figures are in the sticky header above;
                            this section keeps the formula + the tenure note. */}
                        <div className="surface-50 p-2 border-round">
                            {/* Clickable header — toggles the formula/Net-Income detail. The
                                tenure note below stays visible either way. */}
                            <h6
                                className="mt-0 mb-2 flex align-items-center cursor-pointer"
                                style={{ fontSize: '0.8rem' }}
                                onClick={() => setFormulaExpanded((v) => !v)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFormulaExpanded((v) => !v); } }}
                                aria-expanded={formulaExpanded}
                            >
                                <i className={`pi ${formulaExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} mr-1 text-color-secondary`} style={{ fontSize: '0.6rem' }}></i>
                                <i className="pi pi-calculator mr-2 text-blue-500" style={{ fontSize: '0.75rem' }}></i>
                                How it's calculated
                            </h6>
                            {formulaExpanded && (
                            <>
                            {(() => {
                                // The headline figures (Monthly Income, Income after FOIR,
                                // Obligations, Available for EMI) now live in the sticky summary
                                // row above, so we don't repeat them here. We keep only the
                                // Net Income line when it adds information (obligations > 0, so it
                                // differs from Total Income), plus the formula box below.
                                const obligations = Number(editableBreakdown.total_obligations) || 0;
                                const netIncome = Number(editableBreakdown.total_income) - obligations;
                                if (obligations <= 0) return null;
                                return (
                                    <div className="mb-1" style={{ fontSize: '0.75rem' }}>
                                        <div className="text-color-secondary mb-1" style={{ fontSize: '0.7rem' }}>Net Income (Income − Obligations)</div>
                                        <div className="font-bold text-blue-600">{formatCurrency(netIncome)}</div>
                                    </div>
                                );
                            })()}
                            <div className="mt-2 p-2 surface-100 border-round font-mono" style={{ fontSize: '0.6rem' }}>
                                <strong>FOIR-based:</strong><br/>
                                &nbsp;&nbsp;(Total Income × FOIR%) − Obligations = Available EMI<br/>
                                &nbsp;&nbsp;Max Eligibility (FOIR) = (Available EMI / EMI per Lac) × 1,00,000
                                <br/><br/>
                                <strong>Multiplier-based:</strong><br/>
                                &nbsp;&nbsp;Max Eligibility (Multiplier) = (Total Income − Obligations) × Multiplier
                                {Number(editableBreakdown.netsalarymultiple) > 0 && (
                                    <span> = {formatCurrency(editableBreakdown.total_income - editableBreakdown.total_obligations)} × {editableBreakdown.netsalarymultiple} = <strong>{formatCurrency(editableBreakdown.eligibility_as_per_multiplier)}</strong></span>
                                )}
                                <br/><br/>
                                <span className="text-color-secondary">Lenders typically sanction the LOWER of the two — pick the more conservative number.</span>
                            </div>
                            </>
                            )}
                            {/* Tenure hint — the EMI per Lac (and therefore Max Eligibility) depends on tenure.
                                In discovery mode (no tenure entered by borrower) the engine falls back to the
                                range's MIN tenure, which produces the smallest possible eligibility figure.
                                Showing the borrower exactly which tenure was used here removes the surprise. */}
                            {(() => {
                                const tenureUsed = Number(editableBreakdown.tenure || editableBreakdown.tenure_months) || 0;
                                const minTenure = Number(editableBreakdown.min_tenure) || 0;
                                const maxTenure = Number(editableBreakdown.max_tenure) || 0;
                                if (!tenureUsed) return null;
                                // Three states:
                                //   1. autoFilledMin — borrower didn't enter a tenure on the lead AND the
                                //      user hasn't edited it in this dialog yet (still at the bank's min).
                                //   2. fromLead     — borrower entered a tenure on the lead (engine used it).
                                //   3. userEdited   — user has typed/sliders a new tenure in this dialog
                                //      (isEdited === true AND value differs from the seeded one).
                                const autoFilledMin = !editableBreakdown.tenure_input_filled
                                    && Number(selectedEligibilityBreakdown?.tenure) === tenureUsed;
                                const userEdited = isEdited
                                    && Number(selectedEligibilityBreakdown?.tenure) !== tenureUsed;
                                return (
                                    <div className="mt-2 p-2 border-round flex align-items-start gap-2" style={{ fontSize: '0.7rem', backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}>
                                        <i className="pi pi-info-circle text-orange-500 mt-1" style={{ fontSize: '0.75rem' }} />
                                        <div className="flex-1">
                                            <div className="font-semibold text-900 mb-1">
                                                Calculated using <span className="text-orange-700">tenure = {tenureUsed} months</span>
                                            </div>
                                            <div className="text-color-secondary">
                                                {autoFilledMin ? (
                                                    <>
                                                        No tenure entered on the lead — using this bank's <strong>minimum tenure</strong> ({minTenure} months){maxTenure > minTenure ? <> out of the allowed band {minTenure}–{maxTenure} months</> : null}.
                                                        Longer tenure → lower EMI per Lac → <strong>higher Max Eligibility</strong>.
                                                        Use the <strong>Tenure</strong> field below to try a different tenure, then click <strong>Recalculate</strong>.
                                                    </>
                                                ) : userEdited ? (
                                                    <>
                                                        Recalculated using your entered tenure of {tenureUsed} months.
                                                        EMI per Lac = PMT on ₹1,00,000 at {editableBreakdown.roi}% for {tenureUsed} months.
                                                    </>
                                                ) : (
                                                    <>
                                                        Using the tenure you entered on the lead. EMI per Lac is calculated on ₹1,00,000 at {editableBreakdown.roi}% for {tenureUsed} months.
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* ────────────────────────────────────────────────────────────
                            ABB CALCULATION — full per-lender breakdown (ABB method only).
                            Shows HOW this lender arrived at its ABB: the month-by-month
                            sampling with each date aligned to its closing balance, then the
                            overall average. Two lenders sampling the same statement on
                            different dates legitimately differ, so this belongs per-offer.
                            Data source: offer.abb_summary (monthly_abbs / warnings), seeded
                            into the breakdown when the dialog opened. Gated to ABB so it
                            never appears for Salary / GST / Net-Profit offers.
                            ──────────────────────────────────────────────────────────── */}
                        {String(editableBreakdown.calculation_method || '').toUpperCase() === 'ABB'
                            && editableBreakdown.abb_summary
                            && Number(editableBreakdown.abb_summary.abb) > 0
                            && (() => {
                                const abb = editableBreakdown.abb_summary;
                                const isAllDays = abb.mode === 'all_calendar_days';
                                const monthly = Array.isArray(abb.monthly_abbs) ? abb.monthly_abbs : [];
                                const warnings = Array.isArray(abb.warnings) ? abb.warnings : [];
                                const isManual = editableBreakdown.abb_source === 'manual_entry';
                                // Pretty month label: "2025-01" → "Jan 2025".
                                const monthLabel = (ym) => {
                                    const m = String(ym || '').match(/^(\d{4})-(\d{2})$/);
                                    if (!m) return ym || '—';
                                    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                    return `${names[Number(m[2]) - 1] || m[2]} ${m[1]}`;
                                };
                                return (
                                    // order:10 places this AFTER the summary/formula (order 0)
                                    // but BEFORE the Applicants panel (order 20) — so the reader
                                    // sees the result, then how the ABB was built, then the
                                    // applicant income/obligations detail. No JSX reshuffle needed.
                                    <div className="surface-50 p-2 border-round" style={{ order: 10 }}>
                                        <h6 className="mt-0 mb-1 flex align-items-center" style={{ fontSize: '0.8rem' }}>
                                            <i className="pi pi-building mr-2 text-purple-500" style={{ fontSize: '0.75rem' }}></i>
                                            ABB Calculation — {editableBreakdown.bank_name || 'this lender'}
                                        </h6>
                                        <div className="text-color-secondary mb-2" style={{ fontSize: '0.68rem' }}>
                                            {isManual
                                                ? 'ABB was entered manually — no statement sampling to show.'
                                                : isAllDays
                                                    ? <>This lender did not specify sampling dates, so the ABB is the average of the closing balance on <strong>every calendar day</strong> of the statement (day-weighted across all months).</>
                                                    : <>This lender samples the closing balance on <strong>fixed dates each month</strong>: <span className="text-purple-700 font-semibold">{abb.dates_display || (String(editableBreakdown.abb_dates_raw ?? '').trim() ? String(editableBreakdown.abb_dates_raw).split(',').map((d) => ordinalDay(d.trim())).filter(Boolean).join(', ') : '—')}</span>. Another lender on different dates would show a different ABB.</>}
                                            {!isManual && (
                                                <div className="mt-1" style={{ fontSize: '0.62rem' }}>
                                                    <i className="pi pi-pencil text-blue-500 mr-1" style={{ fontSize: '0.55rem' }}></i>
                                                    {isAllDays
                                                        ? <>Each <strong>Month ABB</strong> below is editable — the Overall ABB and eligibility recompute live.</>
                                                        : <>Any closing balance below is <strong>editable</strong> — the month ABB, Overall ABB and eligibility recompute live.</>}
                                                </div>
                                            )}
                                        </div>

                                        {/* Month-wise dates → balances table (selected-dates mode). */}
                                        {!isManual && !isAllDays && monthly.length > 0 && (
                                            <div className="border-round overflow-hidden" style={{ border: '1px solid var(--surface-border)' }}>
                                                {/* Header row */}
                                                <div className="flex surface-200 font-semibold text-color-secondary" style={{ fontSize: '0.62rem' }}>
                                                    <div className="p-2" style={{ width: '6rem', flexShrink: 0 }}>Month</div>
                                                    <div className="p-2 flex-1">Sampled dates → closing balance</div>
                                                    <div className="p-2 text-right" style={{ width: '7rem', flexShrink: 0 }}>Month ABB</div>
                                                </div>
                                                {/* One row per month. We render one cell PER CONFIGURED
                                                    date (requested_dates) so every month lines up on the
                                                    same columns; a date that had no posting shows a muted
                                                    "no balance" input the DSA can still fill in. */}
                                                {monthly.map((m, mi) => {
                                                    const used = Array.isArray(m.dates_used) ? m.dates_used : [];
                                                    const bals = Array.isArray(m.picked_balances) ? m.picked_balances : [];
                                                    // Configured sampling days for this lender — prefer the
                                                    // month's own requested_dates, else the summary's.
                                                    const configured = (Array.isArray(m.requested_dates) && m.requested_dates.length)
                                                        ? m.requested_dates
                                                        : (String(editableBreakdown.abb_dates_raw ?? '').split(',').map((x) => parseInt(x, 10)).filter(Boolean));
                                                    const cells = configured.map((day) => {
                                                        const at = used.indexOf(day);
                                                        return { day, has: at >= 0, balance: at >= 0 ? bals[at] : null };
                                                    });
                                                    return (
                                                        <div
                                                            key={m.month || mi}
                                                            className="flex align-items-center"
                                                            style={{ fontSize: '0.66rem', borderTop: '1px solid var(--surface-border)', background: mi % 2 ? 'var(--surface-0)' : 'transparent' }}
                                                        >
                                                            <div className="p-2 font-medium text-900" style={{ width: '6rem', flexShrink: 0 }}>{monthLabel(m.month)}</div>
                                                            <div className="p-2 flex-1">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {cells.map((c) => (
                                                                        <span key={c.day} className="inline-flex align-items-center border-round px-1 py-1" style={{ background: 'var(--surface-100)', whiteSpace: 'nowrap' }}>
                                                                            <span className="text-500 mr-1">{ordinalDay(c.day)}</span>
                                                                            {/* Editable closing balance — edit → month ABB, overall
                                                                                ABB and eligibility recompute live (ABB method,
                                                                                what-if, not persisted). A skipped date shows an
                                                                                empty "no balance" placeholder, still editable. */}
                                                                            <InputNumber
                                                                                value={c.has ? c.balance : null}
                                                                                onValueChange={(e) => updateAbbBalance(mi, c.day, e.value ?? 0)}
                                                                                mode="currency"
                                                                                currency="INR"
                                                                                locale="en-IN"
                                                                                maxFractionDigits={0}
                                                                                placeholder="no balance"
                                                                                inputClassName={`text-right p-1 ${c.has ? 'font-semibold text-900' : 'text-400'}`}
                                                                                inputStyle={{ width: '6.5rem', fontSize: '0.66rem', fontVariantNumeric: 'tabular-nums' }}
                                                                            />
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            <div className="p-2 text-right font-bold text-teal-700" style={{ width: '7rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(m.monthly_abb)}</div>
                                                        </div>
                                                    );
                                                })}
                                                {/* Overall ABB footer */}
                                                <div className="flex align-items-center surface-100 font-bold" style={{ fontSize: '0.68rem', borderTop: '2px solid var(--surface-border)' }}>
                                                    <div className="p-2 text-900" style={{ flex: 1 }}>
                                                        Overall ABB = average of {abb.months_count || monthly.length} monthly average{(abb.months_count || monthly.length) === 1 ? '' : 's'}
                                                    </div>
                                                    <div className="p-2 text-right text-teal-700" style={{ width: '7rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(abb.abb)}</div>
                                                </div>
                                            </div>
                                        )}

                                        {/* All-calendar-days mode (blank abb_dates in the CSV): the
                                            lender averages EVERY calendar day, so there are no fixed
                                            sampling dates to line up. We show a month-wise table with
                                            each month's day-count and its average balance; the MONTH
                                            ABB is editable (editing 30 individual days would be
                                            unusable). The overall ABB stays DAY-WEIGHTED —
                                            Σ(monthABB × days) ÷ Σ(days) — matching the engine's pooled
                                            average, not a simple mean of the monthly figures. */}
                                        {!isManual && isAllDays && monthly.length > 0 && (
                                            <div className="border-round overflow-hidden" style={{ border: '1px solid var(--surface-border)' }}>
                                                <div className="flex surface-200 font-semibold text-color-secondary" style={{ fontSize: '0.62rem' }}>
                                                    <div className="p-2" style={{ width: '7rem', flexShrink: 0 }}>Month</div>
                                                    <div className="p-2 flex-1">Days averaged</div>
                                                    <div className="p-2 text-right" style={{ width: '9rem', flexShrink: 0 }}>Month ABB (editable)</div>
                                                </div>
                                                {monthly.map((m, mi) => (
                                                    <div key={m.month || mi} className="flex align-items-center" style={{ fontSize: '0.66rem', borderTop: '1px solid var(--surface-border)', background: mi % 2 ? 'var(--surface-0)' : 'transparent' }}>
                                                        <div className="p-2 font-medium text-900" style={{ width: '7rem', flexShrink: 0 }}>{monthLabel(m.month)}</div>
                                                        <div className="p-2 flex-1 text-color-secondary">{m.days_counted || 0} day{(m.days_counted || 0) === 1 ? '' : 's'} (every calendar day)</div>
                                                        <div className="p-2 text-right" style={{ width: '9rem', flexShrink: 0 }}>
                                                            <InputNumber
                                                                value={m.monthly_abb ?? 0}
                                                                onValueChange={(e) => updateAbbMonthlyAllDays(mi, e.value ?? 0)}
                                                                mode="currency" currency="INR" locale="en-IN" maxFractionDigits={0}
                                                                inputClassName="text-right font-semibold text-teal-700 p-1"
                                                                inputStyle={{ width: '8rem', fontSize: '0.66rem', fontVariantNumeric: 'tabular-nums' }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                                {/* Day-weighted overall footer */}
                                                <div className="flex align-items-center surface-100 font-bold" style={{ fontSize: '0.68rem', borderTop: '2px solid var(--surface-border)' }}>
                                                    <div className="p-2 text-900" style={{ flex: 1 }}>
                                                        Overall ABB = Σ(month × days) ÷ {abb.total_calendar_days || 0} days (day-weighted)
                                                        {abb.carried_forward_days > 0 && (
                                                            <span className="block text-500 font-normal" style={{ fontSize: '0.58rem' }}>
                                                                {abb.carried_forward_days} day(s) had no transaction — previous close carried forward.
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="p-2 text-right text-teal-700" style={{ width: '9rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(abb.abb)}</div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Sampling warnings (date substituted / skipped / month dropped). */}
                                        {warnings.length > 0 && (
                                            <div className="mt-2 p-2 border-round" style={{ background: '#fffbeb', border: '1px solid #fde68a', fontSize: '0.62rem' }}>
                                                <div className="font-semibold text-orange-700 mb-1">
                                                    <i className="pi pi-exclamation-triangle mr-1" style={{ fontSize: '0.6rem' }}></i>
                                                    Sampling notes ({warnings.length})
                                                </div>
                                                <ul className="m-0 pl-3 text-color-secondary">
                                                    {warnings.slice(0, 6).map((w, wi) => (
                                                        <li key={wi} style={{ marginBottom: '2px' }}>{w.message}</li>
                                                    ))}
                                                    {warnings.length > 6 && <li>…and {warnings.length - 6} more.</li>}
                                                </ul>
                                            </div>
                                        )}

                                        {/* Method notes — mirrors the Eligibility Guide (ABB is raw,
                                            FOIR-first, monthly). Compact so it reinforces the logic
                                            without dominating the dialog. */}
                                        <div className="mt-2 p-2 surface-100 border-round font-mono" style={{ fontSize: '0.58rem' }}>
                                            <strong>ABB method:</strong> income = ABB used raw (100%, no haircut) — FOIR is the only discount.<br/>
                                            &nbsp;&nbsp;Post-DBR = ABB × FOIR% &nbsp;→&nbsp; Available = Post-DBR − Obligations<br/>
                                            &nbsp;&nbsp;Max Eligibility = (Available ÷ EMI per Lac) × 1,00,000
                                        </div>
                                    </div>
                                );
                            })()}

                        {/* ────────────────────────────────────────────────────────────
                            GST CALCULATION — month-wise turnover behind the average
                            (GST_TURNOVER only). Shows each month's taxable turnover from
                            the GST returns; every month is EDITABLE and recomputes the
                            average → income (× margin) → eligibility live. GST is monthly
                            (no ÷12). order:10 = same slot as the ABB table (before the
                            Applicants panel). Gated so it never shows for other methods.
                            ──────────────────────────────────────────────────────────── */}
                        {String(editableBreakdown.calculation_method || '').toUpperCase() === 'GST_TURNOVER'
                            && editableBreakdown.gst_summary
                            && Array.isArray(editableBreakdown.gst_summary.monthly)
                            && editableBreakdown.gst_summary.monthly.length > 0
                            && (() => {
                                const gs = editableBreakdown.gst_summary;
                                const monthly = gs.monthly;
                                const marginPct = Math.round((Number(gs.margin) || 0.08) * 10000) / 100; // e.g. 8
                                const avg = Number(gs.avg_turnover) || 0;
                                const income = Math.round(avg * (Number(gs.margin) || 0.08));
                                // "january" → "Jan", keep year as-is.
                                const mLabel = (name, year) => {
                                    const n = String(name || '').slice(0, 3);
                                    const cap = n ? n.charAt(0).toUpperCase() + n.slice(1).toLowerCase() : '';
                                    return `${cap}${year ? ' ' + year : ''}`.trim() || '—';
                                };
                                return (
                                    <div className="surface-50 p-2 border-round" style={{ order: 10 }}>
                                        <h6 className="mt-0 mb-1 flex align-items-center" style={{ fontSize: '0.8rem' }}>
                                            <i className="pi pi-percentage mr-2 text-purple-500" style={{ fontSize: '0.75rem' }}></i>
                                            GST Turnover — {editableBreakdown.bank_name || 'this lender'}
                                        </h6>
                                        <div className="text-color-secondary mb-2" style={{ fontSize: '0.68rem' }}>
                                            Monthly taxable turnover from the GST returns. Income = <strong>average monthly turnover × {marginPct}% margin</strong> (GST is monthly — no ÷12).
                                            <div className="mt-1" style={{ fontSize: '0.62rem' }}>
                                                <i className="pi pi-pencil text-blue-500 mr-1" style={{ fontSize: '0.55rem' }}></i>
                                                Each month's turnover is <strong>editable</strong> — the average, income and eligibility recompute live.
                                            </div>
                                        </div>

                                        <div className="border-round overflow-hidden" style={{ border: '1px solid var(--surface-border)' }}>
                                            <div className="flex surface-200 font-semibold text-color-secondary" style={{ fontSize: '0.62rem' }}>
                                                <div className="p-2" style={{ width: '9rem', flexShrink: 0 }}>Month</div>
                                                <div className="p-2 flex-1 text-right">Taxable turnover (editable)</div>
                                            </div>
                                            {monthly.map((m, mi) => (
                                                <div key={mi} className="flex align-items-center" style={{ fontSize: '0.66rem', borderTop: '1px solid var(--surface-border)', background: mi % 2 ? 'var(--surface-0)' : 'transparent' }}>
                                                    <div className="p-2 font-medium text-900" style={{ width: '9rem', flexShrink: 0 }}>{mLabel(m.month, m.year)}</div>
                                                    <div className="p-2 flex-1 text-right">
                                                        <InputNumber
                                                            value={m.turnover ?? 0}
                                                            onValueChange={(e) => updateGstMonthly(mi, e.value ?? 0)}
                                                            mode="currency" currency="INR" locale="en-IN" maxFractionDigits={0}
                                                            inputClassName="text-right font-semibold text-900 p-1"
                                                            inputStyle={{ width: '9rem', fontSize: '0.66rem', fontVariantNumeric: 'tabular-nums' }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            {/* Average + income footer */}
                                            <div className="flex align-items-center surface-100 font-bold" style={{ fontSize: '0.68rem', borderTop: '2px solid var(--surface-border)' }}>
                                                <div className="p-2 text-900" style={{ flex: 1 }}>
                                                    Average monthly turnover ({gs.months_count || monthly.length} months)
                                                </div>
                                                <div className="p-2 text-right text-teal-700" style={{ width: '11rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(avg)}</div>
                                            </div>
                                            <div className="flex align-items-center surface-100" style={{ fontSize: '0.66rem', borderTop: '1px solid var(--surface-border)' }}>
                                                <div className="p-2 text-color-secondary flex align-items-center flex-wrap" style={{ flex: 1, gap: '4px' }}>
                                                    <span>Income = {formatCurrency(avg)} ×</span>
                                                    {/* Editable margin % — default 8% / nature-based. Editing
                                                        re-derives income → eligibility live. */}
                                                    <InputNumber
                                                        value={marginPct}
                                                        onValueChange={(e) => updateGstMargin(e.value ?? 0)}
                                                        suffix="%"
                                                        min={0}
                                                        max={100}
                                                        minFractionDigits={0}
                                                        maxFractionDigits={2}
                                                        showButtons
                                                        buttonLayout="horizontal"
                                                        decrementButtonClassName="p-button-text p-button-sm"
                                                        incrementButtonClassName="p-button-text p-button-sm"
                                                        inputClassName="text-center font-bold text-purple-700 p-1"
                                                        inputStyle={{ width: '4rem', fontSize: '0.66rem' }}
                                                    />
                                                    <span className="text-500">margin</span>
                                                </div>
                                                <div className="p-2 text-right font-bold text-green-600" style={{ width: '11rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(income)}</div>
                                            </div>
                                        </div>

                                        <div className="mt-2 p-2 surface-100 border-round font-mono" style={{ fontSize: '0.58rem' }}>
                                            <strong>GST method:</strong> income = avg monthly turnover × margin% (monthly, no ÷12).<br/>
                                            &nbsp;&nbsp;Post-DBR = income × FOIR% &nbsp;→&nbsp; Available = Post-DBR − Obligations<br/>
                                            &nbsp;&nbsp;Max Eligibility = (Available ÷ EMI per Lac) × 1,00,000
                                        </div>
                                    </div>
                                );
                            })()}

                        {/* ────────────────────────────────────────────────────────────
                            NET PROFIT (Cash Profit) — per-year build-up + dip check
                            (NET_PROFIT only). Mirrors the Eligibility Guide's Step 1–4:
                            Cash Profit = PBT + Dep×70% + Salary + Partner Interest per
                            year; considered = AVERAGE of years; monthly = considered ÷ 12.
                            Every component is editable → recompute live. Falls back to a
                            single editable yearly Net-Profit field when no multi-year data.
                            ──────────────────────────────────────────────────────────── */}
                        {String(editableBreakdown.calculation_method || '').toUpperCase() === 'NET_PROFIT'
                            && (() => {
                                const years = Array.isArray(editableBreakdown.np_year_wise) ? editableBreakdown.np_year_wise : [];
                                const an = editableBreakdown.np_year_analysis || {};
                                const declined = an.comparison_result === 'declined_dip';
                                // Three dip states: none (latest ≥ previous), warn (dip within
                                // threshold — still eligible but flagged), declined (dip > threshold).
                                const dipPct = Number(an.dip_percent) || 0;
                                const hasDip = dipPct > 0;
                                const dipWarn = hasDip && !declined; // within limit but income declined
                                const cell = (yi, field, val, cls = '') => (
                                    <InputNumber
                                        value={Number(val) || 0}
                                        onValueChange={(e) => updateNpComponent(yi, field, e.value ?? 0)}
                                        mode="currency" currency="INR" locale="en-IN" maxFractionDigits={0}
                                        inputClassName={`text-right p-1 ${cls}`}
                                        inputStyle={{ width: '7rem', fontSize: '0.64rem', fontVariantNumeric: 'tabular-nums' }}
                                    />
                                );
                                return (
                                    <div className="surface-50 p-2 border-round" style={{ order: 10 }}>
                                        <h6 className="mt-0 mb-1 flex align-items-center" style={{ fontSize: '0.8rem' }}>
                                            <i className="pi pi-chart-line mr-2 text-purple-500" style={{ fontSize: '0.75rem' }}></i>
                                            Cash Profit — {editableBreakdown.bank_name || 'this lender'}
                                        </h6>

                                        {years.length > 0 ? (
                                            <>
                                                {/* Key percentages strip. Depreciation add-back and Dip
                                                    threshold are EDITABLE (recompute live); FOIR / ROI are
                                                    read-only here (edited in "Adjust Rate, FOIR & Tenure"). */}
                                                <div className="flex flex-wrap align-items-center gap-2 mb-2">
                                                    <span className="inline-flex align-items-center border-round px-2 py-1" style={{ background: 'var(--surface-100)', fontSize: '0.6rem' }}>
                                                        <span className="text-color-secondary mr-1">Depreciation add-back:</span>
                                                        <InputNumber
                                                            value={parseFloat(String(editableBreakdown.np_dep_pct != null ? editableBreakdown.np_dep_pct * 100 : (years[0]?.depreciation_policy_percent || '70')))}
                                                            onValueChange={(e) => updateNpDepPct(e.value ?? 0)}
                                                            suffix="%" min={0} max={100} minFractionDigits={0} maxFractionDigits={2}
                                                            inputClassName="text-center font-bold text-purple-700 p-1"
                                                            inputStyle={{ width: '3.5rem', fontSize: '0.6rem' }}
                                                        />
                                                    </span>
                                                    <span className="inline-flex align-items-center border-round px-2 py-1" style={{ background: 'var(--surface-100)', fontSize: '0.6rem' }}>
                                                        <span className="text-color-secondary mr-1">Dip threshold:</span>
                                                        <InputNumber
                                                            value={Number(an.dip_threshold) || 50}
                                                            onValueChange={(e) => updateNpThreshold(e.value ?? 0)}
                                                            suffix="%" min={0} max={100} minFractionDigits={0} maxFractionDigits={2}
                                                            inputClassName="text-center font-bold text-orange-600 p-1"
                                                            inputStyle={{ width: '3.5rem', fontSize: '0.6rem' }}
                                                        />
                                                    </span>
                                                    <span className="inline-flex align-items-center border-round px-2 py-1" style={{ background: 'var(--surface-100)', fontSize: '0.6rem' }}>
                                                        <span className="text-color-secondary mr-1">FOIR:</span>
                                                        <span className="font-bold text-orange-600">{editableBreakdown.foir}%</span>
                                                    </span>
                                                    <span className="inline-flex align-items-center border-round px-2 py-1" style={{ background: 'var(--surface-100)', fontSize: '0.6rem' }}>
                                                        <span className="text-color-secondary mr-1">ROI:</span>
                                                        <span className="font-bold text-blue-600">{editableBreakdown.roi}%</span>
                                                    </span>
                                                </div>
                                                <div className="text-color-secondary mb-2" style={{ fontSize: '0.68rem' }}>
                                                    Cash Profit = <strong>PBT + Depreciation×{years[0]?.depreciation_policy_percent || '70%'} + Salary + Partner Interest</strong> per year; income = <strong>average ÷ 12</strong> (monthly).
                                                    <div className="mt-1" style={{ fontSize: '0.62rem' }}>
                                                        <i className="pi pi-pencil text-blue-500 mr-1" style={{ fontSize: '0.55rem' }}></i>
                                                        Every component is <strong>editable</strong> — cash profit, average, income and eligibility recompute live.
                                                    </div>
                                                </div>

                                                <div className="border-round overflow-hidden" style={{ border: '1px solid var(--surface-border)' }}>
                                                    <div className="flex surface-200 font-semibold text-color-secondary" style={{ fontSize: '0.58rem' }}>
                                                        <div className="p-2" style={{ width: '5.5rem', flexShrink: 0 }}>Year</div>
                                                        <div className="p-2 flex-1 text-right">PBT</div>
                                                        <div className="p-2 flex-1 text-right">Deprec.</div>
                                                        <div className="p-2 flex-1 text-right">Salary</div>
                                                        <div className="p-2 flex-1 text-right">Partner Int.</div>
                                                        <div className="p-2 text-right" style={{ width: '7.5rem', flexShrink: 0 }}>Cash Profit</div>
                                                    </div>
                                                    {years.map((y, yi) => (
                                                        <div key={yi} className="flex align-items-center" style={{ fontSize: '0.64rem', borderTop: '1px solid var(--surface-border)', background: yi % 2 ? 'var(--surface-0)' : 'transparent' }}>
                                                            <div className="p-2 font-medium text-900" style={{ width: '5.5rem', flexShrink: 0 }}>
                                                                {y.year || '—'}
                                                                <span className="block text-500" style={{ fontSize: '0.52rem' }}>
                                                                    {yi === 0 ? 'Latest' : 'Previous'}
                                                                    {yi === 0 && hasDip && (
                                                                        <span className={`ml-1 font-bold ${declined ? 'text-red-600' : 'text-yellow-700'}`} title={`Income dipped ${dipPct}% vs previous year`}>
                                                                            <i className="pi pi-arrow-down" style={{ fontSize: '0.5rem' }}></i> {dipPct}%
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <div className="p-2 flex-1 text-right">{cell(yi, 'pbt', y.pbt, 'text-900')}</div>
                                                            <div className="p-2 flex-1 text-right">
                                                                {cell(yi, 'depreciation_actual', y.depreciation_actual, 'text-900')}
                                                                <span className="block text-500" style={{ fontSize: '0.5rem' }}>× {y.depreciation_policy_percent || '70%'} = {formatCurrency(y.depreciation_considered)}</span>
                                                            </div>
                                                            <div className="p-2 flex-1 text-right">{cell(yi, 'director_salary', y.director_salary, 'text-900')}</div>
                                                            <div className="p-2 flex-1 text-right">{cell(yi, 'partner_interest', y.partner_interest, 'text-900')}</div>
                                                            <div className="p-2 text-right font-bold text-teal-700" style={{ width: '7.5rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(y.cash_profit)}</div>
                                                        </div>
                                                    ))}
                                                    {/* Dip check row — ALWAYS shown (latest vs previous, dip%,
                                                        threshold, decision), so the DSA sees the safeguard even
                                                        when there's no dip. */}
                                                    {years.length >= 2 && (
                                                        <div className="flex align-items-center" style={{ fontSize: '0.6rem', borderTop: '1px solid var(--surface-border)', background: declined ? '#fef2f2' : (dipWarn ? '#fffbeb' : 'transparent') }}>
                                                            <div className="p-2" style={{ flex: 1 }}>
                                                                <i className={`pi mr-1 ${declined ? 'pi-times-circle text-red-600' : (dipWarn ? 'pi-exclamation-triangle text-yellow-600' : 'pi-check-circle text-green-600')}`} style={{ fontSize: '0.6rem' }}></i>
                                                                <span className="font-semibold text-color-secondary">Dip check: </span>
                                                                latest {formatCurrency(an.latest_cash_profit)} vs previous {formatCurrency(an.previous_cash_profit)} →
                                                                <span className={`font-bold ml-1 ${declined ? 'text-red-600' : (dipWarn ? 'text-yellow-700' : 'text-green-600')}`}>
                                                                    {hasDip ? `dip ${dipPct}%` : 'no decline'}
                                                                </span>
                                                                <span className="text-500"> (threshold {an.dip_threshold || 50}%)</span>
                                                                <span className={`font-bold ml-1 ${declined ? 'text-red-600' : (dipWarn ? 'text-yellow-700' : 'text-green-600')}`}>
                                                                    → {declined
                                                                        ? 'DECLINED — dip exceeds limit, income ₹0'
                                                                        : (dipWarn
                                                                            ? `income dipped but within ${an.dip_threshold || 50}% limit — proceed`
                                                                            : 'within limit, proceed')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {/* Average footer */}
                                                    <div className={`flex align-items-center font-bold ${declined ? 'bg-yellow-50' : 'surface-100'}`} style={{ fontSize: '0.66rem', borderTop: '2px solid var(--surface-border)' }}>
                                                        <div className="p-2" style={{ flex: 1 }}>
                                                            <span className="text-900">Considered = average of {years.length} year{years.length === 1 ? '' : 's'}</span>
                                                        </div>
                                                        <div className="p-2 text-right text-teal-700" style={{ width: '7.5rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(an.considered_value ?? an.average_cash_profit)}</div>
                                                    </div>
                                                    <div className="flex align-items-center surface-100" style={{ fontSize: '0.64rem', borderTop: '1px solid var(--surface-border)' }}>
                                                        <div className="p-2 text-color-secondary" style={{ flex: 1 }}>Monthly income = {formatCurrency(an.considered_value ?? an.average_cash_profit)} ÷ 12</div>
                                                        <div className="p-2 text-right font-bold text-green-600" style={{ width: '7.5rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(Math.round((an.considered_value ?? an.average_cash_profit ?? 0) / 12))}</div>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            /* Flat-only case — no multi-year ITR fields. One editable
                                               yearly net-profit field, with the ÷12 monthly income. */
                                            <div>
                                                <div className="text-color-secondary mb-2" style={{ fontSize: '0.68rem' }}>
                                                    Net Profit is entered <strong>yearly</strong>; income = <strong>yearly ÷ 12</strong> (monthly).
                                                    <span className="ml-1"><i className="pi pi-pencil text-blue-500 mr-1" style={{ fontSize: '0.55rem' }}></i>Editable.</span>
                                                </div>
                                                <div className="border-round overflow-hidden" style={{ border: '1px solid var(--surface-border)' }}>
                                                    <div className="flex align-items-center" style={{ fontSize: '0.66rem' }}>
                                                        <div className="p-2 text-color-secondary" style={{ flex: 1 }}>Net Profit (yearly)</div>
                                                        <div className="p-2 text-right" style={{ width: '9rem', flexShrink: 0 }}>
                                                            <InputNumber
                                                                value={Number(editableBreakdown.income_value) || 0}
                                                                onValueChange={(e) => updateNpFlat(e.value ?? 0)}
                                                                mode="currency" currency="INR" locale="en-IN" maxFractionDigits={0}
                                                                inputClassName="text-right font-semibold text-900 p-1"
                                                                inputStyle={{ width: '8rem', fontSize: '0.66rem', fontVariantNumeric: 'tabular-nums' }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex align-items-center surface-100 font-bold" style={{ fontSize: '0.66rem', borderTop: '2px solid var(--surface-border)' }}>
                                                        <div className="p-2 text-color-secondary" style={{ flex: 1 }}>Monthly income = yearly ÷ 12</div>
                                                        <div className="p-2 text-right text-green-600" style={{ width: '9rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(Math.round((Number(editableBreakdown.income_value) || 0) / 12))}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-2 p-2 surface-100 border-round font-mono" style={{ fontSize: '0.58rem' }}>
                                            <strong>Net Profit method:</strong> Cash Profit = PBT + Dep×70% + Salary + Partner Interest; considered = avg of years; income = considered ÷ 12.<br/>
                                            &nbsp;&nbsp;Dip &gt; {an.dip_threshold || 50}% (latest vs previous) → case declined.<br/>
                                            &nbsp;&nbsp;Post-DBR = income × FOIR% → Available = Post-DBR − Obligations → Max Eligibility = (Available ÷ EMI/Lac) × 1,00,000
                                        </div>
                                    </div>
                                );
                            })()}

                        {/* Applicants Breakdown — ALWAYS visible (no collapse). Rendered as a
                            plain panel per applicant with a header strip, rather than an
                            Accordion, so the income/obligations detail is never hidden behind a
                            click. Professional card look: subtle border, header rule, coloured
                            income on the right. */}
                        {editableBreakdown.applicants_breakdown?.length > 0 && (
                            <div className="flex flex-column gap-2" style={{ order: 20 }}>
                                {editableBreakdown.applicants_breakdown.map((applicant, appIdx) => (
                                    <div
                                        key={appIdx}
                                        className="border-round overflow-hidden"
                                        style={{ border: '1px solid var(--surface-border)' }}
                                    >
                                        {/* Header strip */}
                                        <div className="flex align-items-center px-3 py-2 surface-100" style={{ borderBottom: '1px solid var(--surface-border)' }}>
                                            <i className={`pi ${applicant.type === 'main' ? 'pi-user' : 'pi-users'} mr-2 text-color-secondary`} style={{ fontSize: '0.8rem' }}></i>
                                            <span className="font-semibold text-900" style={{ fontSize: '0.8rem' }}>
                                                {applicant.name || (applicant.type === 'main' ? 'Main Applicant' : `Co-Applicant ${appIdx}`)}
                                            </span>
                                            <span className="ml-auto text-green-600 font-bold" style={{ fontSize: '0.8rem' }}>
                                                Income: {formatCurrency(applicant.income)}
                                            </span>
                                        </div>
                                        <div className="p-2">
                                        <div className="grid">
                                            {/* Income Breakdown - Editable.
                                                For ABB, the Average-Bank-Balance figure is edited in the
                                                dedicated ABB table above (month-wise), so we hide that one
                                                row here to avoid showing — and offering a second edit of —
                                                the same number twice. Other income rows (rental/other) and
                                                all other methods are unaffected. */}
                                            {(() => {
                                                const isAbb = String(editableBreakdown.calculation_method || '').toUpperCase() === 'ABB';
                                                const rows = (applicant.income_breakdown || []).filter((item) =>
                                                    !(isAbb && String(item.field) === 'average_bank_balance'));
                                                // ABB with only the ABB row → the whole Income Breakdown
                                                // panel is redundant (the ABB table covers it); render a
                                                // one-line pointer instead of an empty panel.
                                                const abbOnly = isAbb && rows.length === 0;
                                                return (
                                            <div className="col-12 md:col-6">
                                                <h6 className="mt-0 mb-1 text-blue-600" style={{ fontSize: '0.78rem' }}>
                                                    <i className="pi pi-plus-circle mr-1" style={{ fontSize: '0.7rem' }}></i>
                                                    Income Breakdown
                                                </h6>
                                                {abbOnly ? (
                                                    <div className="text-color-secondary p-1" style={{ fontSize: '0.66rem' }}>
                                                        Income = Average Banking Balance (see the ABB table above; edit month balances there).
                                                    </div>
                                                ) : rows.length > 0 ? (
                                                    <div className="flex flex-column gap-1">
                                                        {rows.map((item, i) => (
                                                            <div key={i} className="p-1 surface-100 border-round">
                                                                <div className="flex justify-content-between align-items-center mb-1">
                                                                    <span className="text-color-secondary" style={{ fontSize: '0.65rem' }}>
                                                                        {item.field?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                        <span className="ml-1" style={{ opacity: 0.7 }}>({item.multiplier})</span>
                                                                    </span>
                                                                    <span className="font-bold" style={{ fontSize: '0.7rem' }}>
                                                                        → {formatCurrency(item.adjusted)}
                                                                    </span>
                                                                </div>
                                                                <InputNumber
                                                                    value={item.value}
                                                                    onValueChange={(e) => updateBreakdownValue(appIdx, 'income', i, e.value)}
                                                                    mode="currency"
                                                                    currency="INR"
                                                                    locale="en-IN"
                                                                    currencyDisplay="symbol"
                                                                    className="w-full p-inputtext-sm"
                                                                    inputClassName="text-right"
                                                                    min={0}
                                                                />
                                                            </div>
                                                        ))}
                                                        <div className="flex justify-content-between p-1 surface-200 border-round font-bold" style={{ fontSize: '0.75rem' }}>
                                                            <span>Total Income</span>
                                                            <span className="text-green-600">{formatCurrency(applicant.income)}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-color-secondary" style={{ fontSize: '0.7rem' }}>No income breakdown available</div>
                                                )}
                                            </div>
                                                );
                                            })()}

                                            {/* Obligation Breakdown - Editable */}
                                            <div className="col-12 md:col-6">
                                                <h6 className="mt-0 mb-1 text-red-600" style={{ fontSize: '0.78rem' }}>
                                                    <i className="pi pi-minus-circle mr-1" style={{ fontSize: '0.7rem' }}></i>
                                                    Obligations Breakdown
                                                </h6>
                                                {applicant.obligation_breakdown?.length > 0 ? (
                                                    <div className="flex flex-column gap-1">
                                                        {applicant.obligation_breakdown.map((item, i) => (
                                                            <div key={i} className="p-1 surface-100 border-round">
                                                                <div className="flex justify-content-between align-items-center mb-1">
                                                                    <span className="text-color-secondary" style={{ fontSize: '0.65rem' }}>
                                                                        {item.field?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                        <span className="ml-1" style={{ opacity: 0.7 }}>({item.multiplier})</span>
                                                                    </span>
                                                                    <span className="font-bold" style={{ fontSize: '0.7rem' }}>
                                                                        → {formatCurrency(item.adjusted)}
                                                                    </span>
                                                                </div>
                                                                <InputNumber
                                                                    value={item.value}
                                                                    onValueChange={(e) => updateBreakdownValue(appIdx, 'obligation', i, e.value)}
                                                                    mode="currency"
                                                                    currency="INR"
                                                                    locale="en-IN"
                                                                    currencyDisplay="symbol"
                                                                    className="w-full p-inputtext-sm"
                                                                    inputClassName="text-right"
                                                                    min={0}
                                                                />
                                                            </div>
                                                        ))}
                                                        <div className="flex justify-content-between p-1 surface-200 border-round font-bold" style={{ fontSize: '0.75rem' }}>
                                                            <span>Total Obligations</span>
                                                            <span className="text-red-600">{formatCurrency(applicant.obligations)}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-color-secondary" style={{ fontSize: '0.7rem' }}>No obligations</div>
                                                )}
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Adjust Rate, FOIR & Tenure — sits just above Multipliers (From Range).
                            The user tweaks these and hits Recalculate; changing ROI or Tenure
                            re-runs the PMT formula to refresh EMI per Lac so Max Eligibility
                            stays consistent. When the offer has a ROI band (roi_is_range) the
                            entered ROI must be inside [roi_min, roi_max]; tenure must be inside
                            [min_tenure, max_tenure] when the range exposes those bounds.
                            Outside values show a red error + disable Recalculate. */}
                        {(() => {
                            const bd = editableBreakdown;
                            const roiVal = Number(bd.roi);
                            const foirVal = Number(bd.foir);
                            const tenureVal = Number(bd.tenure || bd.tenure_months);
                            const minTen = Number(bd.min_tenure) || 0;
                            const maxTen = Number(bd.max_tenure) || 0;
                            const roiBandInvalid = bd.roi_is_range
                                && (roiVal < Number(bd.roi_min) || roiVal > Number(bd.roi_max));
                            const roiEmpty = !roiVal || roiVal <= 0;
                            const roiTooHigh = roiVal > 100;
                            const foirInvalid = !foirVal || foirVal <= 0 || foirVal > 100;
                            const tenureBandInvalid = (minTen > 0 && tenureVal < minTen)
                                || (maxTen > 0 && tenureVal > maxTen);
                            const tenureEmpty = !tenureVal || tenureVal <= 0;
                            const tenureTooHigh = tenureVal > 600;
                            return (
                                <div className="surface-50 p-2 border-round" style={{ order: 30 }}>
                                    <h6 className="mt-0 mb-2 flex align-items-center" style={{ fontSize: '0.8rem' }}>
                                        <i className="pi pi-pencil mr-2 text-blue-500" style={{ fontSize: '0.75rem' }}></i>
                                        Adjust Rate, FOIR & Tenure
                                    </h6>
                                    <div className="grid">
                                        {/* ROI editor */}
                                        <div className="col-12 md:col-6">
                                            <label className="block text-color-secondary mb-1" style={{ fontSize: '0.7rem' }}>
                                                Interest Rate (p.a.)
                                                {bd.roi_is_range && (
                                                    <span className="text-500 ml-1">
                                                        · allowed {bd.roi_min}% – {bd.roi_max}%
                                                    </span>
                                                )}
                                            </label>
                                            <InputNumber
                                                value={bd.roi}
                                                onValueChange={(e) => updateEligibilityField('roi', e.value)}
                                                mode="decimal"
                                                minFractionDigits={0}
                                                maxFractionDigits={2}
                                                min={bd.roi_is_range ? Number(bd.roi_min) : 0.1}
                                                max={bd.roi_is_range ? Number(bd.roi_max) : 100}
                                                step={0.05}
                                                suffix="%"
                                                showButtons
                                                buttonLayout="horizontal"
                                                decrementButtonClassName="p-button-text p-button-sm"
                                                incrementButtonClassName="p-button-text p-button-sm"
                                                className={`w-full ${(roiEmpty || roiTooHigh || roiBandInvalid) ? 'p-invalid' : ''}`}
                                                inputClassName="text-center font-bold text-blue-600 p-1"
                                                inputStyle={{ fontSize: '0.9rem' }}
                                            />
                                            {/* Band slider — drag to pick a rate within [roi_min, roi_max].
                                                Updates ROI live, which auto-recalculates EMI / eligibility /
                                                cost (debounced). Only shown for band offers. */}
                                            {bd.roi_is_range && (
                                                <div className="mt-2 px-1">
                                                    <Slider
                                                        value={Number(bd.roi) || Number(bd.roi_min)}
                                                        onChange={(e) => updateEligibilityField('roi', e.value)}
                                                        min={Number(bd.roi_min)}
                                                        max={Number(bd.roi_max)}
                                                        step={bandStep(bd.roi_min, bd.roi_max)}
                                                        className="w-full"
                                                    />
                                                    <div className="flex justify-content-between mt-1 text-500" style={{ fontSize: '0.65rem' }}>
                                                        <span>{bd.roi_min}%</span>
                                                        <span className="font-semibold text-blue-600">{bd.roi}%</span>
                                                        <span>{bd.roi_max}%</span>
                                                    </div>
                                                </div>
                                            )}
                                            {roiBandInvalid && (
                                                <div className="text-red-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                                    Enter a value between <strong>{bd.roi_min}%</strong> and <strong>{bd.roi_max}%</strong>.
                                                </div>
                                            )}
                                            {!roiBandInvalid && (roiEmpty || roiTooHigh) && (
                                                <div className="text-red-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                                    ROI must be between 0.1% and 100%.
                                                </div>
                                            )}
                                        </div>
                                        {/* FOIR editor */}
                                        <div className="col-12 md:col-6">
                                            <label className="block text-color-secondary mb-1" style={{ fontSize: '0.7rem' }}>
                                                FOIR · allowed 1% – 100%
                                            </label>
                                            <InputNumber
                                                value={bd.foir}
                                                onValueChange={(e) => updateEligibilityField('foir', e.value)}
                                                mode="decimal"
                                                minFractionDigits={0}
                                                maxFractionDigits={2}
                                                min={1}
                                                max={100}
                                                step={1}
                                                suffix="%"
                                                showButtons
                                                buttonLayout="horizontal"
                                                decrementButtonClassName="p-button-text p-button-sm"
                                                incrementButtonClassName="p-button-text p-button-sm"
                                                className={`w-full ${foirInvalid ? 'p-invalid' : ''}`}
                                                inputClassName="text-center font-bold text-orange-600 p-1"
                                                inputStyle={{ fontSize: '0.9rem' }}
                                            />
                                            {foirInvalid && (
                                                <div className="text-red-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                                    FOIR must be between 1% and 100%.
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Tenure editor — sits on its own row BELOW ROI/FOIR so it always
                                        gets full width and never collides with their layout. Changing the
                                        tenure re-derives EMI per Lac via PMT on Recalculate. Bounds (when
                                        present) come from the lender range's minten/maxten; otherwise we
                                        accept any positive value up to 600 months.
                                        Highlighted with a tinted background so the user can see at-a-glance
                                        that tenure drives Max Eligibility (the most common surprise factor). */}
                                    <div className="mt-2 p-2 border-round" style={{ backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe' }}>
                                        <div className="flex flex-column md:flex-row md:align-items-center gap-2">
                                            <label className="block text-color-secondary md:w-auto" style={{ fontSize: '0.75rem', minWidth: '12rem' }}>
                                                <i className="pi pi-clock mr-1 text-purple-600" style={{ fontSize: '0.7rem' }}></i>
                                                <strong className="text-900">Tenure</strong> <span className="text-color-secondary">(months)</span>
                                                {(minTen > 0 || maxTen > 0) && (
                                                    <div className="text-500" style={{ fontSize: '0.65rem' }}>
                                                        Allowed: {minTen || 1}{maxTen > 0 ? `–${maxTen}` : '+'} mo
                                                    </div>
                                                )}
                                            </label>
                                            <div className="flex-1">
                                                <InputNumber
                                                    value={bd.tenure || bd.tenure_months}
                                                    onValueChange={(e) => {
                                                        // Mirror to BOTH keys so recalculateEligibility
                                                        // (reads `bd.tenure || bd.tenure_months`) sees the
                                                        // new value regardless of which key it picks first.
                                                        updateEligibilityField('tenure', e.value);
                                                        updateEligibilityField('tenure_months', e.value);
                                                    }}
                                                    mode="decimal"
                                                    useGrouping={false}
                                                    min={minTen > 0 ? minTen : 1}
                                                    max={maxTen > 0 ? maxTen : 600}
                                                    step={6}
                                                    suffix=" mo"
                                                    showButtons
                                                    buttonLayout="horizontal"
                                                    decrementButtonClassName="p-button-text p-button-sm"
                                                    incrementButtonClassName="p-button-text p-button-sm"
                                                    className={`w-full ${(tenureEmpty || tenureTooHigh || tenureBandInvalid) ? 'p-invalid' : ''}`}
                                                    inputClassName="text-center font-bold text-purple-600 p-1"
                                                    inputStyle={{ fontSize: '0.95rem' }}
                                                />
                                            </div>
                                        </div>
                                        {/* Quick-pick buttons — common tenure values so the user doesn't
                                            have to click the spinner 60 times. Clamped to the allowed band
                                            so out-of-range presets are hidden. */}
                                        {(() => {
                                            const presets = [12, 24, 36, 48, 60, 84, 120, 180, 240];
                                            const upperBound = maxTen > 0 ? maxTen : 600;
                                            const lowerBound = minTen > 0 ? minTen : 1;
                                            const visible = presets.filter((p) => p >= lowerBound && p <= upperBound);
                                            if (!visible.length) return null;
                                            return (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    <span className="text-color-secondary mr-1" style={{ fontSize: '0.65rem', alignSelf: 'center' }}>Quick pick:</span>
                                                    {visible.map((p) => {
                                                        const active = tenureVal === p;
                                                        return (
                                                            <Button
                                                                key={p}
                                                                label={`${p} mo`}
                                                                size="small"
                                                                severity={active ? 'help' : 'secondary'}
                                                                outlined={!active}
                                                                onClick={() => {
                                                                    updateEligibilityField('tenure', p);
                                                                    updateEligibilityField('tenure_months', p);
                                                                }}
                                                                className="p-button-sm"
                                                                style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                        {/* Tenure band slider — only when the range exposes both bounds. */}
                                        {minTen > 0 && maxTen > minTen && (
                                            <div className="mt-2 px-1">
                                                <Slider
                                                    value={tenureVal || minTen}
                                                    onChange={(e) => {
                                                        updateEligibilityField('tenure', e.value);
                                                        updateEligibilityField('tenure_months', e.value);
                                                    }}
                                                    min={minTen}
                                                    max={maxTen}
                                                    step={6}
                                                    className="w-full"
                                                />
                                                <div className="flex justify-content-between mt-1 text-500" style={{ fontSize: '0.65rem' }}>
                                                    <span>{minTen} mo</span>
                                                    <span className="font-semibold text-purple-600">{tenureVal} mo</span>
                                                    <span>{maxTen} mo</span>
                                                </div>
                                            </div>
                                        )}
                                        {tenureBandInvalid && (
                                            <div className="text-red-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                                <i className="pi pi-exclamation-circle mr-1"></i>
                                                Tenure must be between <strong>{minTen}</strong> and <strong>{maxTen}</strong> months.
                                            </div>
                                        )}
                                        {!tenureBandInvalid && (tenureEmpty || tenureTooHigh) && (
                                            <div className="text-red-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                                <i className="pi pi-exclamation-circle mr-1"></i>
                                                Tenure must be between 1 and 600 months.
                                            </div>
                                        )}
                                        {!bd.tenure_input_filled && !tenureBandInvalid && !tenureEmpty && (
                                            <div className="text-orange-600 mt-1" style={{ fontSize: '0.65rem' }}>
                                                <i className="pi pi-info-circle mr-1"></i>
                                                Auto-filled with bank's minimum. Pick a longer tenure → higher Max Eligibility, then click Recalculate.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Multipliers - Editable */}
                        {(editableBreakdown.income_multipliers || editableBreakdown.obligation_multipliers) && (
                            <div className="surface-50 p-2 border-round" style={{ order: 30 }}>
                                <h6 className="mt-0 mb-2 flex align-items-center" style={{ fontSize: '0.8rem' }}>
                                    <i className="pi pi-sliders-h mr-2 text-purple-500" style={{ fontSize: '0.75rem' }}></i>
                                    Multipliers (From Range)
                                </h6>
                                <div className="grid">
                                    {editableBreakdown.income_multipliers && (
                                        <div className="col-12 md:col-6">
                                            <div className="text-color-secondary mb-1 font-semibold" style={{ fontSize: '0.7rem' }}>Income Multipliers:</div>
                                            <div className="flex flex-column gap-1">
                                                {Object.entries(editableBreakdown.income_multipliers).map(([key, val]) => (
                                                    <div key={key} className="flex align-items-center gap-1">
                                                        <span className="text-color-secondary white-space-nowrap" style={{ minWidth: '80px', fontSize: '0.65rem' }}>
                                                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </span>
                                                        <InputNumber
                                                            value={Math.round(val * 100)}
                                                            onValueChange={(e) => updateMultiplier('income', key, e.value)}
                                                            suffix="%"
                                                            className="p-inputtext-sm"
                                                            inputClassName="text-right"
                                                            inputStyle={{ width: '60px' }}
                                                            min={0}
                                                            max={100}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {editableBreakdown.obligation_multipliers && (
                                        <div className="col-12 md:col-6">
                                            <div className="text-color-secondary mb-1 font-semibold" style={{ fontSize: '0.7rem' }}>Obligation Multipliers:</div>
                                            <div className="flex flex-column gap-1">
                                                {Object.entries(editableBreakdown.obligation_multipliers).map(([key, val]) => (
                                                    <div key={key} className="flex align-items-center gap-1">
                                                        <span className="text-color-secondary white-space-nowrap" style={{ minWidth: '80px', fontSize: '0.65rem' }}>
                                                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </span>
                                                        <InputNumber
                                                            value={Math.round(val * 100)}
                                                            onValueChange={(e) => updateMultiplier('obligation', key, e.value)}
                                                            suffix="%"
                                                            className="p-inputtext-sm"
                                                            inputClassName="text-right"
                                                            inputStyle={{ width: '60px' }}
                                                            min={0}
                                                            max={100}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Eligibility Status */}
                        <div className={`p-2 border-round ${editableBreakdown.is_eligible ? 'bg-green-50' : 'bg-yellow-50'}`} style={{ order: 40 }}>
                            <div className="flex align-items-center">
                                <i className={`pi ${editableBreakdown.is_eligible ? 'pi-check-circle text-green-600' : 'pi-exclamation-circle text-yellow-600'} mr-2`} style={{ fontSize: '1.2rem' }}></i>
                                <div>
                                    <div className="font-bold" style={{ fontSize: '0.78rem' }}>
                                        {editableBreakdown.is_eligible ? 'Eligible for this amount' : 'Loan amount exceeds eligibility'}
                                    </div>
                                    <div className="text-color-secondary" style={{ fontSize: '0.7rem' }}>
                                        Status: {editableBreakdown.eligibility_status?.replace(/_/g, ' ').toUpperCase()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Edit Offer Dialog */}
            <Dialog
                visible={editDialogVisible}
                onHide={() => setEditDialogVisible(false)}
                header={<div className="flex align-items-center gap-2"><i className="pi pi-pencil text-primary"></i><span>Edit Offer #{editingOfferIndex !== null ? editingOfferIndex + 1 : ''}</span>{editForm.bank_name && <Tag value={editForm.bank_name} severity="info" className="text-xs" />}</div>}
                style={{ width: '90vw', maxWidth: '650px' }}
                modal maximizable breakpoints={{ '640px': '95vw' }}
                footer={<div className="flex justify-content-between"><Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => setEditDialogVisible(false)} /><Button label="Save Changes" icon="pi pi-check" className="p-button-sm" onClick={handleSaveEdit} /></div>}
            >
                {(() => {
                    const numField = (label, field, opts = {}) => (
                        <div className={opts.col || 'col-6 md:col-4'} key={field}>
                            <label className="font-semibold text-xs block mb-1">{label}</label>
                            <InputNumber value={editForm[field]} onValueChange={(e) => handleEditFieldChange(field, e.value)}
                                {...(opts.currency ? { mode: 'currency', currency: 'INR', locale: 'en-IN' } : {})}
                                {...(opts.suffix ? { suffix: opts.suffix } : {})}
                                {...(opts.decimal ? { mode: 'decimal', minFractionDigits: 1, maxFractionDigits: 2 } : {})}
                                className="w-full" inputClassName="p-inputtext-sm" disabled={opts.disabled} />
                        </div>
                    );
                    const textField = (label, field, opts = {}) => (
                        <div className={opts.col || 'col-12'} key={field}>
                            <label className="font-semibold text-xs block mb-1">{label}</label>
                            {opts.multiline
                                ? <InputTextarea value={editForm[field] || ''} onChange={(e) => handleEditFieldChange(field, e.target.value)} className="w-full" rows={2} style={{ fontSize: '0.8rem' }} />
                                : <InputText value={editForm[field] || ''} onChange={(e) => handleEditFieldChange(field, e.target.value)} className="w-full p-inputtext-sm" />
                            }
                        </div>
                    );
                    const section = (icon, label, color) => {
                        // 'neutral' maps to theme surface/text tokens (no Tailwind gray)
                        const isNeutral = color === 'neutral';
                        const bgClass = isNeutral ? 'surface-100' : `bg-${color}-50`;
                        const iconClass = isNeutral ? 'text-600' : `text-${color}-500`;
                        const textClass = isNeutral ? 'text-700' : `text-${color}-700`;
                        return (
                            <div className="col-12 py-0 mt-2">
                                <div className={`flex align-items-center gap-1 p-1 border-round ${bgClass}`}>
                                    <i className={`${icon} ${iconClass} text-xs`}></i>
                                    <span className={`font-semibold ${textClass} text-xs`}>{label}</span>
                                </div>
                            </div>
                        );
                    };

                    return (
                        <div className="grid" style={{ rowGap: '0.25rem' }}>
                            {section('pi pi-star', 'Offer Details', 'blue')}
                            {numField('Loan Amount', 'loan_amount', { currency: true })}
                            {numField('Interest Rate (p.a.)', 'roi', { decimal: true, suffix: '%' })}
                            {numField('Tenure (months)', 'tenure')}
                            {typeof editForm.processing_fee === 'number'
                                ? numField('Processing Fee', 'processing_fee', { currency: true })
                                : textField('Processing Fee', 'processing_fee', { col: 'col-6 md:col-4' })
                            }
                            {numField('EMI (Monthly)', 'emi', { currency: true, disabled: true })}
                            <div className="col-6 md:col-4">
                                <label className="font-semibold text-xs block mb-1">EMI per Lac</label>
                                <div className="p-inputtext p-inputtext-sm surface-100 border-round" style={{ padding: '0.5rem', fontSize: '0.85rem' }}>
                                    {editForm.roi && (editForm.tenure || editForm.tenure_months) ? `₹${calculateEmi(100000, editForm.roi, editForm.tenure || editForm.tenure_months).toLocaleString('en-IN')}` : '—'}
                                </div>
                            </div>

                            {section('pi pi-sliders-h', 'Range (Min - Max)', 'cyan')}
                            {numField('Min Loan', 'min_loan_amount', { currency: true, col: 'col-6 md:col-3' })}
                            {numField('Max Loan', 'max_loan_amount', { currency: true, col: 'col-6 md:col-3' })}
                            {numField('Min Tenure', 'min_tenure', { col: 'col-6 md:col-3' })}
                            {numField('Max Tenure', 'max_tenure', { col: 'col-6 md:col-3' })}
                            {numField('Min Salary', 'min_salary', { currency: true, col: 'col-6 md:col-3' })}
                            {numField('Max Salary', 'max_salary', { currency: true, col: 'col-6 md:col-3' })}
                            {textField('Min CIBIL', 'min_cibil', { col: 'col-6 md:col-3' })}
                            {textField('Max CIBIL', 'max_cibil', { col: 'col-6 md:col-3' })}

                            {section('pi pi-check-circle', 'Eligibility', 'green')}
                            {numField('Max Loan Eligibility', 'max_loan_eligibility', { currency: true, col: 'col-6' })}
                            {numField('FOIR Available', 'foir_available', { currency: true, col: 'col-6' })}

                            {section('pi pi-wallet', 'Salary Analysis', 'purple')}
                            {numField('Salary After EMI', 'salary_after_emi', { currency: true, col: 'col-6' })}
                            {textField('EMI/Salary Ratio', 'emi_to_salary_ratio', { col: 'col-6' })}

                            {section('pi pi-info-circle', 'Pre-closure & Part Payment', 'orange')}
                            {textField('Pre-closure Charges', 'foreclosurecharges', { col: 'col-6' })}
                            {textField('Lock-in (months)', 'ppm', { col: 'col-6 md:col-3' })}
                            {textField('Pre-closure Month', 'pre_closure_month', { col: 'col-6 md:col-3' })}
                            {textField('Part Payment Charges', 'ppr', { col: 'col-6' })}
                            {textField('Part Payment Amount', 'ppa', { col: 'col-6 md:col-3' })}
                            {textField('Part Payment Times', 'ppt', { col: 'col-6 md:col-3' })}
                            {textField('Part Payment Month', 'part_payment_month', { col: 'col-6 md:col-3' })}

                            {section('pi pi-list', 'Other', 'neutral')}
                            {textField('Conditions', 'conditions', { multiline: true })}

                            {/* Auto-calculated summary */}
                            <div className="col-12 mt-2">
                                <div className="surface-100 border-round p-2">
                                    <div className="text-xs font-semibold text-primary mb-2"><i className="pi pi-calculator mr-1"></i>Auto-calculated</div>
                                    <div className="grid">
                                        <div className="col-4 text-center">
                                            <div className="text-xs text-600">Total Interest</div>
                                            <div className="font-bold text-orange-600 text-sm">{editForm.emi && editForm.loan_amount && (editForm.tenure || editForm.tenure_months) ? formatCurrency(editForm.emi * (editForm.tenure || editForm.tenure_months) - editForm.loan_amount) : '—'}</div>
                                        </div>
                                        <div className="col-4 text-center">
                                            <div className="text-xs text-600">Total Cost</div>
                                            <div className="font-bold text-sm">{editForm.emi && editForm.loan_amount && (editForm.tenure || editForm.tenure_months) ? formatCurrency(editForm.emi * (editForm.tenure || editForm.tenure_months) - editForm.loan_amount + (editForm.processing_fee || 0)) : '—'}</div>
                                        </div>
                                        <div className="col-4 text-center">
                                            <div className="text-xs text-600">Total Payment</div>
                                            <div className="font-bold text-sm">{editForm.emi && (editForm.tenure || editForm.tenure_months) ? formatCurrency(editForm.emi * (editForm.tenure || editForm.tenure_months)) : '—'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Dialog>

            {/* EMI Recalculate Dialog — user picks a specific ROI within the band (e.g. 14.5%-16%) */}
            <EmiRecalculateDialog
                visible={recalcDialogVisible}
                offer={recalcOffer}
                onHide={() => setRecalcDialogVisible(false)}
                onApply={handleRecalcApply}
            />
        </div>
    );
};

export default OffersComparison;
